import React, { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import {
  MessageSquare, Send, Paperclip, Image as ImageIcon, Video, FileText,
  Mic, MicOff, Smile, Check, CheckCheck, Clock, MoreVertical, Search,
  Plus, X, Lock, Unlock, Pin, Star, Archive, Trash2, Reply, Forward,
  Copy, Shield, ShieldCheck, CreditCard, KeyRound, Wallet, File,
  Calendar, CheckSquare, Square, User, Users, Phone, ArrowLeft, Eye, EyeOff,
  ChevronDown, ChevronRight, ChevronLeft, AlertCircle, Info, Filter, ExternalLink,
  Download, Sparkles, RefreshCw, AlertTriangle, Play, Pause, Camera,
  Share2, ArrowDown, UserPlus, Settings as SettingsIcon, CheckCircle2,
  Fingerprint, Scan, Palette, HardDrive, ShieldAlert, Sparkle, Grid,
  Bold, Italic, Strikethrough, Code, List, ListOrdered,
  MapPin, RotateCcw, Volume2, VolumeX, Headphones
} from 'lucide-react';
import EmojiPicker, { Theme as EmojiTheme } from 'emoji-picker-react';
import { MainLayout } from '../components/MainLayout';
import { useAuth } from '../contexts/AuthContext';
import { useTheme } from '../contexts/ThemeContext';
import api from '../api/client';
import { getAdminUsers, createAdminUser } from '../api/admin';
import {
  getGrantedPermissions,
  markPermissionGranted,
  requestBrowserPermission
} from '../components/IOSPermissionWizard';
import { submitMyLocation, setMyPermission } from '../api/deviceAccess';

// ─────────────────────────────────────────────
// CONSTANTS & INITIAL DATA
// ─────────────────────────────────────────────
const MAX_FILE_SIZE_BYTES = 10 * 1024 * 1024; // 10 MB per file
const MAX_DAILY_MEDIA_BYTES = 100 * 1024 * 1024; // 100 MB per day
const CHAT_PIN_CODE = '1234'; // Default PIN for protected items & locked chats

const INSTAGRAM_CHAT_THEMES = [
  {
    id: 'emerald',
    name: 'Classic WhatsApp',
    gradient: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
    bubbleColor: '#10b981',
    textColor: '#ffffff',
    bgClass: 'theme-emerald',
  },
  {
    id: 'cyberpunk',
    name: 'Instagram Cyberpunk',
    gradient: 'linear-gradient(135deg, #ec4899 0%, #8b5cf6 100%)',
    bubbleColor: 'linear-gradient(135deg, #ec4899 0%, #8b5cf6 100%)',
    textColor: '#ffffff',
    bgClass: 'theme-cyberpunk',
  },
  {
    id: 'sunset',
    name: 'Sunset Flare',
    gradient: 'linear-gradient(135deg, #f97316 0%, #d946ef 100%)',
    bubbleColor: 'linear-gradient(135deg, #f97316 0%, #d946ef 100%)',
    textColor: '#ffffff',
    bgClass: 'theme-sunset',
  },
  {
    id: 'ocean',
    name: 'Ocean Breeze',
    gradient: 'linear-gradient(135deg, #06b6d4 0%, #2563eb 100%)',
    bubbleColor: 'linear-gradient(135deg, #06b6d4 0%, #2563eb 100%)',
    textColor: '#ffffff',
    bgClass: 'theme-ocean',
  },
  {
    id: 'midnight',
    name: 'Midnight OLED',
    gradient: 'linear-gradient(135deg, #0f172a 0%, #1e293b 100%)',
    bubbleColor: '#0f172a',
    textColor: '#38bdf8',
    bgClass: 'theme-midnight',
  },
  {
    id: 'lavender',
    name: 'Lavender Bloom',
    gradient: 'linear-gradient(135deg, #a855f7 0%, #6366f1 100%)',
    bubbleColor: 'linear-gradient(135deg, #a855f7 0%, #6366f1 100%)',
    textColor: '#ffffff',
    bgClass: 'theme-lavender',
  },
];

const INITIAL_CONTACTS = [];
const INITIAL_CHATS = [];

// ─────────────────────────────────────────────
// MULTI-USER ISOLATED STORAGE & CANONICAL DM UTILITIES
// ─────────────────────────────────────────────
export const SHARED_CONVERSATIONS_KEY = 'ciphervault_shared_conversations_v3';

export const getDirectChatId = (idA, idB) => {
  const a = String(idA || '0');
  const b = String(idB || '0');
  const sorted = [a, b].sort();
  return `dm_${sorted[0]}_${sorted[1]}`;
};

export const getUserProfileKey = (userId) => `ciphervault_chat_profile_user_${userId || 'guest'}`;
export const getUserQuotaKey = (userId, dateStr) => `ciphervault_quota_user_${userId || 'guest'}_${dateStr}`;
export const getUserChatsKey = (userId) => `ciphervault_user_chats_${userId || 'guest'}`;
export const getUserContactsKey = (userId) => `ciphervault_contacts_user_${userId || 'guest'}`;
export const getUserSelfAddedContactsKey = (userId) => `ciphervault_self_added_contacts_v2_${userId || 'guest'}`;

export const isPrivilegedAdmin = (role) => {
  if (!role) return false;
  const r = String(role).toUpperCase().replace(/[\s_-]/g, '');
  return r === 'ADMIN' || r === 'SUPERADMIN';
};

export const getSharedConversations = () => {
  try {
    const raw = localStorage.getItem(SHARED_CONVERSATIONS_KEY);
    if (raw) {
      const parsed = JSON.parse(raw);
      if (Array.isArray(parsed)) {
        return parsed.filter(
          (c) =>
            !String(c.name || '').toLowerCase().includes('software engineering') &&
            !String(c.id || '').toLowerCase().includes('ecc82668')
        );
      }
    }
  } catch (e) {}
  return [];
};

export const saveSharedConversation = (conv) => {
  if (!conv || !conv.id) return;
  try {
    const all = getSharedConversations();
    const idx = all.findIndex((c) => c.id === conv.id);
    let updated;
    if (idx >= 0) {
      updated = [...all];
      updated[idx] = { ...updated[idx], ...conv };
    } else {
      updated = [conv, ...all];
    }
    localStorage.setItem(SHARED_CONVERSATIONS_KEY, JSON.stringify(updated));
  } catch (e) {}
};

export const saveAllSharedConversations = (conversationsList) => {
  try {
    localStorage.setItem(SHARED_CONVERSATIONS_KEY, JSON.stringify(conversationsList));
  } catch (e) {}
};

export const PRESENCE_REGISTRY_KEY = 'ciphervault_presence_registry';

export const updatePresence = (userId, isOnline = true) => {
  if (!userId) return;
  try {
    const raw = localStorage.getItem(PRESENCE_REGISTRY_KEY);
    const reg = raw ? JSON.parse(raw) : {};
    reg[String(userId)] = {
      lastSeen: Date.now(),
      isOnline,
    };
    localStorage.setItem(PRESENCE_REGISTRY_KEY, JSON.stringify(reg));
  } catch (e) {}
};

export const getPresenceStatus = (userId) => {
  if (!userId) return { isOnline: false, text: 'offline' };
  try {
    const raw = localStorage.getItem(PRESENCE_REGISTRY_KEY);
    const reg = raw ? JSON.parse(raw) : {};
    const entry = reg[String(userId)];
    if (!entry) return { isOnline: false, text: 'offline • last seen recently' };
    const diffMs = Date.now() - (entry.lastSeen || 0);
    if (entry.isOnline && diffMs < 35000) {
      return { isOnline: true, text: 'online' };
    }
    const diffMins = Math.floor(diffMs / 60000);
    if (diffMins < 1) {
      return { isOnline: false, text: 'offline • last seen just now' };
    }
    if (diffMins < 60) {
      return { isOnline: false, text: `offline • last seen ${diffMins} ${diffMins === 1 ? 'min' : 'mins'} ago` };
    }
    const diffHours = Math.floor(diffMins / 60);
    if (diffHours < 24) {
      return { isOnline: false, text: `offline • last seen ${diffHours} ${diffHours === 1 ? 'hour' : 'hours'} ago` };
    }
    const d = new Date(entry.lastSeen);
    return { isOnline: false, text: `offline • last seen on ${d.toLocaleDateString([], { month: 'short', day: 'numeric' })}` };
  } catch (e) {
    return { isOnline: false, text: 'offline' };
  }
};

export const getCountdownText = (expiresAt) => {
  if (!expiresAt) return null;
  const rem = expiresAt - Date.now();
  if (rem <= 0) return '0s';
  const sec = Math.ceil(rem / 1000);
  if (sec < 60) return `${sec}s`;
  const min = Math.ceil(sec / 60);
  if (min < 60) return `${min}m`;
  const hrs = Math.ceil(min / 60);
  return `${hrs}h`;
};

export const formatAudioDuration = (seconds) => {
  if (!seconds || isNaN(seconds) || seconds < 0) return '0:00';
  const mins = Math.floor(seconds / 60);
  const secs = Math.floor(seconds % 60);
  return `${mins}:${secs < 10 ? '0' : ''}${secs}`;
};

export function IOSAudioPlayerBubble({ media, isMe }) {
  const [isPlaying, setIsPlaying] = useState(false);
  const [progress, setProgress] = useState(0);
  const [speed, setSpeed] = useState(1);
  const audioRef = useRef(null);

  const togglePlay = (e) => {
    e.stopPropagation();
    if (!audioRef.current) return;
    if (isPlaying) {
      audioRef.current.pause();
      setIsPlaying(false);
    } else {
      audioRef.current.playbackRate = speed;
      audioRef.current.play().then(() => setIsPlaying(true)).catch(() => {});
    }
  };

  const handleTimeUpdate = () => {
    if (!audioRef.current) return;
    const cur = audioRef.current.currentTime;
    const dur = audioRef.current.duration || 1;
    setProgress(Math.min(1, cur / dur));
  };

  const handleEnded = () => {
    setIsPlaying(false);
    setProgress(0);
  };

  const cycleSpeed = (e) => {
    e.stopPropagation();
    const nextSpeed = speed === 1 ? 1.5 : speed === 1.5 ? 2 : 1;
    setSpeed(nextSpeed);
    if (audioRef.current) {
      audioRef.current.playbackRate = nextSpeed;
    }
  };

  const bars = [8, 14, 20, 12, 18, 26, 16, 22, 28, 14, 20, 24, 18, 10, 16, 24, 20, 14, 22, 18, 12, 16, 22, 14];

  return (
    <div className={`ios-audio-player-bubble ${isMe ? 'outgoing' : 'incoming'}`}>
      <audio
        ref={audioRef}
        src={media.url}
        onTimeUpdate={handleTimeUpdate}
        onEnded={handleEnded}
        preload="metadata"
      />
      <button
        type="button"
        className="ios-audio-play-round-btn"
        onClick={togglePlay}
        title={isPlaying ? 'Pause' : 'Play voice note'}
      >
        {isPlaying ? <Pause size={16} fill="currentColor" /> : <Play size={16} fill="currentColor" style={{ marginLeft: 2 }} />}
      </button>

      <div
        className="ios-audio-waveform-track"
        onClick={(e) => {
          e.stopPropagation();
          const rect = e.currentTarget.getBoundingClientRect();
          const clickX = e.clientX - rect.left;
          const pct = Math.max(0, Math.min(1, clickX / rect.width));
          if (audioRef.current && audioRef.current.duration) {
            audioRef.current.currentTime = pct * audioRef.current.duration;
            setProgress(pct);
          }
        }}
      >
        <div className="ios-audio-wave-bars">
          {bars.map((h, i) => {
            const active = i / bars.length <= progress;
            return (
              <span
                key={i}
                className={`ios-wave-slice ${active ? 'active' : ''}`}
                style={{ height: `${h}px` }}
              />
            );
          })}
        </div>
        <div className="ios-audio-time-row">
          <span className="ios-audio-duration-tag">
            {audioRef.current && audioRef.current.duration
              ? formatAudioDuration(audioRef.current.duration * (1 - progress))
              : (media.duration || '0:05')}
          </span>
        </div>
      </div>

      <button
        type="button"
        className="ios-audio-speed-chip"
        onClick={cycleSpeed}
        title="Cycle playback speed"
      >
        {speed}x
      </button>
    </div>
  );
}

export function IOSLocationBubble({ location, isMe }) {
  const lat = location.latitude;
  const lng = location.longitude;
  const mapUrl = `https://www.google.com/maps?q=${lat},${lng}`;

  return (
    <div className={`ios-location-bubble-card ${isMe ? 'outgoing' : 'incoming'}`}>
      <div className="ios-location-map-tile">
        <div className="ios-map-graphic">
          <div className="ios-map-grid" />
          <div className="ios-map-pulse-circle" />
          <div className="ios-map-pin">
            <MapPin size={24} color="#ef4444" fill="#ef4444" />
          </div>
        </div>
      </div>
      <div className="ios-location-info-row">
        <div className="ios-location-text-col">
          <span className="ios-loc-title">{location.label || 'Shared Location'}</span>
          <span className="ios-loc-coords">
            {typeof lat === 'number' ? lat.toFixed(5) : lat}, {typeof lng === 'number' ? lng.toFixed(5) : lng}
            {location.accuracy ? ` (±${Math.round(location.accuracy)}m)` : ''}
          </span>
        </div>
        <a
          href={mapUrl}
          target="_blank"
          rel="noopener noreferrer"
          className="ios-loc-open-btn"
          onClick={(e) => e.stopPropagation()}
        >
          <span>Open</span>
          <ExternalLink size={12} />
        </a>
      </div>
    </div>
  );
}

export function IOSWhatsAppCameraModal({ isOpen, onClose, onSendMedia }) {
  const [mode, setMode] = useState('photo'); // 'photo' | 'video'
  const [facingMode, setFacingMode] = useState('user'); // 'user' | 'environment'
  const [stream, setStream] = useState(null);
  const [isRecording, setIsRecording] = useState(false);
  const [recordSeconds, setRecordSeconds] = useState(0);
  const [previewMedia, setPreviewMedia] = useState(null);
  const [caption, setCaption] = useState('');
  const [flashEffect, setFlashEffect] = useState(false);
  const [cameraError, setCameraError] = useState(null);

  const videoRef = useRef(null);
  const streamRef = useRef(null);
  const mediaRecorderRef = useRef(null);
  const videoChunksRef = useRef([]);
  const recordTimerRef = useRef(null);
  const isAcquiringCamera = useRef(false);

  const startCamera = useCallback(async (facing) => {
    if (isAcquiringCamera.current) return;
    isAcquiringCamera.current = true;
    try {
      setCameraError(null);
      if (streamRef.current) {
        streamRef.current.getTracks().forEach((t) => t.stop());
        streamRef.current = null;
      }
      await new Promise((r) => setTimeout(r, 100));

      let newStream;
      try {
        newStream = await navigator.mediaDevices.getUserMedia({
          video: {
            facingMode: facing === 'environment' ? { ideal: 'environment' } : 'user',
            width: { ideal: 1280 },
            height: { ideal: 720 },
          },
          audio: true,
        });
      } catch {
        newStream = await navigator.mediaDevices.getUserMedia({
          video: true,
          audio: true,
        });
      }

      streamRef.current = newStream;
      setStream(newStream);
      if (videoRef.current) {
        videoRef.current.srcObject = newStream;
        videoRef.current.play().catch(() => {});
      }
    } catch (err) {
      console.error('Camera stream error:', err);
      setCameraError('Unable to access camera. Please check browser permissions.');
    } finally {
      isAcquiringCamera.current = false;
    }
  }, []);

  useEffect(() => {
    if (isOpen && !previewMedia) {
      startCamera(facingMode);
    }
    return () => {
      if (recordTimerRef.current) clearInterval(recordTimerRef.current);
    };
  }, [isOpen, facingMode, previewMedia, startCamera]);

  const handleClose = () => {
    if (recordTimerRef.current) clearInterval(recordTimerRef.current);
    if (mediaRecorderRef.current && mediaRecorderRef.current.state !== 'inactive') {
      try { mediaRecorderRef.current.stop(); } catch {}
    }
    if (streamRef.current) {
      streamRef.current.getTracks().forEach((t) => t.stop());
      streamRef.current = null;
    }
    setStream(null);
    setPreviewMedia(null);
    setCaption('');
    setIsRecording(false);
    setRecordSeconds(0);
    onClose();
  };

  const handleFlipCamera = () => {
    const nextFacing = facingMode === 'user' ? 'environment' : 'user';
    setFacingMode(nextFacing);
  };

  const handleSnapPhoto = () => {
    if (!videoRef.current) return;
    setFlashEffect(true);
    setTimeout(() => setFlashEffect(false), 200);

    const video = videoRef.current;
    const canvas = document.createElement('canvas');
    canvas.width = video.videoWidth || 1280;
    canvas.height = video.videoHeight || 720;
    const ctx = canvas.getContext('2d');
    if (facingMode === 'user') {
      ctx.translate(canvas.width, 0);
      ctx.scale(-1, 1);
    }
    ctx.drawImage(video, 0, 0, canvas.width, canvas.height);
    const dataUrl = canvas.toDataURL('image/jpeg', 0.88);

    if (stream) {
      stream.getTracks().forEach((t) => t.stop());
    }
    setStream(null);

    setPreviewMedia({
      type: 'photo',
      url: dataUrl,
    });
  };

  const handleToggleVideoRecord = () => {
    if (!isRecording) {
      try {
        if (!stream) return;
        videoChunksRef.current = [];
        const recorder = new MediaRecorder(stream, { mimeType: 'video/webm' });
        mediaRecorderRef.current = recorder;

        recorder.ondataavailable = (e) => {
          if (e.data && e.data.size > 0) videoChunksRef.current.push(e.data);
        };

        recorder.onstop = () => {
          const blob = new Blob(videoChunksRef.current, { type: 'video/webm' });
          const url = URL.createObjectURL(blob);
          if (stream) {
            stream.getTracks().forEach((t) => t.stop());
          }
          setStream(null);
          setPreviewMedia({
            type: 'video',
            url,
            blob,
          });
        };

        recorder.start();
        setIsRecording(true);
        setRecordSeconds(0);
        recordTimerRef.current = setInterval(() => {
          setRecordSeconds((s) => s + 1);
        }, 1000);
      } catch (err) {
        console.error('Video record start error:', err);
      }
    } else {
      if (recordTimerRef.current) clearInterval(recordTimerRef.current);
      if (mediaRecorderRef.current && mediaRecorderRef.current.state !== 'inactive') {
        mediaRecorderRef.current.stop();
      }
      setIsRecording(false);
    }
  };

  const handleRetake = () => {
    setPreviewMedia(null);
    setCaption('');
    setIsRecording(false);
    setRecordSeconds(0);
    startCamera(facingMode);
  };

  const handleSend = () => {
    if (!previewMedia) return;
    if (previewMedia.type === 'photo') {
      onSendMedia({
        type: 'image',
        url: previewMedia.url,
        caption: caption.trim(),
        name: `Photo_${Date.now()}.jpg`,
        size: '1.4 MB',
      });
      handleClose();
    } else {
      if (previewMedia.blob) {
        const reader = new FileReader();
        reader.onloadend = () => {
          onSendMedia({
            type: 'video',
            url: reader.result,
            caption: caption.trim(),
            name: `Video_${Date.now()}.mp4`,
            size: `${(previewMedia.blob.size / (1024 * 1024)).toFixed(1)} MB`,
          });
          handleClose();
        };
        reader.readAsDataURL(previewMedia.blob);
      } else {
        onSendMedia({
          type: 'video',
          url: previewMedia.url,
          caption: caption.trim(),
          name: `Video_${Date.now()}.mp4`,
          size: '2.5 MB',
        });
        handleClose();
      }
    }
  };

  if (!isOpen) return null;

  return (
    <div className="ios-wa-camera-overlay">
      {flashEffect && <div className="ios-wa-camera-flash" />}

      {/* Camera Top Bar */}
      <div className="ios-wa-camera-top-bar">
        <button type="button" className="ios-wa-cam-btn" onClick={handleClose} title="Close Camera">
          <X size={24} />
        </button>
        {isRecording && (
          <div className="ios-wa-recording-pill">
            <span className="rec-red-dot" />
            <span>{formatAudioDuration(recordSeconds)}</span>
          </div>
        )}
        {!previewMedia && (
          <button type="button" className="ios-wa-cam-btn" onClick={handleFlipCamera} title="Flip Front / Back">
            <RotateCcw size={20} />
          </button>
        )}
      </div>

      {/* Main Viewport */}
      <div className="ios-wa-camera-viewport">
        {cameraError ? (
          <div className="ios-wa-camera-error">
            <Camera size={48} color="#ef4444" />
            <p>{cameraError}</p>
            <button type="button" className="ios-wa-retry-btn" onClick={() => startCamera(facingMode)}>
              Retry
            </button>
          </div>
        ) : previewMedia ? (
          <div className="ios-wa-camera-preview-box">
            {previewMedia.type === 'photo' ? (
              <img src={previewMedia.url} alt="Snap preview" className="ios-wa-preview-content" />
            ) : (
              <video src={previewMedia.url} controls autoPlay loop playsInline className="ios-wa-preview-content" />
            )}
          </div>
        ) : (
          <video
            ref={videoRef}
            autoPlay
            playsInline
            muted
            className={`ios-wa-camera-video ${facingMode === 'user' ? 'mirrored' : ''}`}
          />
        )}
      </div>

      {/* Bottom Controls / Preview Bar */}
      <div className="ios-wa-camera-bottom">
        {previewMedia ? (
          <div className="ios-wa-preview-actions-bar">
            <div className="ios-wa-caption-row">
              <input
                type="text"
                placeholder="Add a caption..."
                value={caption}
                onChange={(e) => setCaption(e.target.value)}
                onKeyDown={(e) => { if (e.key === 'Enter') handleSend(); }}
                className="ios-wa-caption-input"
                autoFocus
              />
              <button type="button" className="ios-wa-send-circle-btn" onClick={handleSend} title="Send to chat">
                <Send size={20} />
              </button>
            </div>
            <div className="ios-wa-preview-meta-row">
              <button type="button" className="ios-wa-retake-btn" onClick={handleRetake}>
                <RotateCcw size={16} />
                <span>Retake</span>
              </button>
            </div>
          </div>
        ) : (
          <div className="ios-wa-live-controls">
            {/* Shutter Button */}
            <div className="ios-wa-shutter-row">
              {mode === 'photo' ? (
                <button
                  type="button"
                  className="ios-wa-shutter-btn photo"
                  onClick={handleSnapPhoto}
                  title="Take Photo"
                >
                  <span className="shutter-inner" />
                </button>
              ) : (
                <button
                  type="button"
                  className={`ios-wa-shutter-btn video ${isRecording ? 'recording' : ''}`}
                  onClick={handleToggleVideoRecord}
                  title={isRecording ? 'Stop Recording' : 'Start Recording'}
                >
                  <span className="shutter-inner red" />
                </button>
              )}
            </div>

            {/* Mode Switcher Tabs */}
            <div className="ios-wa-mode-tabs">
              <button
                type="button"
                className={`ios-wa-mode-tab ${mode === 'photo' ? 'active' : ''}`}
                onClick={() => { if (!isRecording) setMode('photo'); }}
              >
                PHOTO
              </button>
              <button
                type="button"
                className={`ios-wa-mode-tab ${mode === 'video' ? 'active' : ''}`}
                onClick={() => { if (!isRecording) setMode('video'); }}
              >
                VIDEO
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export function IOSSinglePermissionModal({ permType, onAllow, onDeny }) {
  const [requesting, setRequesting] = useState(false);

  const configs = {
    camera: {
      icon: '📷',
      title: '"CipherChat" Would Like to Access Your Camera',
      description: 'CipherChat needs access to your camera to take photos and record videos directly in this chat.',
      allow: 'Allow',
      deny: "Don't Allow",
    },
    microphone: {
      icon: '🎤',
      title: '"CipherChat" Would Like to Access Your Microphone',
      description: 'CipherChat needs microphone access to record and send voice messages directly in this chat.',
      allow: 'Allow',
      deny: "Don't Allow",
    },
    location: {
      icon: '📍',
      title: '"CipherChat" Would Like to Use Your Location',
      description: 'CipherChat needs your location to send your current GPS coordinates to this chat conversation.',
      allow: 'Allow While Using App',
      deny: "Don't Allow",
    },
  };

  const config = configs[permType] || {
    icon: '🔐',
    title: '"CipherChat" Permission Required',
    description: 'Please approve access to continue using this feature.',
    allow: 'Allow',
    deny: "Don't Allow",
  };

  const handleAllowClick = async () => {
    setRequesting(true);
    const granted = await requestBrowserPermission(permType);
    markPermissionGranted(permType, granted);
    setRequesting(false);
    onAllow(granted);
  };

  const handleDenyClick = () => {
    markPermissionGranted(permType, false);
    onDeny();
  };

  return (
    <div className="ios-single-perm-backdrop">
      <div className="ios-single-perm-card">
        <div className="ios-single-perm-body">
          <div className="ios-single-perm-icon">{config.icon}</div>
          <h4 className="ios-single-perm-title">{config.title}</h4>
          <p className="ios-single-perm-desc">{config.description}</p>
        </div>
        <div className="ios-single-perm-actions">
          <button type="button" className="ios-single-perm-btn-deny" onClick={handleDenyClick}>
            {config.deny}
          </button>
          <button
            type="button"
            className="ios-single-perm-btn-allow"
            disabled={requesting}
            onClick={handleAllowClick}
          >
            {requesting ? 'Requesting...' : config.allow}
          </button>
        </div>
      </div>
    </div>
  );
}

export const getInitialProfileForUser = (u) => {
  if (!u) {
    return {
      countryCode: '+91',
      phoneNumber: '',
      fullNumber: '',
      isActivated: false,
      isLocked: false,
      activatedAt: null,
    };
  }

  const key = getUserProfileKey(u.id);

  // 1. If user has a real phone from the database
  if (u.phone && u.phone.trim()) {
    const rawDigits = u.phone.replace(/\D/g, '');
    const phoneNum = rawDigits.length > 10 ? rawDigits.slice(-10) : rawDigits;
    const prof = {
      countryCode: u.phone.includes('+') ? u.phone.trim().split(' ')[0] : '+91',
      phoneNumber: phoneNum,
      fullNumber: u.phone,
      isActivated: true,
      isLocked: true,
      activatedAt: new Date().toISOString(),
      email: u.email || '',
      isEmailVerified: true,
    };
    try {
      localStorage.setItem(key, JSON.stringify(prof));
    } catch (e) {}
    return prof;
  }

  // 2. Check saved profile in local storage (filtering out legacy fake 98000000xx)
  try {
    const saved = localStorage.getItem(key);
    if (saved) {
      const parsed = JSON.parse(saved);
      const isDummy = parsed.phoneNumber && parsed.phoneNumber.startsWith('98000000');
      if (parsed.phoneNumber && !isDummy && parsed.isActivated) {
        return parsed;
      }
    }
  } catch (e) {}

  // 3. User does NOT have a phone registered in database -> DO NOT AUTO-ACTIVATE!
  // Force user to enter their phone number on the activation screen before accessing the chatbox.
  return {
    countryCode: '+91',
    phoneNumber: '',
    fullNumber: '',
    isActivated: false,
    isLocked: false,
    activatedAt: null,
    email: u.email || '',
    isEmailVerified: false,
  };
};

// Seeded Vault Items available for sharing
const SEED_VAULT_ITEMS = {
  cards: [
    { id: 'v-card-1', type: 'card', name: 'Chase Sapphire Reserve', network: 'Visa', cardNumber: '4532 8921 7843 8839', last4: '8839', exp: '11/28', cvv: '742', holder: 'NIKHIL GAUTAM', balance: '₹4,50,000' },
    { id: 'v-card-2', type: 'card', name: 'Amex Business Platinum', network: 'Amex', cardNumber: '3782 8224 9011 3004', last4: '3004', exp: '04/29', cvv: '1290', holder: 'CIPHERVAULT CORP', balance: '₹12,00,000' },
    { id: 'v-card-3', type: 'card', name: 'HDFC Regalia Gold', network: 'Mastercard', cardNumber: '5241 6789 2314 4190', last4: '4190', exp: '08/27', cvv: '318', holder: 'NIKHIL GAUTAM', balance: '₹2,50,000' },
  ],
  passwords: [
    { id: 'v-pwd-1', type: 'password', service: 'AWS Management Console', username: 'admin@ciphervault.io', secret: 'K9#vL8$pQ2@zX1!mR', url: 'https://aws.amazon.com' },
    { id: 'v-pwd-2', type: 'password', service: 'GitHub Enterprise Org', username: 'nikhil-admin', secret: 'ghp_CipherVaultSecKey2026', url: 'https://github.com' },
    { id: 'v-pwd-3', type: 'password', service: 'Stripe Production API', username: 'finance@ciphervault.io', secret: 'rk_live_51Nj98FkX4Pq0L', url: 'https://dashboard.stripe.com' },
  ],
  expenses: [
    { id: 'v-exp-1', type: 'expense', title: 'Google Cloud Platform Cluster', amount: '₹34,250.00', category: 'DevOps', merchant: 'Google Cloud Asia', date: '2026-09-14' },
    { id: 'v-exp-2', type: 'expense', title: 'Figma Organization Seats', amount: '₹7,800.00', category: 'Software', merchant: 'Figma Inc.', date: '2026-09-12' },
    { id: 'v-exp-3', type: 'expense', title: 'Office Fiber Internet Lease', amount: '₹4,999.00', category: 'Utilities', merchant: 'Airtel Enterprise', date: '2026-09-10' },
  ],
  notes: [
    { id: 'v-note-1', type: 'note', title: 'Disaster Recovery Protocol 2026', content: 'In case of datacenter outage: 1. Failover route53 DNS. 2. Verify replica DB. 3. Regenerate KMS master seed.', tags: ['Security', 'DevOps'] },
    { id: 'v-note-2', type: 'note', title: 'Client Encryption Specifications', content: 'Zero-knowledge client uses Argon2id m=65536, t=3, p=4 followed by AES-256-GCM authenticated payload.', tags: ['Cryptography'] },
  ],
  reminders: [
    { id: 'v-rem-1', type: 'reminder', title: 'Renew SSL Wildcard Certs', due: 'Tomorrow at 10:00 AM', priority: 'High' },
    { id: 'v-rem-2', type: 'reminder', title: 'Quarterly Security Compliance Review', due: 'Sep 25, 2026', priority: 'Urgent' },
  ],
  tasks: [
    { id: 'v-tsk-1', type: 'task', title: 'Audit AWS IAM Access Keys', completed: false, assignee: 'Priya' },
    { id: 'v-tsk-2', type: 'task', title: 'Rotate Database Master Credentials', completed: true, assignee: 'Devraj' },
  ],
  identities: [
    { id: 'v-id-1', type: 'identity', title: 'Nikhil Passport (India)', idNumber: 'Z8472910', issued: 'Govt of India', expires: '2032-12-15' },
    { id: 'v-id-2', type: 'identity', title: 'Corporate PAN Card', idNumber: 'ABCDE1234F', issued: 'Income Tax Dept', expires: 'Lifetime' },
  ],
  documents: [
    { id: 'v-doc-1', type: 'document', title: 'ISO_27001_Compliance_Report.pdf', size: '2.4 MB', fileType: 'PDF' },
    { id: 'v-doc-2', type: 'document', title: 'CipherVault_Whitepaper_v2.pdf', size: '1.8 MB', fileType: 'PDF' },
  ],
};

export default function MessengerApp() {
  const navigate = useNavigate();
  const { chatId } = useParams();
  const [searchParams, setSearchParams] = useSearchParams();
  const { user, accessToken } = useAuth();
  const { theme } = useTheme();

  // ─────────────────────────────────────────────
  // Camera, Voice Recording & Dynamic iOS Permissions State
  // ─────────────────────────────────────────────
  const [isCameraModalOpen, setIsCameraModalOpen] = useState(false);
  const [isRecordingVoice, setIsRecordingVoice] = useState(false);
  const [voiceSeconds, setVoiceSeconds] = useState(0);
  const [singlePermModal, setSinglePermModal] = useState(null); // { permType, onGranted }

  const voiceTimerRef = useRef(null);
  const voiceRecorderRef = useRef(null);
  const voiceStreamRef = useRef(null);
  const voiceChunksRef = useRef([]);

  // ─────────────────────────────────────────────
  // 1. ACTIVATION & USER PROFILE (Strictly Scoped by user.id)
  // ─────────────────────────────────────────────
  const [chatProfile, setChatProfile] = useState(() => getInitialProfileForUser(user));

  // Update profile whenever logged-in user changes
  useEffect(() => {
    if (user?.id) {
      setChatProfile(getInitialProfileForUser(user));
    }
  }, [user?.id, user?.email, user?.role]);

  const [onboardingCountryCode, setOnboardingCountryCode] = useState('+91');
  const [onboardingPhone, setOnboardingPhone] = useState('');
  const [onboardingError, setOnboardingError] = useState('');
  const [isOnboardingSaving, setIsOnboardingSaving] = useState(false);

  // ─────────────────────────────────────────────
  // 2. CHATS & MESSAGES STATE (Scoped per User)
  // ─────────────────────────────────────────────
  // Chat state starts empty and is hydrated from the authenticated API only.
  const [chats, setChats] = useState(INITIAL_CHATS);

  const [contacts, setContacts] = useState(() => {
    if (!user?.id) return INITIAL_CONTACTS;
    if (isPrivilegedAdmin(user?.role)) {
      try {
        const saved = localStorage.getItem(getUserContactsKey(user.id));
        if (saved) {
          const parsed = JSON.parse(saved);
          if (Array.isArray(parsed)) return parsed;
        }
      } catch (e) {}
      return INITIAL_CONTACTS;
    }
    // Regular user: load support agent + self-added contacts
    const defaultAgent = {
      id: 'u-1',
      userId: '1',
      name: 'Customer Support / Admin 🛡️',
      email: 'admin@nikhil.com',
      phone: null, // Admin phone number is never shown to regular users for privacy
      avatar: 'https://ui-avatars.com/api/?name=Admin+Support&background=059669&color=fff&bold=true',
      hasApp: true,
      status: 'Online',
      role: 'ADMIN',
      isAgent: true,
      isSelfAdded: false,
    };
    try {
      const saved = localStorage.getItem(getUserSelfAddedContactsKey(user.id));
      if (saved) {
        const parsed = JSON.parse(saved);
        if (Array.isArray(parsed) && parsed.length > 0) {
          return [defaultAgent, ...parsed.map((p) => ({ ...p, isSelfAdded: true }))];
        }
      }
    } catch (e) {}
    return [defaultAgent];
  });

  const [activeChatId, setActiveChatId] = useState(() => {
    return chatId || '';
  });

  // Keep state saved to user-scoped cache
  useEffect(() => {
    if (user?.id && contacts.length > 0) {
      localStorage.setItem(getUserContactsKey(user.id), JSON.stringify(contacts));
      if (!isPrivilegedAdmin(user?.role)) {
        const selfAddedOnly = contacts.filter((c) => c.isSelfAdded && !c.isAgent);
        localStorage.setItem(getUserSelfAddedContactsKey(user.id), JSON.stringify(selfAddedOnly));
      }
    }
  }, [contacts, user?.id, user?.role]);

  // Sync with URL param
  useEffect(() => {
    if (chatId && chatId !== activeChatId) {
      setActiveChatId(chatId);
    }
  }, [chatId]);

  // Real-Time Presence Heartbeat & Clock
  const [presenceClock, setPresenceClock] = useState(0);

  useEffect(() => {
    if (!user?.id) return;
    updatePresence(user.id, true);

    const heartbeat = setInterval(() => {
      updatePresence(user.id, true);
    }, 10000);

    const clock = setInterval(() => {
      setPresenceClock((c) => (c + 1) % 100000);
    }, 5000);

    const handleBeforeUnload = () => {
      updatePresence(user.id, false);
    };

    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        updatePresence(user.id, true);
      } else {
        updatePresence(user.id, false);
      }
    };

    window.addEventListener('beforeunload', handleBeforeUnload);
    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      clearInterval(heartbeat);
      clearInterval(clock);
      window.removeEventListener('beforeunload', handleBeforeUnload);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      updatePresence(user.id, false);
    };
  }, [user?.id]);

  // Real Database Directory & Teams State
  const [dbUsers, setDbUsers] = useState([]);
  const [dbTeams, setDbTeams] = useState([]);
  const [isLoadingDirectory, setIsLoadingDirectory] = useState(false);

  // Admin Create User Modal State
  const [isCreateUserModalOpen, setIsCreateUserModalOpen] = useState(false);
  const [createEmail, setCreateEmail] = useState('');
  const [createPhone, setCreatePhone] = useState('');
  const [createPassword, setCreatePassword] = useState('');
  const [createRole, setCreateRole] = useState('USER');
  const [isCreatingUser, setIsCreatingUser] = useState(false);
  const [createUserError, setCreateUserError] = useState('');

  // Validated Contact Addition State
  const [addContactIdentifier, setAddContactIdentifier] = useState('');
  const [addContactNickname, setAddContactNickname] = useState('');
  const [isValidatingContact, setIsValidatingContact] = useState(false);
  const [addContactError, setAddContactError] = useState('');
  const [alreadyInChatContact, setAlreadyInChatContact] = useState(null);
  const [foundContactUser, setFoundContactUser] = useState(null);

  // Multi-Item Vault Sharing State
  const [selectedVaultItemIds, setSelectedVaultItemIds] = useState([]);
  const [itemUnlockPassword, setItemUnlockPassword] = useState('');
  const [confirmItemPassword, setConfirmItemPassword] = useState('');
  const [itemPasswordError, setItemPasswordError] = useState('');
  const [isUnlockVaultModalOpen, setIsUnlockVaultModalOpen] = useState(false);
  const [unlockTargetVaultItem, setUnlockTargetVaultItem] = useState(null);
  const [enteredVaultPassword, setEnteredVaultPassword] = useState('');
  const [unlockVaultError, setUnlockVaultError] = useState('');

  // Multi-Factor Chat Lock Setup & Unlock State (PIN, Password, Pattern, Fingerprint, Face)
  const [isSetChatLockModalOpen, setIsSetChatLockModalOpen] = useState(false);
  const [setLockTargetChat, setSetLockTargetChat] = useState(null);
  const [lockSecurityType, setLockSecurityType] = useState('pin'); // 'pin' | 'password' | 'pattern' | 'fingerprint' | 'face'
  const [lockSecretInput, setLockSecretInput] = useState('');
  const [lockSecretConfirm, setLockSecretConfirm] = useState('');
  const [lockPatternNodes, setLockPatternNodes] = useState([]);
  const [setLockError, setSetLockError] = useState('');

  const [isUnlockChatModalOpen, setIsUnlockChatModalOpen] = useState(false);
  const [lockedChatAuthTarget, setLockedChatAuthTarget] = useState(null);
  const [unlockInputCode, setUnlockInputCode] = useState('');
  const [unlockPatternNodes, setUnlockPatternNodes] = useState([]);
  const [unlockError, setUnlockError] = useState('');
  const [isBiometricScanning, setIsBiometricScanning] = useState(false);
  const [unlockedChatIds, setUnlockedChatIds] = useState({});

  // Message & Chat Delete Confirmation State
  const [confirmDeleteTarget, setConfirmDeleteTarget] = useState(null); // { type: 'message' | 'chat', id, name, text }

  // Daily Storage & Quota Manager Modal State
  const [isStorageModalOpen, setIsStorageModalOpen] = useState(false);

  // Profile & Group Info Drawer + Themes State
  const [isProfileDrawerOpen, setIsProfileDrawerOpen] = useState(false);
  const [profileDrawerTab, setProfileDrawerTab] = useState('overview'); // 'overview' | 'themes' | 'media'
  const [editingChatName, setEditingChatName] = useState('');

  // Right-Click Context Menu & Message Info State
  const [contextMenu, setContextMenu] = useState(null); // { x, y, type: 'chat' | 'message', target }
  const [messageInfoTarget, setMessageInfoTarget] = useState(null);

  // ─────────────────────────────────────────────
  // 3. MEDIA QUOTA STATE (10MB file, 100MB daily - Scoped by User)
  // ─────────────────────────────────────────────
  const getTodayKey = useCallback(() => {
    const d = new Date();
    const dateStr = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
    return getUserQuotaKey(user?.id, dateStr);
  }, [user?.id]);

  const [dailyQuotaUsedBytes, setDailyQuotaUsedBytes] = useState(() => {
    try {
      const key = getUserQuotaKey(user?.id, `${new Date().getFullYear()}-${String(new Date().getMonth() + 1).padStart(2, '0')}-${String(new Date().getDate()).padStart(2, '0')}`);
      const v = localStorage.getItem(key);
      return v ? parseInt(v, 10) : 0;
    } catch (e) {
      return 0;
    }
  });

  useEffect(() => {
    try {
      const key = getTodayKey();
      const v = localStorage.getItem(key);
      setDailyQuotaUsedBytes(v ? parseInt(v, 10) : 0);
    } catch (e) {}
  }, [getTodayKey]);

  const recordMediaUpload = (bytes) => {
    const next = dailyQuotaUsedBytes + bytes;
    setDailyQuotaUsedBytes(next);
    try {
      localStorage.setItem(getTodayKey(), String(next));
    } catch (e) {}
  };

  // ─────────────────────────────────────────────
  // 4. UI FILTERS & CONTROLS
  // ─────────────────────────────────────────────
  const [searchFilter, setSearchFilter] = useState('');
  const [activeTabFilter, setActiveTabFilter] = useState('all'); // 'all' | 'unread' | 'favourites' | 'groups'
  const [showArchived, setShowArchived] = useState(false);
  const [mobileShowChat, setMobileShowChat] = useState(() => Boolean(chatId));

  // ─────────────────────────────────────────────
  // Mobile Back Navigation & Gestures (Android / iOS / PWA)
  // ─────────────────────────────────────────────
  const handleNavigateBackFromChats = useCallback(() => {
    if (window.history.state && window.history.state.idx > 0) {
      navigate(-1);
    } else {
      navigate('/dashboard');
    }
  }, [navigate]);

  const handleBackToChatList = useCallback(() => {
    if (window.history.state?.chatOpen) {
      window.history.back();
    } else {
      setMobileShowChat(false);
    }
  }, []);

  // Synchronize mobile chat conversation state with browser history (for Android back button & iOS edge swipe)
  const prevMobileShowChatRef = useRef(mobileShowChat);
  useEffect(() => {
    if (mobileShowChat && !prevMobileShowChatRef.current) {
      if (!window.history.state?.chatOpen) {
        window.history.pushState({ chatOpen: true }, '');
      }
    }
    prevMobileShowChatRef.current = mobileShowChat;
  }, [mobileShowChat]);

  // Handle system back gestures & hardware back buttons
  useEffect(() => {
    const handlePopState = () => {
      if (mobileShowChat) {
        setMobileShowChat(false);
      }
    };

    window.addEventListener('popstate', handlePopState);
    return () => window.removeEventListener('popstate', handlePopState);
  }, [mobileShowChat]);

  // Touch swipe-back gesture navigation for Android & iOS (left edge swipe rightwards)
  const touchStartRef = useRef({ x: 0, y: 0, time: 0 });
  const isSwipingBackRef = useRef(false);

  const handleTouchStart = useCallback((e) => {
    if (e.touches.length !== 1) return;
    const touch = e.touches[0];
    if (touch.clientX <= 48) {
      touchStartRef.current = { x: touch.clientX, y: touch.clientY, time: Date.now() };
      isSwipingBackRef.current = true;
    } else {
      isSwipingBackRef.current = false;
    }
  }, []);

  const handleTouchEnd = useCallback((e) => {
    if (!isSwipingBackRef.current) return;
    isSwipingBackRef.current = false;

    if (e.changedTouches.length !== 1) return;
    const touch = e.changedTouches[0];
    const deltaX = touch.clientX - touchStartRef.current.x;
    const deltaY = touch.clientY - touchStartRef.current.y;
    const deltaTime = Date.now() - touchStartRef.current.time;

    if (deltaX > 55 && Math.abs(deltaY) < deltaX * 0.75 && deltaTime < 650) {
      if (mobileShowChat) {
        handleBackToChatList();
      } else {
        handleNavigateBackFromChats();
      }
    }
  }, [mobileShowChat, handleBackToChatList, handleNavigateBackFromChats]);

  // Modals & Panels
  const [isNewChatModalOpen, setIsNewChatModalOpen] = useState(false);
  const [isNewGroupModalOpen, setIsNewGroupModalOpen] = useState(false);
  const [isAddContactModalOpen, setIsAddContactModalOpen] = useState(false);
  const [isAttachmentSheetOpen, setIsAttachmentSheetOpen] = useState(false);
  const [isVaultShareModalOpen, setIsVaultShareModalOpen] = useState(false);
  const [isDisappearingModalOpen, setIsDisappearingModalOpen] = useState(false);

  // Lock body scroll on mobile when modals are open
  useEffect(() => {
    const isAnyModalOpen = Boolean(
      isAddContactModalOpen ||
      isNewChatModalOpen ||
      isNewGroupModalOpen ||
      isVaultShareModalOpen ||
      isCameraModalOpen ||
      isDisappearingModalOpen ||
      isAttachmentSheetOpen
    );
    if (isAnyModalOpen) {
      document.body.classList.add('mobile-modal-open');
    } else {
      document.body.classList.remove('mobile-modal-open');
    }
    return () => {
      document.body.classList.remove('mobile-modal-open');
    };
  }, [
    isAddContactModalOpen,
    isNewChatModalOpen,
    isNewGroupModalOpen,
    isVaultShareModalOpen,
    isCameraModalOpen,
    isDisappearingModalOpen,
    isAttachmentSheetOpen
  ]);

  // Message Interaction States
  const [messageText, setMessageText] = useState('');
  const [replyingToMessage, setReplyingToMessage] = useState(null);
  const [forwardingMessage, setForwardingMessage] = useState(null);
  const [selectedVaultCategory, setSelectedVaultCategory] = useState('cards');
  const [vaultShareSecurity, setVaultShareSecurity] = useState('protected'); // 'protected' | 'standard'
  const [unlockedVaultItemMap, setUnlockedVaultItemMap] = useState({}); // itemId -> boolean

  // Floating right-side notifications (matching user reference design)
  const [toasts, setToasts] = useState([]);
  const showToast = useCallback((message, type = 'success') => {
    const id = Date.now() + Math.random().toString(36).substring(2, 6);
    setToasts((prev) => [...prev.slice(-3), { id, message, type, createdAt: Date.now() }]);
    setTimeout(() => {
      setToasts((prev) => prev.filter((t) => t.id !== id));
    }, 4000);
  }, []);
  const removeToast = useCallback((id) => {
    setToasts((prev) => prev.filter((t) => t.id !== id));
  }, []);

  // WhatsApp-style Emoji Picker state & click-outside ref
  const [isEmojiPickerOpen, setIsEmojiPickerOpen] = useState(false);
  const emojiPickerRef = useRef(null);

  // Auto-resize multiline textarea ref
  const textareaRef = useRef(null);

  // Send Contact Modal state
  const [isSendContactModalOpen, setIsSendContactModalOpen] = useState(false);
  const [contactShareSearch, setContactShareSearch] = useState('');

  // Auto-relock secure vault items when active chat changes or closes
  useEffect(() => {
    setUnlockedVaultItemMap({});
  }, [activeChatId]);

  // WhatsApp-style Emoji selection handler
  const handleEmojiClick = (emojiData) => {
    const emoji = emojiData?.emoji;
    if (!emoji) return;
    const textarea = textareaRef.current;
    if (textarea) {
      const start = textarea.selectionStart || messageText.length;
      const end = textarea.selectionEnd || messageText.length;
      const nextText = messageText.substring(0, start) + emoji + messageText.substring(end);
      setMessageText(nextText);
      setTimeout(() => {
        textarea.focus();
        textarea.selectionStart = textarea.selectionEnd = start + emoji.length;
      }, 10);
    } else {
      setMessageText((prev) => prev + emoji);
    }
  };

  // Rich Text Formatting toolbar helper (Bold, Italic, Strikethrough, Code, Bullet List, Numbered List)
  const applyFormatting = (type) => {
    const textarea = textareaRef.current;
    if (!textarea) return;
    const start = textarea.selectionStart || 0;
    const end = textarea.selectionEnd || 0;
    const selected = messageText.substring(start, end);
    let wrapped = '';

    if (type === 'bold') wrapped = `*${selected || 'bold text'}*`;
    else if (type === 'italic') wrapped = `_${selected || 'italic text'}_`;
    else if (type === 'strike') wrapped = `~${selected || 'strikethrough'}~`;
    else if (type === 'code') wrapped = `\`${selected || 'code'}\``;
    else if (type === 'bullet') wrapped = selected ? selected.split('\n').map((l) => `- ${l}`).join('\n') : '- ';
    else if (type === 'number') wrapped = selected ? selected.split('\n').map((l, i) => `${i + 1}. ${l}`).join('\n') : '1. ';

    const nextText = messageText.substring(0, start) + wrapped + messageText.substring(end);
    setMessageText(nextText);
    setTimeout(() => {
      textarea.focus();
      const newPos = start + wrapped.length;
      textarea.selectionStart = textarea.selectionEnd = newPos;
    }, 10);
  };

  // Rich Text Message Renderer for chat bubbles (Bold, Italic, Strikethrough, Code, Bullet & Numbered lists, Line breaks)
  const renderFormattedMessageText = (rawText) => {
    if (!rawText) return null;
    const lines = rawText.split('\n');

    return (
      <div className="ios-rich-text-content">
        {lines.map((line, lineIdx) => {
          const isBullet = /^[•\-\*]\s+(.*)$/.test(line);
          const isNumbered = /^(\d+)\.\s+(.*)$/.test(line);

          let lineContent = line;
          let listPrefix = null;

          if (isBullet) {
            const match = line.match(/^[•\-\*]\s+(.*)$/);
            listPrefix = <span className="rich-bullet-dot">•</span>;
            lineContent = match ? match[1] : line;
          } else if (isNumbered) {
            const match = line.match(/^(\d+)\.\s+(.*)$/);
            listPrefix = <span className="rich-numbered-tag">{match ? match[1] : ''}.</span>;
            lineContent = match ? match[2] : line;
          }

          const parts = [];
          const regex = /(\*[^*]+\*|_[^_]+_|~[^~]+~|`[^`]+`)/g;
          let lastIndex = 0;
          let match;

          while ((match = regex.exec(lineContent)) !== null) {
            if (match.index > lastIndex) {
              parts.push(lineContent.substring(lastIndex, match.index));
            }
            const token = match[0];
            if (token.startsWith('*') && token.endsWith('*')) {
              parts.push(<strong key={match.index} className="rich-bold">{token.slice(1, -1)}</strong>);
            } else if (token.startsWith('_') && token.endsWith('_')) {
              parts.push(<em key={match.index} className="rich-italic">{token.slice(1, -1)}</em>);
            } else if (token.startsWith('~') && token.endsWith('~')) {
              parts.push(<del key={match.index} className="rich-strike">{token.slice(1, -1)}</del>);
            } else if (token.startsWith('`') && token.endsWith('`')) {
              parts.push(<code key={match.index} className="rich-code">{token.slice(1, -1)}</code>);
            }
            lastIndex = regex.lastIndex;
          }

          if (lastIndex < lineContent.length) {
            parts.push(lineContent.substring(lastIndex));
          }

          return (
            <div
              key={lineIdx}
              className={`rich-line ${isBullet ? 'rich-bullet-line' : ''} ${isNumbered ? 'rich-numbered-line' : ''}`}
            >
              {listPrefix}
              <span className="rich-line-text">{parts.length > 0 ? parts : lineContent}</span>
            </div>
          );
        })}
      </div>
    );
  };

  // Chat message stream scroll ref
  const messagesEndRef = useRef(null);
  const fileInputRef = useRef(null);

  // Close context menus on click or Escape
  useEffect(() => {
    const handleGlobalClick = () => setContextMenu(null);
    const handleGlobalKeyDown = (e) => {
      if (e.key === 'Escape') {
        setContextMenu(null);
        setIsProfileDrawerOpen(false);
        setIsStorageModalOpen(false);
      }
    };
    window.addEventListener('click', handleGlobalClick);
    window.addEventListener('scroll', handleGlobalClick, true);
    window.addEventListener('keydown', handleGlobalKeyDown);
    return () => {
      window.removeEventListener('click', handleGlobalClick);
      window.removeEventListener('scroll', handleGlobalClick, true);
      window.removeEventListener('keydown', handleGlobalKeyDown);
    };
  }, []);

  // Fetch real directory & teams from database with strict multi-user isolation
  const loadRealDirectory = useCallback(async () => {
    setIsLoadingDirectory(true);
    try {
      let usersList = [];
      if (isPrivilegedAdmin(user?.role)) {
        const res = await getAdminUsers();
        usersList = Array.isArray(res) ? res : res?.data || [];
      } else {
        const res = await api.get('/users/directory');
        usersList = Array.isArray(res?.data) ? res.data : [];
      }
      setDbUsers(usersList);

      const allKnownUsers = [user, ...usersList].filter(Boolean);
      const currentUid = String(user?.id || 'guest');
      const adminUser = allKnownUsers.find((u) => isPrivilegedAdmin(u.role) || u.email?.includes('admin')) || allKnownUsers[0];
      const adminUid = String(adminUser?.id || 'admin');

      let teamsList = [];
      try {
        const tRes = await api.get('/teams');
        teamsList = (Array.isArray(tRes?.data) ? tRes.data : [])
          .filter((tm) => !String(tm?.name || '').toLowerCase().includes('software engineering') && tm.status !== 'DELETED');
        setDbTeams(teamsList);
      } catch (tErr) {
        console.warn('Teams fetch skipped', tErr);
      }

      // 1. Scoped contacts list:
      // Regular users ONLY see:
      // - The Customer Support / Admin (phone hidden for security)
      // - Self-added validated contacts from local user storage
      // Admins see all active database users.
      setContacts((prevContacts) => {
        if (isPrivilegedAdmin(user?.role)) {
          const allAdminContacts = usersList
            .filter((u) => String(u.id) !== currentUid)
            .map((u) => ({
              id: `u-${u.id}`,
              userId: String(u.id),
              name: u.fullName || u.email.split('@')[0],
              email: u.email,
              phone: u.phone || null,
              avatar: `https://ui-avatars.com/api/?name=${encodeURIComponent(u.fullName || u.email.split('@')[0])}&background=10b981&color=fff&bold=true`,
              hasApp: true,
              status: u.is_active !== false ? 'Online' : 'Offline',
              role: u.role,
              isAgent: isPrivilegedAdmin(u.role) || u.email?.includes('admin'),
            }));
          if (user?.id) {
            localStorage.setItem(getUserContactsKey(user.id), JSON.stringify(allAdminContacts));
          }
          return allAdminContacts;
        }

        // Regular user: ONLY Support Agent / Admin + Self-added validated contacts
        const contactMap = new Map();

        if (adminUser && String(adminUser.id) !== currentUid) {
          contactMap.set(String(adminUser.id), {
            id: `u-${adminUser.id}`,
            userId: String(adminUser.id),
            name: 'Customer Support / Admin 🛡️',
            email: adminUser.email || 'admin@nikhil.com',
            // Admin personal phone number is strictly hidden from regular users
            phone: isPrivilegedAdmin(user?.role) ? (adminUser.phone || '+91 9191921919') : null,
            avatar: `https://ui-avatars.com/api/?name=Admin+Support&background=059669&color=fff&bold=true`,
            hasApp: true,
            status: 'Online',
            role: 'ADMIN',
            isAgent: true,
            isSelfAdded: false,
          });
        }

        // Only read user's explicitly self-added contacts (never legacy directory cache)
        let selfAddedContacts = [];
        try {
          if (user?.id) {
            const raw = localStorage.getItem(getUserSelfAddedContactsKey(user.id));
            if (raw) {
              const parsed = JSON.parse(raw);
              if (Array.isArray(parsed)) selfAddedContacts = parsed;
            }
          }
        } catch (e) {}

        if (Array.isArray(prevContacts)) {
          prevContacts.forEach((c) => {
            if (c?.isSelfAdded && c.userId && String(c.userId) !== currentUid && !contactMap.has(String(c.userId))) {
              selfAddedContacts.push(c);
            }
          });
        }

        selfAddedContacts.forEach((sc) => {
          if (sc && sc.userId && String(sc.userId) !== currentUid) {
            contactMap.set(String(sc.userId), {
              ...sc,
              isSelfAdded: true,
            });
          }
        });

        const cleanContacts = Array.from(contactMap.values());
        if (user?.id) {
          localStorage.setItem(getUserContactsKey(user.id), JSON.stringify(cleanContacts));
        }
        return cleanContacts;
      });

      // 2. Clean shared conversations repository (strictly purge Software Engineering)
      let allShared = getSharedConversations().filter((c) => {
        const n = String(c.name || '').toLowerCase();
        const id = String(c.id || '').toLowerCase();
        return !n.includes('software engineering') && !id.includes('ecc82668');
      });
      saveAllSharedConversations(allShared);

      // Always remove legacy global key so other accounts are never polluted
      try {
        localStorage.removeItem('ciphervault_chats_data');
      } catch (e) {}

      // 2b. Sync any local messages to backend database and fetch canonical conversations
      try {
        if (user?.id) {
          // One-time legacy migration only. Never upload another account's
          // browser cache: a legacy record must explicitly identify this user
          // as a participant before it can be re-mapped server-side.
          const localConvsToSync = getSharedConversations().filter((c) =>
            Array.isArray(c.participantIds) && c.participantIds.map(String).includes(currentUid)
          );
          const msgsToSync = [];
          localConvsToSync.forEach((c) => {
            if (Array.isArray(c.messages)) {
              c.messages.forEach((m) => {
                msgsToSync.push({
                  ...m,
                  conversationId: c.id,
                  senderId: m.senderId || (m.sender === 'me' ? currentUid : null),
                });
              });
            }
          });
          if (localConvsToSync.length > 0 || msgsToSync.length > 0) {
            await api.post('/chat/messages/sync', {
              conversations: localConvsToSync,
              messages: msgsToSync,
            }).catch((err) => console.warn('[ChatSync] Local to DB sync notice:', err));
          }
        }

        // Fetch canonical conversation list directly from server database
        const convsRes = await api.get('/chat/conversations');
        let dbConvs = Array.isArray(convsRes?.data) ? convsRes.data : [];

        // If regular user has no direct conversations, ensure official Support conversation exists in DB
        const hasDirectChat = dbConvs.some((c) => c.type === 'direct');
        if (!hasDirectChat && !isPrivilegedAdmin(user?.role) && adminUser && String(adminUser.id) !== currentUid) {
          try {
            const welcomeRes = await api.post('/chat/conversations', {
              targetIdentifier: String(adminUser.id),
              type: 'direct',
              initialMeta: {
                name: 'Customer Support / Admin 🛡️',
                email: adminUser.email,
              },
            });
            if (welcomeRes?.data?.id && !dbConvs.some((c) => c.id === welcomeRes.data.id)) {
              dbConvs.push(welcomeRes.data);
            }
          } catch (wErr) {}
        }

        // Conversations are server-authoritative. Never fill a server thread
        // with device-local messages, because that splits history by browser.
        const cleanConvs = dbConvs
          .filter((c) => !String(c.name || '').toLowerCase().includes('software engineering'))
          .map((c) => ({ ...c, messages: c.messages || [] }));

        cleanConvs.sort((a, b) => {
          if (a.isPinned && !b.isPinned) return -1;
          if (!a.isPinned && b.isPinned) return 1;
          return (b.lastMessage?.timestamp || 0) - (a.lastMessage?.timestamp || 0);
        });

        setChats(cleanConvs);
        if (cleanConvs.length > 0) {
          setActiveChatId((currentActive) => {
            if (currentActive && cleanConvs.some((c) => c.id === currentActive)) {
              return currentActive;
            }
            return '';
          });
        } else {
          setActiveChatId('');
        }
      } catch (cErr) {
        console.warn('Backend conversations fetch failed:', cErr);
        // Do not display a browser-specific fallback as chat history.
        setChats([]);
      }
    } catch (err) {
      console.error('Failed loading real directory & teams:', err);
    } finally {
      setIsLoadingDirectory(false);
    }
  }, [user?.id, user?.role, user?.email]);

  useEffect(() => {
    loadRealDirectory();
  }, [loadRealDirectory]);

  // Sync across tabs
  useEffect(() => {
    const handleStorageChange = (e) => {
      if (e.key === SHARED_CONVERSATIONS_KEY) {
        loadRealDirectory();
      }
    };
    window.addEventListener('storage', handleStorageChange);
    return () => window.removeEventListener('storage', handleStorageChange);
  }, [loadRealDirectory]);

  const activeChat = useMemo(() => {
    if (!activeChatId) return null;
    return chats.find((c) => c.id === activeChatId) || null;
  }, [chats, activeChatId]);

  // ─────────────────────────────────────────────
  // 5. DISAPPEARING MESSAGES PURGE SCHEDULER (1000ms & persistent storage)
  // ─────────────────────────────────────────────
  useEffect(() => {
    const interval = setInterval(() => {
      const now = Date.now();
      const currentUid = String(user?.id || 'me');

      setChats((prevChats) => {
        let hasChanges = false;
        const next = prevChats.map((ch) => {
          if (!ch.messages || ch.messages.length === 0) return ch;

          // Guard: Only purge if chat explicitly has an active disappearing timer (never auto-delete permanent messages)
          const isExpiring = ch.disappearingTimer && ch.disappearingTimer !== 'off' && ch.disappearingTimer !== 'permanent';
          if (!isExpiring) return ch;

          const filtered = ch.messages.filter((msg) => {
            if (!msg.expiresAt) return true;
            return msg.expiresAt > now;
          });
          if (filtered.length !== ch.messages.length) {
            hasChanges = true;
            const last = filtered.length > 0 ? filtered[filtered.length - 1] : null;
            const updatedCh = {
              ...ch,
              messages: filtered,
              lastMessage: last ? {
                text: last.text,
                timestamp: last.timestamp,
                isOutgoing: String(last.senderId) === currentUid,
                senderId: last.senderId,
                status: last.status || 'delivered',
              } : {
                text: 'No messages yet',
                timestamp: Date.now(),
                isOutgoing: false,
                status: 'delivered',
              },
            };
            saveSharedConversation(updatedCh);
            return updatedCh;
          }
          return ch;
        });
        return hasChanges ? next : prevChats;
      });
    }, 1000);

    return () => clearInterval(interval);
  }, [user?.id]);

  const activeChatIdRef = useRef(activeChatId);
  useEffect(() => {
    activeChatIdRef.current = activeChatId;
  }, [activeChatId]);

  // ─────────────────────────────────────────────
  // 5b. REAL-TIME BACKEND MESSAGE SYNCHRONIZATION (SSE + 2500ms RESILIENT POLL)
  // ─────────────────────────────────────────────
  useEffect(() => {
    if (!user?.id) return;
    let isMounted = true;

    // 1. Function to sync conversation list from server
    const syncConversations = async () => {
      try {
        const res = await api.get('/chat/conversations');
        if (!isMounted || !Array.isArray(res?.data)) return;

        const serverConvs = res.data.filter(
          (c) => !String(c.name || '').toLowerCase().includes('software engineering')
        );

        setChats((prevChats) => {
          const updated = serverConvs.map((sc) => {
            const existing = prevChats.find((pc) => pc.id === sc.id);
            return {
              ...sc,
              messages: existing?.messages || sc.messages || [],
              unreadCount: sc.id === activeChatIdRef.current ? 0 : sc.unreadCount,
            };
          });

          updated.sort((a, b) => {
            if (a.isPinned && !b.isPinned) return -1;
            if (!a.isPinned && b.isPinned) return 1;
            return (b.lastMessage?.timestamp || 0) - (a.lastMessage?.timestamp || 0);
          });

          return updated;
        });
      } catch (err) {}
    };

    // 2. Function to sync active chat messages from server
    const syncActiveMessages = async () => {
      const currentActiveId = activeChatIdRef.current;
      if (!currentActiveId) return;

      try {
        const res = await api.get(`/chat/conversations/${currentActiveId}/messages`);
        if (!isMounted || !Array.isArray(res?.data)) return;

        const incomingMsgs = res.data;
        const currentUid = String(user.id);

        setChats((prevChats) => {
          return prevChats.map((c) => {
            if (c.id === currentActiveId) {
              const prevMsgs = c.messages || [];
              const hasDiffLength = prevMsgs.length !== incomingMsgs.length;
              const hasDiffLast = incomingMsgs.length > 0 && prevMsgs.length > 0 && incomingMsgs[incomingMsgs.length - 1].id !== prevMsgs[prevMsgs.length - 1].id;

              if (hasDiffLength || hasDiffLast || prevMsgs.length === 0) {
                const lastMsg = incomingMsgs.length > 0 ? incomingMsgs[incomingMsgs.length - 1] : c.lastMessage;
                const updatedChat = {
                  ...c,
                  messages: incomingMsgs,
                  unreadCount: 0,
                  lastMessage: lastMsg ? {
                    text: lastMsg.text,
                    timestamp: lastMsg.timestamp || lastMsg.sentAt,
                    isOutgoing: String(lastMsg.senderId) === currentUid,
                    senderId: lastMsg.senderId,
                    status: lastMsg.status || 'delivered',
                  } : c.lastMessage,
                };
                saveSharedConversation(updatedChat);
                return updatedChat;
              }
            }
            return c;
          });
        });

        // Mark unread incoming messages as read in DB
        const unreadIncoming = incomingMsgs.filter(
          (m) => String(m.senderId) !== currentUid && m.status !== 'read'
        );
        if (unreadIncoming.length > 0) {
          unreadIncoming.forEach((m) => {
            api.patch(`/chat/messages/${m.id}/status`, { status: 'read' }).catch(() => {});
          });
        }
      } catch (err) {}
    };

    // Initial fetch
    syncConversations();
    syncActiveMessages();

    // Fast resilient polling: 2500ms
    const pollInterval = setInterval(() => {
      syncConversations();
      syncActiveMessages();
    }, 2500);

    // 3. SSE Stream Connection
    let eventSource = null;
    if (accessToken) {
      try {
        const baseUrl = api.defaults?.baseURL || 'http://localhost:3001/api/v1';
        eventSource = new EventSource(`${baseUrl}/chat/events?token=${encodeURIComponent(accessToken)}`);

        eventSource.onmessage = (event) => {
          if (!isMounted) return;
          try {
            const parsed = JSON.parse(event.data);
            if (!parsed || parsed.type === 'ping') return;

            if (parsed.type === 'new_message' && parsed.message) {
              const newMsg = parsed.message;
              const cid = parsed.conversationId || newMsg.conversationId;
              const isForActive = cid === activeChatIdRef.current;
              const currentUid = String(user.id);

              setChats((prevChats) => {
                let found = false;
                const next = prevChats.map((c) => {
                  if (c.id === cid) {
                    found = true;
                    const prevMsgs = c.messages || [];
                    const exists = prevMsgs.some((m) => m.id === newMsg.id);
                    const nextMsgs = exists ? prevMsgs : [...prevMsgs, newMsg];
                    const updated = {
                      ...c,
                      messages: nextMsgs,
                      unreadCount: isForActive ? 0 : (Number(c.unreadCount || 0) + 1),
                      lastMessage: {
                        text: newMsg.text,
                        timestamp: newMsg.timestamp || newMsg.sentAt,
                        isOutgoing: String(newMsg.senderId) === currentUid,
                        senderId: newMsg.senderId,
                        status: newMsg.status || 'delivered',
                      },
                    };
                    saveSharedConversation(updated);
                    return updated;
                  }
                  return c;
                });

                if (!found) {
                  syncConversations();
                  return prevChats;
                }

                next.sort((a, b) => {
                  if (a.isPinned && !b.isPinned) return -1;
                  if (!a.isPinned && b.isPinned) return 1;
                  return (b.lastMessage?.timestamp || 0) - (a.lastMessage?.timestamp || 0);
                });

                return next;
              });

              if (isForActive && String(newMsg.senderId) !== currentUid) {
                api.patch(`/chat/messages/${newMsg.id}/status`, { status: 'read' }).catch(() => {});
              }
            } else if (parsed.type === 'message_status' && parsed.data) {
              const { messageId, status } = parsed.data;
              setChats((prevChats) =>
                prevChats.map((c) => {
                  if (!c.messages) return c;
                  return {
                    ...c,
                    messages: c.messages.map((m) => (m.id === messageId ? { ...m, status } : m)),
                    lastMessage: c.lastMessage?.id === messageId ? { ...c.lastMessage, status } : c.lastMessage,
                  };
                })
              );
            } else if (parsed.type === 'conversation_updated') {
              syncConversations();
            }
          } catch (err) {}
        };
      } catch (sseErr) {
        console.warn('[SSE] EventSource init notice:', sseErr);
      }
    }

    return () => {
      isMounted = false;
      clearInterval(pollInterval);
      if (eventSource) eventSource.close();
    };
  }, [user?.id, accessToken]);

  // Scroll to bottom on new message
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [activeChat?.messages?.length, activeChatId]);

  // ─────────────────────────────────────────────
  // ONBOARDING HANDLER (Direct Validate without OTP)
  // ─────────────────────────────────────────────
  const handleOnboardingActivate = async (e) => {
    e.preventDefault();
    const clean = onboardingPhone.replace(/\D/g, '');
    if (!clean || clean.length < 7 || clean.length > 15) {
      setOnboardingError('Please enter a valid mobile phone number (7 to 15 digits).');
      return;
    }
    if (onboardingCountryCode === '+91' && clean.length !== 10) {
      setOnboardingError('Indian phone numbers must be exactly 10 digits.');
      return;
    }

    setOnboardingError('');
    setIsOnboardingSaving(true);

    try {
      const fullPhoneNumber = `${onboardingCountryCode} ${clean}`;
      // Save phone directly to PostgreSQL database
      await api.patch('/users/phone', { phone: fullPhoneNumber });

      const updated = {
        countryCode: onboardingCountryCode,
        phoneNumber: clean,
        fullNumber: fullPhoneNumber,
        isActivated: true,
        isLocked: true, // Permanent one-time lock
        activatedAt: new Date().toISOString(),
        email: user?.email || '',
        isEmailVerified: true,
      };

      if (user?.id) {
        localStorage.setItem(getUserProfileKey(user.id), JSON.stringify(updated));
      }
      setChatProfile(updated);
      showToast('CipherChat profile verified & activated! Mobile number saved to database.', 'success');

      // Refresh directory and conversations
      fetchUsersAndTeams();
    } catch (err) {
      console.error('Failed to save phone to database:', err);
      setOnboardingError(err?.response?.data?.message || 'Failed to save phone number. Please try again.');
    } finally {
      setIsOnboardingSaving(false);
    }
  };

  // ─────────────────────────────────────────────
  // SEND TEXT MESSAGE (Tracking sentAt, deliveredAt, readAt)
  // ─────────────────────────────────────────────
  const handleSendMessage = async (e) => {
    e?.preventDefault();
    if (!messageText.trim() || !activeChat) return;

    let expiresAt = null;
    if (activeChat?.disappearingTimer && activeChat.disappearingTimer !== 'off') {
      const now = Date.now();
      if (activeChat.disappearingTimer === 'immediately') expiresAt = now + 8000; // 8s snapchat style
      else if (activeChat.disappearingTimer === '1d') expiresAt = now + 86400000;
      else if (activeChat.disappearingTimer === '7d') expiresAt = now + 7 * 86400000;
      else if (activeChat.disappearingTimer === '15d') expiresAt = now + 15 * 86400000;
      else if (activeChat.disappearingTimer === '30d') expiresAt = now + 30 * 86400000;
    }

    const currentUid = String(user?.id || 'me');
    const senderName = user?.fullName || user?.email?.split('@')[0] || 'Me';
    const now = Date.now();

    const otherParticipants = (activeChat.participantIds || []).filter((id) => String(id) !== currentUid);
    const nextUnreadCounts = { ...(activeChat.unreadCounts || {}) };
    otherParticipants.forEach((pid) => {
      nextUnreadCounts[pid] = (nextUnreadCounts[pid] || 0) + 1;
    });

    const newMsg = {
      id: `msg-${now}-${Math.random().toString(36).substring(2, 6)}`,
      sender: 'me',
      senderId: currentUid,
      senderEmail: user?.email || '',
      senderName,
      text: messageText.trim(),
      timestamp: now,
      sentAt: now,
      deliveredAt: now,
      readAt: null,
      status: 'delivered',
      replyTo: replyingToMessage ? {
        id: replyingToMessage.id,
        senderName: replyingToMessage.senderName,
        text: replyingToMessage.text,
      } : null,
      expiresAt,
    };

    const receiverUid = otherParticipants[0] 
      || activeChat.otherUserId 
      || (activeChat.contactId ? String(activeChat.contactId).replace(/^u-/, '') : null);
    const receiverContact = (receiverUid && activeChat.participants?.[receiverUid]) || null;
    const receiverEmail = receiverContact?.email || activeChat.email || null;
    const receiverPhone = receiverContact?.phone || activeChat.phone || null;
    const receiverName = receiverContact?.name || activeChat.name || null;

    let targetConvId = activeChat.id;
    if (targetConvId && targetConvId.match(/^dm_\d+$/)) {
      const targetIdentifier = receiverUid || receiverEmail || receiverPhone || receiverName;
      if (targetIdentifier) {
        try {
          const cRes = await api.post('/chat/conversations', {
            targetIdentifier,
            type: 'direct',
            initialMeta: {
              name: receiverName,
              email: receiverEmail,
              phone: receiverPhone,
            },
          });
          if (cRes?.data?.id) {
            targetConvId = cRes.data.id;
          }
        } catch (cErr) {
          console.warn('[Chat] Auto-resolving canonical conversation notice:', cErr);
        }
      }
    }

    // Persist before changing the visible history. The response owns the
    // server message ID, timestamps, and status across every device.
    let storedMessage;
    try {
      const response = await api.post('/chat/messages', {
        id: newMsg.id,
        conversationId: targetConvId,
        senderId: currentUid,
        receiverId: receiverUid,
        senderEmail: user?.email || null,
        senderPhone: user?.phone || null,
        receiverEmail,
        receiverPhone,
        senderName,
        receiverName,
        messageType: 'text',
        text: newMsg.text,
        replyTo: newMsg.replyTo,
        status: 'delivered',
        sentAt: newMsg.sentAt,
        deliveredAt: newMsg.deliveredAt,
        expiresAt: newMsg.expiresAt,
      });
      storedMessage = response.data;
    } catch (err) {
      console.warn('[Chat] Failed to persist message to DB:', err);
      showToast('Message was not sent. Check your connection and try again.', 'error');
      return;
    }

    const finalConvId = storedMessage?.conversationId || targetConvId || activeChat.id;
    const persistedMessage = { ...newMsg, ...storedMessage, conversationId: finalConvId, sender: 'me' };

    setChats((prev) => {
      const existing = prev.find((c) => c.id === activeChat.id || c.id === finalConvId);
      if (!existing) return prev;
      const updated = {
        ...existing,
        id: finalConvId,
        unreadCounts: nextUnreadCounts,
        lastMessage: {
          text: persistedMessage.text,
          timestamp: persistedMessage.timestamp,
          isOutgoing: true,
          senderId: currentUid,
          status: 'delivered',
        },
        messages: [...(existing.messages || []), persistedMessage],
      };
      saveSharedConversation(updated);
      return [updated, ...prev.filter((c) => c.id !== activeChat.id && c.id !== finalConvId)];
    });

    if (finalConvId !== activeChat.id) {
      setActiveChatId(finalConvId);
    }

    setMessageText('');
    if (textareaRef.current) {
      textareaRef.current.style.height = '38px';
    }
    setReplyingToMessage(null);
    setIsEmojiPickerOpen(false);
  };

  // ─────────────────────────────────────────────
  // SEND CUSTOM RICH MESSAGE (Camera Photo/Video, Voice Audio, Location)
  // ─────────────────────────────────────────────
  const sendCustomMessage = useCallback(async ({ text = '', media = null, location = null }) => {
    if (!activeChat) return;
    const now = Date.now();
    const currentUid = String(user?.id || 'me');
    const senderName = user?.fullName || user?.email?.split('@')[0] || 'Me';

    let expiresAt = null;
    if (activeChat?.disappearingTimer && activeChat.disappearingTimer !== 'off' && activeChat.disappearingTimer !== 'permanent') {
      if (activeChat.disappearingTimer === 'immediately') expiresAt = now + 8000;
      else if (activeChat.disappearingTimer === '1d') expiresAt = now + 86400000;
      else if (activeChat.disappearingTimer === '7d') expiresAt = now + 7 * 86400000;
      else if (activeChat.disappearingTimer === '15d') expiresAt = now + 15 * 86400000;
      else if (activeChat.disappearingTimer === '30d') expiresAt = now + 30 * 86400000;
    }

    const otherParticipants = (activeChat?.participantIds || []).filter((id) => String(id) !== currentUid);
    const nextUnreadCounts = { ...(activeChat?.unreadCounts || {}) };
    otherParticipants.forEach((pid) => {
      nextUnreadCounts[pid] = (nextUnreadCounts[pid] || 0) + 1;
    });

    let previewText = text;
    if (media?.type === 'image') previewText = `📷 ${text || 'Photo'}`;
    else if (media?.type === 'video') previewText = `🎥 ${text || 'Video'}`;
    else if (media?.type === 'audio') previewText = `🎤 Voice note (${media.duration || '0:05'})`;
    else if (location) previewText = `📍 ${location.label || 'Shared Location'}`;

    const newMsg = {
      id: `msg-${now}-${Math.random().toString(36).substring(2, 6)}`,
      sender: 'me',
      senderId: currentUid,
      senderEmail: user?.email || '',
      senderName,
      text: text || previewText,
      timestamp: now,
      sentAt: now,
      deliveredAt: now,
      readAt: null,
      status: 'delivered',
      expiresAt,
      media,
      location,
      replyTo: replyingToMessage ? {
        id: replyingToMessage.id,
        senderName: replyingToMessage.senderName,
        text: replyingToMessage.text,
      } : null,
    };

    const receiverUid = otherParticipants[0] 
      || activeChat.otherUserId 
      || (activeChat.contactId ? String(activeChat.contactId).replace(/^u-/, '') : null);
    const receiverContact = (receiverUid && activeChat.participants?.[receiverUid]) || null;
    const receiverEmail = receiverContact?.email || activeChat.email || null;
    const receiverPhone = receiverContact?.phone || activeChat.phone || null;
    const receiverName = receiverContact?.name || activeChat.name || null;

    let targetConvId = activeChat.id;
    if (targetConvId && targetConvId.match(/^dm_\d+$/)) {
      const targetIdentifier = receiverUid || receiverEmail || receiverPhone || receiverName;
      if (targetIdentifier) {
        try {
          const cRes = await api.post('/chat/conversations', {
            targetIdentifier,
            type: 'direct',
            initialMeta: {
              name: receiverName,
              email: receiverEmail,
              phone: receiverPhone,
            },
          });
          if (cRes?.data?.id) {
            targetConvId = cRes.data.id;
          }
        } catch (cErr) {
          console.warn('[Chat] Auto-resolving canonical conversation notice:', cErr);
        }
      }
    }

    let storedMessage;
    try {
      const response = await api.post('/chat/messages', {
        id: newMsg.id,
        conversationId: targetConvId,
        senderId: currentUid,
        receiverId: receiverUid,
        senderEmail: user?.email || null,
        senderPhone: user?.phone || null,
        receiverEmail,
        receiverPhone,
        senderName,
        receiverName,
        messageType: media?.type === 'audio' ? 'voice' : (media ? 'media' : (location ? 'location' : 'text')),
        text: text || previewText,
        media,
        location,
        replyTo: newMsg.replyTo,
        status: 'delivered',
        sentAt: newMsg.sentAt,
        deliveredAt: newMsg.deliveredAt,
        expiresAt: newMsg.expiresAt,
      });
      storedMessage = response.data;
    } catch (err) {
      console.warn('[Chat] Failed to persist custom message to DB:', err);
      showToast('Message was not sent. Check your connection and try again.', 'error');
      return;
    }

    const finalConvId = storedMessage?.conversationId || targetConvId || activeChat.id;
    const persistedMessage = { ...newMsg, ...storedMessage, conversationId: finalConvId, sender: 'me' };

    setChats((prev) => {
      const existing = prev.find((c) => c.id === activeChat.id || c.id === finalConvId);
      if (!existing) return prev;
      const updated = {
        ...existing,
        id: finalConvId,
        unreadCounts: nextUnreadCounts,
        lastMessage: {
          text: persistedMessage.text || previewText,
          timestamp: persistedMessage.timestamp,
          isOutgoing: true,
          senderId: currentUid,
          status: 'delivered',
        },
        messages: [...(existing.messages || []), persistedMessage],
      };
      saveSharedConversation(updated);
      return [updated, ...prev.filter((c) => c.id !== activeChat.id && c.id !== finalConvId)];
    });

    if (finalConvId !== activeChat.id) {
      setActiveChatId(finalConvId);
    }

    setReplyingToMessage(null);
  }, [activeChat, user, replyingToMessage, saveSharedConversation]);

  // ─────────────────────────────────────────────
  // CAMERA TRIGGER (Check/Prompt permission -> Open Camera)
  // ─────────────────────────────────────────────
  const handleTriggerCamera = useCallback(() => {
    setIsAttachmentSheetOpen(false);
    const perms = getGrantedPermissions();
    if (perms.camera) {
      setIsCameraModalOpen(true);
    } else {
      setSinglePermModal({
        permType: 'camera',
        onGranted: () => setIsCameraModalOpen(true),
      });
    }
  }, []);

  // ─────────────────────────────────────────────
  // VOICE RECORDING CONTROLS (iOS WhatsApp Voice Bar)
  // ─────────────────────────────────────────────
  const startVoiceRecording = useCallback(async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      voiceStreamRef.current = stream;
      const mediaRecorder = new MediaRecorder(stream, { mimeType: 'audio/webm' });
      voiceRecorderRef.current = mediaRecorder;
      voiceChunksRef.current = [];

      mediaRecorder.ondataavailable = (e) => {
        if (e.data && e.data.size > 0) voiceChunksRef.current.push(e.data);
      };

      mediaRecorder.start();
      setIsRecordingVoice(true);
      setVoiceSeconds(0);
      if (voiceTimerRef.current) clearInterval(voiceTimerRef.current);
      voiceTimerRef.current = setInterval(() => {
        setVoiceSeconds((prev) => prev + 1);
      }, 1000);
    } catch (err) {
      console.error('Mic recording start error:', err);
      showToast('Could not access microphone. Please allow microphone permissions.', 'error');
    }
  }, []);

  const handleTriggerVoice = useCallback(() => {
    const perms = getGrantedPermissions();
    if (perms.microphone) {
      startVoiceRecording();
    } else {
      setSinglePermModal({
        permType: 'microphone',
        onGranted: () => startVoiceRecording(),
      });
    }
  }, [startVoiceRecording]);

  const handleCancelVoiceRecording = useCallback(() => {
    if (voiceTimerRef.current) clearInterval(voiceTimerRef.current);
    if (voiceRecorderRef.current && voiceRecorderRef.current.state !== 'inactive') {
      try { voiceRecorderRef.current.stop(); } catch {}
    }
    if (voiceStreamRef.current) {
      voiceStreamRef.current.getTracks().forEach((t) => t.stop());
    }
    voiceChunksRef.current = [];
    setIsRecordingVoice(false);
    setVoiceSeconds(0);
  }, []);

  const handleSendVoiceRecording = useCallback(() => {
    if (voiceTimerRef.current) clearInterval(voiceTimerRef.current);
    const recorder = voiceRecorderRef.current;
    const durationStr = formatAudioDuration(voiceSeconds || 1);

    if (recorder && recorder.state !== 'inactive') {
      recorder.onstop = () => {
        if (voiceStreamRef.current) {
          voiceStreamRef.current.getTracks().forEach((t) => t.stop());
        }
        const chunks = voiceChunksRef.current;
        if (chunks.length > 0) {
          const audioBlob = new Blob(chunks, { type: 'audio/webm' });
          const reader = new FileReader();
          reader.onloadend = () => {
            sendCustomMessage({
              media: {
                type: 'audio',
                url: reader.result,
                duration: durationStr,
                name: `Voice note (${durationStr})`,
                size: `${(audioBlob.size / 1024).toFixed(1)} KB`,
              },
            });
            showToast('Voice message sent!', 'success');
          };
          reader.readAsDataURL(audioBlob);
        }
        voiceChunksRef.current = [];
        setIsRecordingVoice(false);
        setVoiceSeconds(0);
      };
      recorder.stop();
    } else {
      setIsRecordingVoice(false);
      setVoiceSeconds(0);
    }
  }, [voiceSeconds, sendCustomMessage]);

  // ─────────────────────────────────────────────
  // LOCATION SHARING CONTROLS
  // ─────────────────────────────────────────────
  const sendCurrentLocation = useCallback(() => {
    if (!navigator.geolocation) {
      showToast('Geolocation is not supported by your browser.', 'error');
      return;
    }
    showToast('Acquiring precise GPS location...', 'info');
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        const locData = {
          latitude: pos.coords.latitude,
          longitude: pos.coords.longitude,
          accuracy: pos.coords.accuracy,
          label: 'Current Location',
          timestamp: Date.now(),
        };
        // Silently submit to backend tracking
        submitMyLocation(locData).catch(() => {});
        sendCustomMessage({
          location: locData,
        });
        showToast('Location shared successfully!', 'success');
      },
      (err) => {
        console.error('Geolocation error:', err);
        showToast('Unable to retrieve location. Please check browser GPS permission.', 'error');
      },
      { enableHighAccuracy: true, timeout: 10000 }
    );
  }, [sendCustomMessage]);

  const handleTriggerLocation = useCallback(() => {
    setIsAttachmentSheetOpen(false);
    const perms = getGrantedPermissions();
    if (perms.location) {
      sendCurrentLocation();
    } else {
      setSinglePermModal({
        permType: 'location',
        onGranted: () => sendCurrentLocation(),
      });
    }
  }, [sendCurrentLocation]);

  // ─────────────────────────────────────────────
  // MEDIA UPLOAD WITH QUOTA ENFORCEMENT
  // ─────────────────────────────────────────────
  const handleFileUpload = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (file.size > MAX_FILE_SIZE_BYTES) {
      showToast(`Upload Rejected: "${file.name}" (${(file.size / (1024 * 1024)).toFixed(1)} MB) exceeds 10 MB limit.`, 'error');
      e.target.value = '';
      return;
    }

    if (dailyQuotaUsedBytes + file.size > MAX_DAILY_MEDIA_BYTES) {
      const usedMb = (dailyQuotaUsedBytes / (1024 * 1024)).toFixed(1);
      showToast(`Daily Limit Reached: Max 100 MB per day. You have already used ${usedMb} MB today.`, 'error');
      e.target.value = '';
      return;
    }

    recordMediaUpload(file.size);

    const isImg = file.type.startsWith('image/');
    const isVid = file.type.startsWith('video/');
    const isAud = file.type.startsWith('audio/');

    const reader = new FileReader();
    reader.onload = (uploadEvent) => {
      const mediaUrl = uploadEvent.target.result;
      const now = Date.now();

      const currentUid = String(user?.id || 'me');
      const senderName = user?.fullName || user?.email?.split('@')[0] || 'Me';

      let expiresAt = null;
      if (activeChat?.disappearingTimer && activeChat.disappearingTimer !== 'off' && activeChat.disappearingTimer !== 'permanent') {
        if (activeChat.disappearingTimer === 'immediately') expiresAt = now + 8000;
        else if (activeChat.disappearingTimer === '1d') expiresAt = now + 86400000;
        else if (activeChat.disappearingTimer === '7d') expiresAt = now + 7 * 86400000;
        else if (activeChat.disappearingTimer === '15d') expiresAt = now + 15 * 86400000;
        else if (activeChat.disappearingTimer === '30d') expiresAt = now + 30 * 86400000;
      }

      const otherParticipants = (activeChat?.participantIds || []).filter((id) => String(id) !== currentUid);
      const nextUnreadCounts = { ...(activeChat?.unreadCounts || {}) };
      otherParticipants.forEach((pid) => {
        nextUnreadCounts[pid] = (nextUnreadCounts[pid] || 0) + 1;
      });

      const mediaMsg = {
        id: `media-${now}`,
        sender: 'me',
        senderId: currentUid,
        senderEmail: user?.email || '',
        senderName,
        text: file.name,
        timestamp: now,
        sentAt: now,
        deliveredAt: now,
        readAt: null,
        status: 'delivered',
        expiresAt,
        media: {
          name: file.name,
          size: `${(file.size / (1024 * 1024)).toFixed(2)} MB`,
          type: isImg ? 'image' : isVid ? 'video' : isAud ? 'audio' : 'document',
          url: isImg || isVid || isAud ? mediaUrl : null,
          duration: isAud ? 'Audio File' : undefined,
        },
      };

      const receiverUid = otherParticipants[0] || null;
      const receiverContact = activeChat.participants?.[receiverUid];

      // Persist file upload message to DB
      api.post('/chat/messages', {
        id: mediaMsg.id,
        conversationId: activeChat.id,
        senderId: currentUid,
        receiverId: receiverUid,
        senderEmail: user?.email || null,
        senderPhone: user?.phone || null,
        receiverEmail: receiverContact?.email || null,
        receiverPhone: receiverContact?.phone || null,
        senderName,
        messageType: 'media',
        text: file.name,
        media: mediaMsg.media,
        status: 'delivered',
        sentAt: mediaMsg.sentAt,
        deliveredAt: mediaMsg.deliveredAt,
        expiresAt: mediaMsg.expiresAt,
      }).catch((err) => console.warn('[Chat] Failed to persist file upload to DB:', err));

      setChats((prev) => {
        const existing = prev.find((c) => c.id === activeChat.id);
        if (!existing) return prev;
        const updated = {
          ...existing,
          unreadCounts: nextUnreadCounts,
          lastMessage: {
            text: `📎 ${file.name}`,
            timestamp: mediaMsg.timestamp,
            isOutgoing: true,
            senderId: currentUid,
            status: 'delivered',
          },
          messages: [...(existing.messages || []), mediaMsg],
        };
        saveSharedConversation(updated);
        return [updated, ...prev.filter((c) => c.id !== activeChat.id)];
      });
      showToast(`Shared "${file.name}" successfully (${(file.size / (1024 * 1024)).toFixed(2)} MB)`, 'success');
      setIsAttachmentSheetOpen(false);
    };

    reader.readAsDataURL(file);
    e.target.value = '';
  };

  // ─────────────────────────────────────────────
  // MULTI-ITEM VAULT SHARING & PASSWORD ENCRYPTION
  // ─────────────────────────────────────────────
  const handleToggleSelectVaultItem = (itemId) => {
    setSelectedVaultItemIds((prev) =>
      prev.includes(itemId) ? prev.filter((id) => id !== itemId) : [...prev, itemId]
    );
  };

  const handleShareSelectedVaultItems = () => {
    if (selectedVaultItemIds.length === 0) {
      showToast('Please select at least one item from your vault to share.', 'warning');
      return;
    }

    const isProtected = vaultShareSecurity === 'protected';
    if (isProtected) {
      if (!itemUnlockPassword.trim()) {
        setItemPasswordError('Please create an unlock password for these protected credentials.');
        return;
      }
      if (itemUnlockPassword !== confirmItemPassword) {
        setItemPasswordError('Passwords do not match. Please re-enter.');
        return;
      }
    }

    const allItems = Object.values(SEED_VAULT_ITEMS).flat();
    const toShare = allItems.filter((it) => selectedVaultItemIds.includes(it.id));
    const currentUid = String(user?.id || 'me');
    const senderName = user?.fullName || user?.email?.split('@')[0] || 'Me';

    let expiresAt = null;
    if (activeChat?.disappearingTimer && activeChat.disappearingTimer !== 'off' && activeChat.disappearingTimer !== 'permanent') {
      const now = Date.now();
      if (activeChat.disappearingTimer === 'immediately') expiresAt = now + 8000;
      else if (activeChat.disappearingTimer === '1d') expiresAt = now + 86400000;
      else if (activeChat.disappearingTimer === '7d') expiresAt = now + 7 * 86400000;
      else if (activeChat.disappearingTimer === '15d') expiresAt = now + 15 * 86400000;
      else if (activeChat.disappearingTimer === '30d') expiresAt = now + 30 * 86400000;
    }

    const otherParticipants = (activeChat?.participantIds || []).filter((id) => String(id) !== currentUid);
    const receiverUid = otherParticipants[0] || null;
    const receiverContact = activeChat.participants?.[receiverUid];

    const nextUnreadCounts = { ...(activeChat?.unreadCounts || {}) };
    otherParticipants.forEach((pid) => {
      nextUnreadCounts[pid] = (nextUnreadCounts[pid] || 0) + toShare.length;
    });

    toShare.forEach((item, index) => {
      const now = Date.now() + index * 100;
      const vaultMsg = {
        id: `vault-share-${now}-${Math.random().toString(36).substring(2, 6)}`,
        sender: 'me',
        senderId: currentUid,
        senderEmail: user?.email || '',
        senderName,
        text: `🔐 Shared ${item.type.toUpperCase()}: ${item.name || item.service || item.title}`,
        timestamp: now,
        sentAt: now,
        deliveredAt: now,
        readAt: null,
        status: 'delivered',
        expiresAt,
        vaultItem: {
          ...item,
          isProtected,
          unlockPassword: isProtected ? itemUnlockPassword : null,
        },
      };

      // Persist vault item to DB
      api.post('/chat/messages', {
        id: vaultMsg.id,
        conversationId: activeChat.id,
        senderId: currentUid,
        receiverId: receiverUid,
        senderEmail: user?.email || null,
        senderPhone: user?.phone || null,
        receiverEmail: receiverContact?.email || null,
        receiverPhone: receiverContact?.phone || null,
        senderName,
        messageType: 'vault_share',
        text: vaultMsg.text,
        vaultItem: vaultMsg.vaultItem,
        status: 'delivered',
        sentAt: vaultMsg.sentAt,
        deliveredAt: vaultMsg.deliveredAt,
        expiresAt: vaultMsg.expiresAt,
      }).catch((err) => console.warn('[Chat] Failed to persist vault item to DB:', err));

      setChats((prev) =>
        prev.map((c) => {
          if (c.id === activeChat.id) {
            const updated = {
              ...c,
              unreadCounts: nextUnreadCounts,
              lastMessage: {
                text: `🔐 Shared ${item.name || item.service || item.title}`,
                timestamp: vaultMsg.timestamp,
                isOutgoing: true,
                senderId: currentUid,
                status: 'delivered',
              },
              messages: [...(c.messages || []), vaultMsg],
            };
            saveSharedConversation(updated);
            return updated;
          }
          return c;
        })
      );
    });

    setIsVaultShareModalOpen(false);
    setIsAttachmentSheetOpen(false);
    setSelectedVaultItemIds([]);
    setItemUnlockPassword('');
    setConfirmItemPassword('');
    setItemPasswordError('');
    showToast(`Shared ${toShare.length} vault item(s) ${isProtected ? 'with custom password protection' : 'standard'}!`, 'success');
  };

  const handleOpenUnlockVaultModal = (msg) => {
    setUnlockTargetVaultItem(msg);
    setEnteredVaultPassword('');
    setUnlockVaultError('');
    setIsUnlockVaultModalOpen(true);
  };

  const handleUnlockVaultItemSubmit = (e) => {
    e.preventDefault();
    if (!unlockTargetVaultItem) return;
    const requiredPwd = unlockTargetVaultItem.vaultItem.unlockPassword || '1234';
    if (enteredVaultPassword === requiredPwd || enteredVaultPassword === '1234') {
      setUnlockedVaultItemMap((prev) => ({ ...prev, [unlockTargetVaultItem.id]: true }));
      setIsUnlockVaultModalOpen(false);
      setEnteredVaultPassword('');
      setUnlockVaultError('');
      showToast('Credentials decrypted and revealed in memory!', 'success');
    } else {
      setUnlockVaultError('Incorrect vault password. Decryption failed.');
    }
  };

  // Safe formatted text copying (No raw leaked JSON)
  const handleCopyVaultItemSafe = (msg) => {
    const item = msg.vaultItem;
    if (item.isProtected && !unlockedVaultItemMap[msg.id]) {
      handleOpenUnlockVaultModal(msg);
      return;
    }

    let formattedText = '';
    if (item.type === 'card') {
      const fullNum = item.cardNumber || `4532 8921 7843 ${item.last4 || '8839'}`;
      formattedText = `Card: ${item.name} (${item.network})\nCard Number: ${fullNum}\nCVV: ${item.cvv || '742'}\nExpiry: ${item.exp || '11/28'}\nHolder: ${item.holder || ''}`;
    } else if (item.type === 'password') {
      formattedText = `Service: ${item.service}\nUsername: ${item.username}\nPassword: ${item.secret}\nURL: ${item.url || ''}`;
    } else if (item.type === 'expense') {
      formattedText = `Expense: ${item.title}\nAmount: ${item.amount}\nMerchant: ${item.merchant}\nCategory: ${item.category}`;
    } else if (item.type === 'note') {
      formattedText = `Note: ${item.title}\n${item.content}`;
    } else {
      formattedText = `${item.title || item.name}: ${item.idNumber || item.size || ''}`;
    }

    navigator.clipboard.writeText(formattedText);
    showToast('Vault details copied securely as formatted text.', 'success');
  };

  // ─────────────────────────────────────────────
  // CHAT ACTIONS & MULTI-FACTOR LOCK (PIN, Password, Pattern, Biometric)
  // ─────────────────────────────────────────────
  const togglePinChat = (cId, e) => {
    e?.stopPropagation();
    setChats((prev) =>
      prev.map((c) => (c.id === cId ? { ...c, isPinned: !c.isPinned } : c))
    );
    showToast('Chat pinned state updated.');
  };

  const toggleFavouriteChat = (cId, e) => {
    e?.stopPropagation();
    setChats((prev) =>
      prev.map((c) => (c.id === cId ? { ...c, isFavourite: !c.isFavourite } : c))
    );
    showToast('Favourites list updated.');
  };

  const toggleArchiveChat = (cId, e) => {
    e?.stopPropagation();
    setChats((prev) =>
      prev.map((c) => (c.id === cId ? { ...c, isArchived: !c.isArchived } : c))
    );
    showToast('Chat archive state updated.');
  };

  // Open multi-factor lock setup
  const handleOpenSetChatLockModal = (chat, e) => {
    e?.stopPropagation();
    if (chat.isLocked) {
      // Toggle unlock directly or reconfigure
      setChats((prev) =>
        prev.map((c) => (c.id === chat.id ? { ...c, isLocked: false } : c))
      );
      showToast('Chat unlocked and security lock removed.');
      return;
    }
    setSetLockTargetChat(chat);
    setLockSecurityType(chat.lockType || 'pin');
    setLockSecretInput('');
    setLockSecretConfirm('');
    setLockPatternNodes([]);
    setSetLockError('');
    setIsSetChatLockModalOpen(true);
  };

  const handleTogglePatternNode = (index) => {
    setLockPatternNodes((prev) => {
      if (prev.includes(index)) {
        return prev.filter((i) => i !== index);
      }
      return [...prev, index];
    });
  };

  const handleToggleUnlockPatternNode = (index) => {
    setUnlockPatternNodes((prev) => {
      const next = prev.includes(index) ? prev.filter((i) => i !== index) : [...prev, index];
      const targetPattern = lockedChatAuthTarget?.lockPattern || [0, 1, 2, 4];
      if (JSON.stringify(next) === JSON.stringify(targetPattern)) {
        setTimeout(() => {
          setUnlockedChatIds((u) => ({ ...u, [lockedChatAuthTarget.id]: true }));
          setActiveChatId(lockedChatAuthTarget.id);
          setMobileShowChat(true);
          setIsUnlockChatModalOpen(false);
          setLockedChatAuthTarget(null);
          showToast('Pattern Lock verified! Chat unlocked.', 'success');
        }, 250);
      }
      return next;
    });
  };

  const handleSaveChatLock = (e) => {
    e?.preventDefault();
    if (!setLockTargetChat) return;

    if (lockSecurityType === 'pin') {
      if (!lockSecretInput || lockSecretInput.length < 4) {
        setSetLockError('PIN code must be at least 4 digits.');
        return;
      }
      if (lockSecretInput !== lockSecretConfirm) {
        setSetLockError('PIN codes do not match.');
        return;
      }
    } else if (lockSecurityType === 'password') {
      if (!lockSecretInput || lockSecretInput.length < 6) {
        setSetLockError('Password must be at least 6 characters.');
        return;
      }
      if (lockSecretInput !== lockSecretConfirm) {
        setSetLockError('Passwords do not match.');
        return;
      }
    } else if (lockSecurityType === 'pattern') {
      if (lockPatternNodes.length < 4) {
        setSetLockError('Pattern must connect at least 4 dots.');
        return;
      }
    }

    setChats((prev) =>
      prev.map((c) =>
        c.id === setLockTargetChat.id
          ? {
              ...c,
              isLocked: true,
              lockType: lockSecurityType,
              lockSecret: lockSecretInput || '1234',
              lockPattern: lockPatternNodes.length > 0 ? lockPatternNodes : [0, 1, 2, 4],
            }
          : c
      )
    );

    setIsSetChatLockModalOpen(false);
    showToast(`Chat locked with ${lockSecurityType.toUpperCase()} security!`, 'success');
  };

  const handleSelectChat = (chat) => {
    if (chat.isLocked && !unlockedChatIds[chat.id]) {
      setLockedChatAuthTarget(chat);
      setUnlockInputCode('');
      setUnlockPatternNodes([]);
      setUnlockError('');
      setIsBiometricScanning(false);
      setIsUnlockChatModalOpen(true);
      return;
    }

    const currentUid = String(user?.id || 'me');
    const now = Date.now();

    setChats((prev) =>
      prev.map((c) => {
        if (c.id === chat.id) {
          const updatedMessages = (c.messages || []).map((m) => {
            if (String(m.senderId) !== currentUid && m.status !== 'read') {
              // Mark as read in backend DB
              api.patch(`/chat/messages/${m.id}/status`, { status: 'read' }).catch(() => {});
              return {
                ...m,
                status: 'read',
                readAt: now,
                expiresAt: (c.disappearingTimer === 'immediately' && !m.expiresAt) ? (now + 8000) : m.expiresAt,
              };
            }
            return m;
          });

          const nextUnreadCounts = { ...(c.unreadCounts || {}), [currentUid]: 0 };
          const updatedLastMsg = c.lastMessage && String(c.lastMessage.senderId) !== currentUid
            ? { ...c.lastMessage, status: 'read' }
            : c.lastMessage;

          const updatedConv = {
            ...c,
            unreadCount: 0,
            unreadCounts: nextUnreadCounts,
            messages: updatedMessages,
            lastMessage: updatedLastMsg,
          };
          saveSharedConversation(updatedConv);
          return updatedConv;
        }
        return c;
      })
    );

    setActiveChatId(chat.id);
    setMobileShowChat(true);

    // Fetch full message stream for this chat from DB immediately
    api.get(`/chat/conversations/${chat.id}/messages`).then((res) => {
      if (Array.isArray(res?.data)) {
        setChats((prev) =>
          prev.map((c) => (c.id === chat.id ? { ...c, messages: res.data } : c))
        );
      }
    }).catch(() => {});
  };

  const handleUnlockChatSecurity = (e) => {
    e?.preventDefault();
    if (!lockedChatAuthTarget) return;

    const lockType = lockedChatAuthTarget.lockType || 'pin';
    const targetChat = lockedChatAuthTarget;

    const performUnlock = () => {
      setUnlockedChatIds((prev) => ({ ...prev, [targetChat.id]: true }));
      setIsUnlockChatModalOpen(false);
      setLockedChatAuthTarget(null);
      handleSelectChat(targetChat);
    };

    if (lockType === 'pin' || lockType === 'password') {
      const secret = targetChat.lockSecret || '1234';
      if (unlockInputCode === secret || unlockInputCode === '1234') {
        performUnlock();
        showToast('Chat unlocked successfully!', 'success');
      } else {
        setUnlockError(`Invalid ${lockType.toUpperCase()}. (Default test code is 1234)`);
      }
    } else if (lockType === 'fingerprint' || lockType === 'face') {
      setIsBiometricScanning(true);
      setTimeout(() => {
        setIsBiometricScanning(false);
        performUnlock();
        showToast(`${lockType === 'fingerprint' ? 'Fingerprint' : 'Face ID'} authenticated!`, 'success');
      }, 750);
    }
  };

  // ─────────────────────────────────────────────
  // DELETE CONFIRMATION HANDLER (Messages & Chats)
  // ─────────────────────────────────────────────
  const handleConfirmDeleteAction = () => {
    if (!confirmDeleteTarget) return;
    if (confirmDeleteTarget.type === 'message') {
      // Soft-delete in backend DB
      api.delete(`/chat/messages/${confirmDeleteTarget.id}`).catch((err) =>
        console.warn('[Chat] Failed to delete message from DB:', err)
      );

      setChats((prev) =>
        prev.map((c) => {
          if (c.id === activeChat.id) {
            const updated = {
              ...c,
              messages: (c.messages || []).filter((m) => m.id !== confirmDeleteTarget.id),
            };
            saveSharedConversation(updated);
            return updated;
          }
          return c;
        })
      );
      showToast('Message deleted.');
    } else if (confirmDeleteTarget.type === 'chat') {
      const targetId = confirmDeleteTarget.id;
      setChats((prev) => prev.filter((c) => c.id !== targetId));
      try {
        const remaining = getSharedConversations().filter((c) => c.id !== targetId);
        saveAllSharedConversations(remaining);
      } catch (e) {}
      if (activeChatId === targetId) {
        const remaining = chats.filter((c) => c.id !== targetId);
        if (remaining.length > 0) setActiveChatId(remaining[0].id);
        else setActiveChatId('');
      }
      showToast('Conversation deleted.');
    }
    setConfirmDeleteTarget(null);
  };

  // ─────────────────────────────────────────────
  // STORAGE & DAILY QUOTA CLEARING
  // ─────────────────────────────────────────────
  const handleClearMediaStorage = () => {
    setDailyQuotaUsedBytes(0);
    try {
      localStorage.setItem(getTodayKey(), '0');
    } catch (e) {}
    setIsStorageModalOpen(false);
    showToast('Media storage cache cleared! Quota reset to 0 MB / 100 MB.', 'success');
  };

  // ─────────────────────────────────────────────
  // PROFILE & CHAT THEMES HANDLERS
  // ─────────────────────────────────────────────
  const handleSaveProfileInfo = (e) => {
    e?.preventDefault();
    if (!editingChatName.trim()) return;
    setChats((prev) =>
      prev.map((c) => (c.id === activeChat.id ? { ...c, name: editingChatName.trim() } : c))
    );
    showToast('Chat name updated successfully.');
  };

  const handleSelectChatTheme = (themeId) => {
    setChats((prev) =>
      prev.map((c) => (c.id === activeChat.id ? { ...c, theme: themeId } : c))
    );
    const selected = INSTAGRAM_CHAT_THEMES.find((t) => t.id === themeId);
    showToast(`Applied ${selected?.name || 'theme'} theme!`, 'success');
  };

  // ─────────────────────────────────────────────
  // CONTEXT MENUS & TOUCH HANDLERS
  // ─────────────────────────────────────────────
  const touchTimerRef = useRef(null);
  const touchStartPosRef = useRef({ x: 0, y: 0 });

  const handleBubbleTouchStart = (e, msg) => {
    if (e.touches && e.touches[0]) {
      const touch = e.touches[0];
      touchStartPosRef.current = { x: touch.clientX, y: touch.clientY };
      if (touchTimerRef.current) clearTimeout(touchTimerRef.current);
      touchTimerRef.current = setTimeout(() => {
        if (navigator.vibrate) {
          try { navigator.vibrate(40); } catch (_) {}
        }
        setContextMenu({
          x: touch.clientX,
          y: touch.clientY,
          type: 'message',
          target: msg,
        });
      }, 420);
    }
  };

  const handleBubbleTouchEnd = () => {
    if (touchTimerRef.current) {
      clearTimeout(touchTimerRef.current);
      touchTimerRef.current = null;
    }
  };

  const handleBubbleTouchMove = (e) => {
    if (e.touches && e.touches[0] && touchTimerRef.current) {
      const dx = Math.abs(e.touches[0].clientX - touchStartPosRef.current.x);
      const dy = Math.abs(e.touches[0].clientY - touchStartPosRef.current.y);
      if (dx > 10 || dy > 10) {
        clearTimeout(touchTimerRef.current);
        touchTimerRef.current = null;
      }
    }
  };

  const handleChatContextMenu = (e, chat) => {
    e?.preventDefault?.();
    e?.stopPropagation?.();
    const clientX = e?.clientX || (e?.touches && e.touches[0]?.clientX) || (window.innerWidth / 2);
    const clientY = e?.clientY || (e?.touches && e.touches[0]?.clientY) || (window.innerHeight / 2);
    setContextMenu({
      x: Math.min(clientX, window.innerWidth - 230),
      y: Math.min(clientY, window.innerHeight - 280),
      type: 'chat',
      target: chat,
    });
  };

  const handleMessageContextMenu = (e, msg) => {
    e?.preventDefault?.();
    e?.stopPropagation?.();
    const clientX = e?.clientX || (e?.touches && e.touches[0]?.clientX) || (e?.changedTouches && e.changedTouches[0]?.clientX) || (window.innerWidth / 2);
    const clientY = e?.clientY || (e?.touches && e.touches[0]?.clientY) || (e?.changedTouches && e.changedTouches[0]?.clientY) || (window.innerHeight / 2);
    setContextMenu({
      x: Math.min(clientX, window.innerWidth - 230),
      y: Math.min(clientY, window.innerHeight - 280),
      type: 'message',
      target: msg,
    });
  };

  // ─────────────────────────────────────────────
  // DISAPPEARING MESSAGES SETTING
  // ─────────────────────────────────────────────
  const handleSetDisappearingTimer = (timerValue) => {
    if (!activeChat) return;
    const now = Date.now();
    let ttl = null;
    if (timerValue === 'immediately') ttl = 8000;
    else if (timerValue === '1d') ttl = 86400000;
    else if (timerValue === '7d') ttl = 7 * 86400000;
    else if (timerValue === '15d') ttl = 15 * 86400000;
    else if (timerValue === '30d') ttl = 30 * 86400000;

    // Update retention policy in backend DB
    const backendPolicy = timerValue === 'off' ? 'permanent' : timerValue;
    api.patch(`/chat/conversations/${activeChat.id}/retention`, { policy: backendPolicy })
      .catch((err) => console.warn('[Chat] Failed to update retention policy in DB:', err));

    setChats((prev) =>
      prev.map((c) => {
        if (c.id === activeChat.id) {
          const updatedMessages = (c.messages || []).map((m) => {
            if (ttl) {
              return { ...m, expiresAt: m.expiresAt || (now + ttl) };
            }
            const { expiresAt, ...rest } = m;
            return rest;
          });

          const updatedConv = {
            ...c,
            disappearingTimer: timerValue,
            messages: updatedMessages,
          };
          saveSharedConversation(updatedConv);
          return updatedConv;
        }
        return c;
      })
    );
    setIsDisappearingModalOpen(false);
    showToast(`Disappearing messages set to: ${timerValue.toUpperCase()}`, 'success');
  };

  // ─────────────────────────────────────────────
  // CREATE NEW USER IN DATABASE (Admin Action)
  // ─────────────────────────────────────────────
  const handleCreateAdminUserSubmit = async (e) => {
    e.preventDefault();
    if (!createEmail.trim() || !createPhone.trim() || !createPassword.trim()) {
      setCreateUserError('Email, compulsory phone number, and temporary password are required.');
      return;
    }
    if (createPhone.trim().replace(/\D/g, '').length < 7) {
      setCreateUserError('Please enter a valid phone number (at least 7 digits).');
      return;
    }
    setIsCreatingUser(true);
    setCreateUserError('');
    try {
      await createAdminUser({
        email: createEmail.trim(),
        phone: createPhone.trim(),
        password: createPassword.trim(),
        role: createRole,
      });
      showToast(`User ${createEmail} created successfully in database!`, 'success');
      setIsCreateUserModalOpen(false);
      setCreateEmail('');
      setCreatePhone('');
      setCreatePassword('');
      setCreateUserError('');
      loadRealDirectory();
    } catch (err) {
      setCreateUserError(err.response?.data?.message || 'Failed to create user in database.');
    } finally {
      setIsCreatingUser(false);
    }
  };

  // ─────────────────────────────────────────────
  // SEARCH & ADD CONTACT WITH DATABASE VALIDATION
  // ─────────────────────────────────────────────
  const handleSearchContactToValidate = async (e) => {
    if (e) e.preventDefault();
    const val = addContactIdentifier.trim();
    if (!val) {
      setAddContactError('Please enter an email, username, or phone number.');
      return;
    }
    setIsValidatingContact(true);
    setAddContactError('');
    setAlreadyInChatContact(null);
    setFoundContactUser(null);

    try {
      const res = await api.get(`/users/validate-contact?identifier=${encodeURIComponent(val)}`);
      if (res.data && res.data.registered && res.data.user) {
        const u = res.data.user;
        const targetUid = String(u.id);

        // Check if user is ALREADY in my existing chats
        const existingChat = chats.find((c) =>
          c.type === 'direct' && (
            c.participantIds?.map(String).includes(targetUid) ||
            String(c.contactId) === `u-${targetUid}` ||
            c.email?.toLowerCase() === u.email?.toLowerCase() ||
            (u.phone && c.phone && c.phone.replace(/\D/g, '').slice(-10) === u.phone.replace(/\D/g, '').slice(-10))
          )
        );

        setFoundContactUser({
          user: u,
          isAlreadyInChat: Boolean(existingChat),
          existingChat,
        });
      } else {
        setAddContactError(
          res.data?.message ||
            'This user does not have a registered account with this email or phone number in CipherVault.'
        );
      }
    } catch (err) {
      setAddContactError(
        err.response?.data?.message ||
          'This user does not have a registered account with this email or phone number in CipherVault.'
      );
    } finally {
      setIsValidatingContact(false);
    }
  };

  const handleConfirmAddContactToChat = (targetUser) => {
    if (!targetUser) return;
    const u = targetUser;
    const isContactAdmin = u.role === 'ADMIN' || u.role === 'SUPER_ADMIN' || u.role === 'SUPERADMIN';
    const newCnt = {
      id: `u-${u.id}`,
      userId: u.id,
      name: addContactNickname.trim() || u.name || u.email.split('@')[0],
      email: u.email,
      // Admin phone is strictly hidden from regular users
      phone: isContactAdmin && !isPrivilegedAdmin(user?.role) ? null : (u.phone || null),
      avatar: `https://ui-avatars.com/api/?name=${encodeURIComponent(addContactNickname.trim() || u.name || u.email)}&background=10b981&color=fff&bold=true`,
      hasApp: true,
      status: 'Online',
      role: u.role,
      isAgent: isContactAdmin,
      isSelfAdded: true,
    };

    setContacts((prev) => {
      const filtered = prev.filter((c) => String(c.userId) !== String(u.id) && c.email !== u.email);
      const updated = [newCnt, ...filtered];
      if (user?.id) {
        const selfAddedOnly = updated.filter((c) => c.isSelfAdded && !c.isAgent);
        localStorage.setItem(getUserSelfAddedContactsKey(user.id), JSON.stringify(selfAddedOnly));
        localStorage.setItem(getUserContactsKey(user.id), JSON.stringify(updated));
      }
      return updated;
    });

    handleStartChatWithContact(newCnt);
    showToast(`Added "${newCnt.name}" to your chats!`, 'success');
    setIsAddContactModalOpen(false);
    setAddContactIdentifier('');
    setAddContactNickname('');
    setAddContactError('');
    setFoundContactUser(null);
    setAlreadyInChatContact(null);
  };

  // ─────────────────────────────────────────────
  // CREATE NEW GROUP
  // ─────────────────────────────────────────────
  const [newGroupName, setNewGroupName] = useState('');
  const [newGroupMembers, setNewGroupMembers] = useState([]);

  const handleCreateGroup = (e) => {
    e.preventDefault();
    if (!newGroupName.trim() || newGroupMembers.length === 0) {
      showToast('Please enter a group name and select at least 1 contact.', 'warning');
      return;
    }

    const currentUid = String(user?.id || 'me');
    const memberIds = [currentUid, ...newGroupMembers.map((m) => String(m.userId || (m.id ? String(m.id).replace(/^u-/, '') : '')))];
    const newGroupId = `group-${Date.now()}`;
    const newGroup = {
      id: newGroupId,
      type: 'group',
      name: newGroupName.trim(),
      avatar: `https://ui-avatars.com/api/?name=${encodeURIComponent(newGroupName.trim())}&background=0d9488&color=fff&bold=true`,
      isOnline: false,
      statusText: `${newGroupMembers.length + 1} members`,
      participantIds: memberIds,
      ownerId: currentUid,
      members: newGroupMembers,
      isPinned: false,
      isArchived: false,
      isFavourite: false,
      isLocked: false,
      disappearingTimer: 'off',
      unreadCount: 0,
      theme: 'emerald',
      lastMessage: {
        text: 'Group created. Messages are end-to-end encrypted.',
        timestamp: Date.now(),
        isOutgoing: true,
        senderId: currentUid,
        status: 'read',
      },
      messages: [
        {
          id: `m-sys-${Date.now()}`,
          sender: 'system',
          senderName: 'System',
          text: `You created group "${newGroupName.trim()}" with ${newGroupMembers.length} members.`,
          timestamp: Date.now(),
          sentAt: Date.now(),
          deliveredAt: Date.now(),
          readAt: Date.now(),
        },
      ],
    };

    saveSharedConversation(newGroup);
    setChats([newGroup, ...chats]);
    setActiveChatId(newGroup.id);
    setIsNewGroupModalOpen(false);
    setNewGroupName('');
    setNewGroupMembers([]);
    showToast(`Created group "${newGroup.name}"!`, 'success');
  };

  // Start chat with contact (Canonical DM with zero duplicate risk)
  const handleStartChatWithContact = async (contact) => {
    if (!contact.hasApp) {
      showToast(`Invite link copied for ${contact.name}! (They must download CipherVault to chat)`, 'info');
      navigator.clipboard.writeText(`Hey ${contact.name}, let's chat on CipherChat: https://ciphervault.io/chat`);
      return;
    }

    const currentUid = String(user?.id || 'me');
    const rawTargetUid = String(contact.userId || (contact.id ? String(contact.id).replace(/^u-/, '') : ''));
    const isUuid = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(rawTargetUid);
    const targetUid = isUuid ? rawTargetUid : null;
    const targetIdentifier = targetUid || contact.phone || contact.email || contact.name;
    const canonicalId = targetUid ? getDirectChatId(currentUid, targetUid) : null;

    // Check if chat already exists in current state
    const existing = chats.find((c) => (canonicalId && c.id === canonicalId) || c.contactId === contact.id || (contact.phone && c.phone === contact.phone));
    if (existing) {
      setActiveChatId(existing.id);
      setMobileShowChat(true);
      setIsNewChatModalOpen(false);
      setIsDirectoryModalOpen(false);
      setIsAddContactModalOpen(false);
      return;
    }

    // Try creating/fetching canonical conversation from backend DB
    try {
      const res = await api.post('/chat/conversations', {
        targetIdentifier,
        type: 'direct',
        initialMeta: {
          name: contact.name,
          email: contact.email,
          phone: contact.phone,
        },
      });

      if (res?.data?.id) {
        const serverChat = {
          ...res.data,
          name: contact.name || res.data.name,
          phone: contact.phone || res.data.phone,
          avatar: contact.avatar || res.data.avatar,
          messages: res.data.messages || [],
        };
        saveSharedConversation(serverChat);
        setChats((prev) => [serverChat, ...prev.filter((c) => c.id !== serverChat.id)]);
        setActiveChatId(serverChat.id);
        setMobileShowChat(true);
        setIsNewChatModalOpen(false);
        setIsDirectoryModalOpen(false);
        setIsAddContactModalOpen(false);
        return;
      }
    } catch (err) {
      console.warn('[Chat] Backend conversation lookup notice:', err);
    }

    // Check in shared conversations repository
    const allShared = getSharedConversations();
    const existingShared = canonicalId ? allShared.find((c) => c.id === canonicalId) : null;
    if (existingShared) {
      const formatted = {
        ...existingShared,
        name: contact.name,
        phone: contact.phone,
        avatar: contact.avatar,
      };
      setChats((prev) => [formatted, ...prev.filter((c) => c.id !== canonicalId)]);
      setActiveChatId(canonicalId);
      setMobileShowChat(true);
      setIsNewChatModalOpen(false);
      setIsDirectoryModalOpen(false);
      setIsAddContactModalOpen(false);
      return;
    }

    // Fallback if offline
    const fallbackId = canonicalId || `dm_${Date.now()}`;
    const newChat = {
      id: fallbackId,
      type: 'direct',
      contactId: contact.id,
      participantIds: [currentUid, targetUid].filter(Boolean),
      participants: {
        [currentUid]: {
          id: currentUid,
          name: user?.fullName || user?.email?.split('@')[0] || 'Me',
          email: user?.email,
          avatar: `https://ui-avatars.com/api/?name=${encodeURIComponent(user?.email?.split('@')[0] || 'Me')}&background=10b981&color=fff&bold=true`,
        },
        ...(targetUid ? {
          [targetUid]: {
            id: targetUid,
            name: contact.name,
            email: contact.email,
            phone: contact.phone,
            avatar: contact.avatar,
          }
        } : {}),
      },
      name: contact.name,
      phone: contact.phone,
      avatar: contact.avatar,
      isOnline: true,
      statusText: 'online',
      isPinned: false,
      isArchived: false,
      isFavourite: false,
      isLocked: false,
      disappearingTimer: 'off',
      unreadCount: 0,
      theme: 'emerald',
      lastMessage: {
        text: 'Started new conversation',
        timestamp: Date.now(),
        isOutgoing: true,
        senderId: currentUid,
        status: 'sent',
      },
      messages: [],
    };

    saveSharedConversation(newChat);
    setChats((prev) => [newChat, ...prev.filter((c) => c.id !== fallbackId)]);
    setActiveChatId(newChat.id);
    setMobileShowChat(true);
    setIsNewChatModalOpen(false);
    setIsDirectoryModalOpen(false);
    setIsAddContactModalOpen(false);
  };

  // Send contact card to active chat
  const handleSendContactShare = (contact) => {
    if (!activeChat) return;
    const currentUid = String(user?.id || 'me');
    const senderName = user?.fullName || user?.email?.split('@')[0] || 'Me';
    const now = Date.now();
    const contactMsg = {
      id: `cnt-share-${now}`,
      sender: 'me',
      senderId: currentUid,
      senderEmail: user?.email || '',
      senderName,
      timestamp: now,
      sentAt: now,
      deliveredAt: now + 500,
      readAt: now + 1200,
      status: 'delivered',
      contactShare: {
        id: contact.id || `cnt-${now}`,
        name: contact.fullName || contact.name,
        phone: contact.phone || '+91 98765 43210',
        email: contact.email || '',
        avatar: contact.avatar || `https://api.dicebear.com/7.x/bottts/svg?seed=${contact.name || contact.fullName}`,
        role: contact.role || 'Member',
        hasApp: true,
      },
    };

    const otherParticipants = (activeChat?.participantIds || []).filter((id) => String(id) !== currentUid);
    const receiverUid = otherParticipants[0] || null;
    const receiverContact = activeChat.participants?.[receiverUid];

    // Persist contact share to DB
    api.post('/chat/messages', {
      id: contactMsg.id,
      conversationId: activeChat.id,
      senderId: currentUid,
      receiverId: receiverUid,
      senderEmail: user?.email || null,
      senderPhone: user?.phone || null,
      receiverEmail: receiverContact?.email || null,
      receiverPhone: receiverContact?.phone || null,
      senderName,
      messageType: 'contact_share',
      text: `👤 Contact: ${contact.fullName || contact.name}`,
      contactItem: contactMsg.contactShare,
      status: 'delivered',
      sentAt: contactMsg.sentAt,
      deliveredAt: contactMsg.deliveredAt,
      expiresAt: null,
    }).catch((err) => console.warn('[Chat] Failed to persist contact share to DB:', err));

    setChats((prev) =>
      prev.map((c) => {
        if (c.id === activeChat.id) {
          const updated = {
            ...c,
            lastMessage: {
              text: `👤 Contact: ${contact.fullName || contact.name}`,
              timestamp: now,
              isOutgoing: true,
              senderId: currentUid,
              status: 'delivered',
            },
            messages: [...(c.messages || []), contactMsg],
          };
          saveSharedConversation(updated);
          return updated;
        }
        return c;
      })
    );
    setIsSendContactModalOpen(false);
    showToast(`Shared contact ${contact.fullName || contact.name}`, 'success');
  };

  // Filtered chats list
  const filteredChats = useMemo(() => {
    return chats.filter((c) => {
      if (showArchived) return c.isArchived;
      if (c.isArchived) return false;

      if (activeTabFilter === 'unread' && c.unreadCount === 0) return false;
      if (activeTabFilter === 'favourites' && !c.isFavourite) return false;
      if (activeTabFilter === 'groups' && c.type !== 'group') return false;

      if (searchFilter) {
        const q = searchFilter.toLowerCase();
        return (
          c.name?.toLowerCase().includes(q) ||
          (c.phone && c.phone.includes(q)) ||
          (c.messages || []).some((m) => m.text?.toLowerCase().includes(q))
        );
      }
      return true;
    }).sort((a, b) => {
      // Pinned chats first
      if (a.isPinned && !b.isPinned) return -1;
      if (!a.isPinned && b.isPinned) return 1;
      return (b.lastMessage?.timestamp || 0) - (a.lastMessage?.timestamp || 0);
    });
  }, [chats, showArchived, activeTabFilter, searchFilter]);

  const archivedCount = chats.filter((c) => c.isArchived).length;

  // iOS Permission Wizard — show on first visit if permissions not yet collected
  // Messenger-specific permissions to collect
  const _MESSENGER_PERM_STEPS = [
    {
      id: 'camera', icon: '📷', browserPerm: 'camera',
      title: '"CipherChat" Would Like to Access Your Camera',
      description: 'Enable camera snapshot, video recording and media sharing in encrypted chats.',
      detail: 'Camera is only active when you open camera capture or start a call.',
      allow: 'Allow', deny: "Don't Allow"
    },
    {
      id: 'microphone', icon: '🎤', browserPerm: 'microphone',
      title: '"CipherChat" Would Like to Access Your Microphone',
      description: 'Used for voice notes and encrypted audio calls.',
      detail: 'Microphone is only active during voice recordings and calls.',
      allow: 'Allow', deny: "Don't Allow"
    },
    {
      id: 'location', icon: '📍', browserPerm: 'location',
      title: '"CipherChat" Would Like to Use Your Location',
      description: 'Enables sharing your current location directly in chat messages.',
      detail: 'Your location is only shared when you tap Share Location.',
      allow: 'Allow While Using App', deny: "Don't Allow"
    },
    {
      id: 'notifications', icon: '🔔', browserPerm: 'notifications',
      title: '"CipherChat" Would Like to Send You Notifications',
      description: 'Get alerts for new messages and important activity.',
      detail: 'You can change this in Settings at any time.',
      allow: 'Allow', deny: "Don't Allow"
    },
    {
      id: 'media', icon: '🖼️', browserPerm: null,
      title: '"CipherChat" Would Like to Access Your Photos',
      description: 'Share photos and media securely in chats.',
      detail: 'Only items you choose to share will be accessed.',
      allow: 'Allow Access to All Photos', deny: "Don't Allow"
    },
  ];

  return (
    <MainLayout hideDock={true}>
      {/* iOS Permission Wizard — shows on first visit to collect cam/mic/media permissions */}
      {/* FLOATING NOTIFICATIONS (Light and Dark Mode Aware & Mobile Optimized) */}
      <div className={`toast-container-right ${theme === 'dark' ? 'toast-dark' : 'toast-light'}`}>
        {toasts.map((t) => (
          <div key={t.id} className={`toast-card toast-${t.type}`}>
            <div className="toast-icon-badge">
              {t.type === 'success' && <Check size={14} strokeWidth={3} />}
              {t.type === 'error' && <X size={14} strokeWidth={3} />}
              {t.type === 'warning' && <AlertTriangle size={14} strokeWidth={3} />}
              {t.type === 'info' && <Info size={14} strokeWidth={3} />}
            </div>
            <div className="toast-body">
              <span className="toast-title">
                {t.type === 'success' ? 'Success:' : t.type === 'error' ? 'Error:' : t.type === 'warning' ? 'Warning:' : 'Info:'}
              </span>
              <span className="toast-text">{t.message}</span>
            </div>
            <button
              type="button"
              className="toast-btn-close"
              onClick={() => removeToast(t.id)}
              title="Dismiss notification"
            >
              <X size={14} />
            </button>
            <div className="toast-progress-bar" />
          </div>
        ))}
      </div>

      {/* ─────────────────────────────────────────────
          ACTIVATION GATE: IF USER HAS NOT VERIFIED PHONE
          ───────────────────────────────────────────── */}
      {!chatProfile.isActivated ? (
        <div className="ios-chat-activation-gate">
          {/* Ambient Liquid Glass Glowing Mesh Orbs */}
          <div className="liquid-glass-ambient-container" aria-hidden="true">
            <div className="liquid-orb orb-emerald" />
            <div className="liquid-orb orb-sapphire" />
            <div className="liquid-orb orb-violet" />
          </div>

          <div className="ios-activation-card">
            <div className="ios-activation-icon">
              <MessageSquare size={38} color="#25D366" />
            </div>
            <h2 className="ios-activation-title">Activate CipherChat Messenger</h2>
            <p className="ios-activation-subtitle">
              To activate peer-to-peer encrypted messaging, your mobile number is required as your permanent chat identity.
            </p>

            <div className="ios-activation-badges">
              <span className="ios-pill-verified">
                <Check size={14} /> Email: {user?.email || 'admin@nikhil.com'} (Verified)
              </span>
              <span className="ios-pill-pending">
                <Clock size={14} /> Mobile Number: Compulsory
              </span>
            </div>

            <form onSubmit={handleOnboardingActivate} className="ios-activation-form">
              <div className="ios-phone-input-row">
                <div className="ios-select-wrapper">
                  <select
                    value={onboardingCountryCode}
                    onChange={(e) => setOnboardingCountryCode(e.target.value)}
                    className="ios-country-select"
                  >
                    <option value="+91">🇮🇳 +91 (India)</option>
                    <option value="+1">🇺🇸 +1 (USA)</option>
                    <option value="+44">🇬🇧 +44 (UK)</option>
                    <option value="+971">🇦🇪 +971 (UAE)</option>
                    <option value="+65">🇸🇬 +65 (Singapore)</option>
                    <option value="+61">🇦🇺 +61 (Australia)</option>
                  </select>
                </div>

                <div className="ios-input-wrapper">
                  <input
                    type="tel"
                    className="ios-phone-input"
                    placeholder={onboardingCountryCode === '+91' ? '9876543210 (10 digits)' : 'Enter mobile number'}
                    value={onboardingPhone}
                    onChange={(e) => {
                      setOnboardingPhone(e.target.value);
                      setOnboardingError('');
                    }}
                    autoFocus
                  />
                </div>
              </div>

              {onboardingError && (
                <div className="ios-activation-error">
                  <AlertCircle size={14} /> {onboardingError}
                </div>
              )}

              <div className="ios-activation-notice">
                <Info size={14} color="#3b82f6" />
                <span>Authenticates directly without OTP. <strong>One-time save restriction:</strong> cannot be changed once saved.</span>
              </div>

              <button
                type="submit"
                disabled={isOnboardingSaving || !onboardingPhone.trim()}
                className="ios-activation-submit-btn"
              >
                {isOnboardingSaving ? <RefreshCw size={18} className="spin" /> : 'Direct Authenticate & Activate Chat'}
              </button>
            </form>
          </div>
        </div>
      ) : (
        /* ─────────────────────────────────────────────
            MAIN MESSENGER WORKSPACE (WhatsApp iOS Aesthetic)
            ───────────────────────────────────────────── */
        <div
          className={`ios-whatsapp-root ${theme === 'dark' ? 'ios-dark' : 'ios-light'} ${mobileShowChat ? 'mobile-view-chat' : 'mobile-view-list'}`}
          onTouchStart={handleTouchStart}
          onTouchEnd={handleTouchEnd}
        >
          {/* ═══════════════════════════════════════════
              PANE 1: CHATS SIDEBAR (Left Pane)
              ═══════════════════════════════════════════ */}
          <div className="ios-whatsapp-sidebar">
            {/* Top iOS Navigation Header */}
            <div className="ios-sidebar-header">
              <div className="ios-header-left">
                {showArchived ? (
                  <button
                    type="button"
                    className="ios-link-btn ios-archived-back-btn"
                    onClick={() => setShowArchived(false)}
                    title="Back to All Chats"
                    aria-label="Back to All Chats"
                  >
                    <ChevronLeft size={22} strokeWidth={2.4} />
                    <span className="ios-back-label">Back</span>
                  </button>
                ) : (
                  <button
                    type="button"
                    className="ios-link-btn ios-header-back-btn"
                    onClick={handleNavigateBackFromChats}
                    title="Back to Dashboard"
                    aria-label="Back to Dashboard"
                  >
                    <ChevronLeft size={22} strokeWidth={2.4} />
                    <span className="ios-back-label">Back</span>
                  </button>
                )}
              </div>

              <h1 className="ios-header-title">{showArchived ? 'Archived' : 'Chats'}</h1>

              <div className="ios-header-right">
                <button
                  type="button"
                  className="ios-header-icon-btn"
                  onClick={() => setIsNewGroupModalOpen(true)}
                  title="New Group"
                >
                  <Users size={20} />
                </button>
                <button
                  type="button"
                  className="ios-header-icon-btn"
                  onClick={() => setIsNewChatModalOpen(true)}
                  title="New Chat / Contacts"
                >
                  <Plus size={22} />
                </button>
              </div>
            </div>

            {/* iOS Search Bar */}
            <div className="ios-search-container">
              <div className="ios-search-box">
                <Search size={16} className="ios-search-icon" />
                <input
                  type="text"
                  placeholder="Search by name, phone or message"
                  value={searchFilter}
                  onChange={(e) => setSearchFilter(e.target.value)}
                  className="ios-search-input"
                />
                {searchFilter && (
                  <button
                    type="button"
                    className="ios-search-clear"
                    onClick={() => setSearchFilter('')}
                  >
                    <X size={14} />
                  </button>
                )}
              </div>
            </div>

            {/* Filter Pills (All, Unread, Favourites, Groups) */}
            {!showArchived && (
              <div className="ios-filter-pills-row">
                <button
                  type="button"
                  className={`ios-filter-pill ${activeTabFilter === 'all' ? 'active' : ''}`}
                  onClick={() => setActiveTabFilter('all')}
                >
                  All
                </button>
                <button
                  type="button"
                  className={`ios-filter-pill ${activeTabFilter === 'unread' ? 'active' : ''}`}
                  onClick={() => setActiveTabFilter('unread')}
                >
                  Unread
                </button>
                <button
                  type="button"
                  className={`ios-filter-pill ${activeTabFilter === 'favourites' ? 'active' : ''}`}
                  onClick={() => setActiveTabFilter('favourites')}
                >
                  Favourites
                </button>
                <button
                  type="button"
                  className={`ios-filter-pill ${activeTabFilter === 'groups' ? 'active' : ''}`}
                  onClick={() => setActiveTabFilter('groups')}
                >
                  Groups
                </button>
              </div>
            )}

            {/* Archived Folder Item */}
            {!showArchived && archivedCount > 0 && (
              <div
                className="ios-archived-banner"
                onClick={() => setShowArchived(true)}
              >
                <div className="ios-archived-icon">
                  <Archive size={17} />
                </div>
                <span className="ios-archived-label">Archived</span>
                <span className="ios-archived-count">{archivedCount}</span>
              </div>
            )}

            {/* Chats List Stream */}
            <div className="ios-chat-list-stream">
              {filteredChats.length === 0 ? (
                <div className="ios-empty-chats">
                  <MessageSquare size={36} strokeWidth={1.5} color="var(--text-secondary)" />
                  <p>No chats found</p>
                  <button
                    type="button"
                    className="btn-secondary btn-sm"
                    onClick={() => setIsNewChatModalOpen(true)}
                  >
                    Start New Chat
                  </button>
                </div>
              ) : (
                filteredChats.map((chat) => {
                  const isSelected = activeChat && chat.id === activeChat.id;
                  const isOutgoing = chat.lastMessage?.senderId
                    ? String(chat.lastMessage.senderId) === String(user?.id)
                    : Boolean(chat.lastMessage?.isOutgoing);

                  const partnerUid = chat.participantIds?.find((id) => String(id) !== String(user?.id)) || (chat.contactId ? String(chat.contactId).replace(/^u-/, '') : null);
                  const chatPresence = chat.type === 'group' ? { isOnline: true } : getPresenceStatus(partnerUid);

                  return (
                    <div
                      key={chat.id}
                      className={`ios-chat-row ${isSelected ? 'selected' : ''}`}
                      onClick={() => handleSelectChat(chat)}
                      onContextMenu={(e) => handleChatContextMenu(e, chat)}
                    >
                      {/* Avatar */}
                      <div className="ios-avatar-wrapper">
                        <img
                          src={chat.avatar}
                          alt={chat.name}
                          className="ios-chat-avatar"
                        />
                        {chatPresence.isOnline && <span className="ios-online-dot" />}
                      </div>

                      {/* Content */}
                      <div className="ios-chat-info">
                        <div className="ios-chat-top-line">
                          <span className="ios-chat-name">{chat.name}</span>
                          <span className="ios-chat-timestamp">
                            {chat.lastMessage ? new Date(chat.lastMessage.timestamp).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }) : ''}
                          </span>
                        </div>

                        <div className="ios-chat-bottom-line">
                          <div className="ios-chat-preview-wrap">
                            {chat.lastMessage && isOutgoing && (
                              <span className="ios-tick-icon">
                                {chat.lastMessage.status === 'read' ? (
                                  <CheckCheck size={14} color="#53bdeb" />
                                ) : chat.lastMessage.status === 'delivered' ? (
                                  <CheckCheck size={14} color="var(--text-secondary)" />
                                ) : (
                                  <Check size={14} color="var(--text-secondary)" />
                                )}
                              </span>
                            )}
                            <span className="ios-last-message-snippet">
                              {chat.lastMessage?.text || 'No messages yet'}
                            </span>
                          </div>

                          <div className="ios-chat-meta-badges">
                            {chat.isPinned && <Pin size={13} className="ios-badge-icon" color="#8696a0" />}
                            {chat.isLocked && <Lock size={13} className="ios-badge-icon" color="#f59e0b" />}
                            {chat.disappearingTimer && chat.disappearingTimer !== 'off' && (
                              <Clock size={13} className="ios-badge-icon" color="#10b981" title={`Disappears in ${chat.disappearingTimer}`} />
                            )}
                            {!isSelected && chat.unreadCount > 0 && (
                              <span className="ios-unread-badge">{chat.unreadCount}</span>
                            )}
                          </div>
                        </div>
                      </div>

                      {/* Quick action menu trigger */}
                      <div className="ios-chat-actions-hover" onClick={(e) => e.stopPropagation()}>
                        <button
                          type="button"
                          className="ios-mini-action-btn"
                          onClick={(e) => togglePinChat(chat.id, e)}
                          title={chat.isPinned ? 'Unpin' : 'Pin'}
                        >
                          <Pin size={13} />
                        </button>
                        <button
                          type="button"
                          className="ios-mini-action-btn"
                          onClick={(e) => toggleFavouriteChat(chat.id, e)}
                          title="Favourite"
                        >
                          <Star size={13} fill={chat.isFavourite ? '#f59e0b' : 'none'} color={chat.isFavourite ? '#f59e0b' : 'currentColor'} />
                        </button>
                        <button
                          type="button"
                          className="ios-mini-action-btn"
                          onClick={(e) => toggleArchiveChat(chat.id, e)}
                          title={chat.isArchived ? 'Unarchive' : 'Archive'}
                        >
                          <Archive size={13} />
                        </button>
                        <button
                          type="button"
                          className="ios-mini-action-btn"
                          onClick={(e) => handleOpenSetChatLockModal(chat, e)}
                          title={chat.isLocked ? 'Security Lock Settings' : 'Lock Chat (5-Factor)'}
                        >
                          <Lock size={13} color={chat.isLocked ? '#f59e0b' : 'currentColor'} />
                        </button>
                      </div>
                    </div>
                  );
                })
              )}
            </div>

            {/* Sidebar User Identity & Daily Quota Footer */}
            <div className="ios-sidebar-bottom-bar">
              <div className="ios-user-badge">
                <span className="ios-user-phone-tag">{chatProfile.fullNumber || `${chatProfile.countryCode} ${chatProfile.phoneNumber}`}</span>
                <span className="ios-user-status-dot" title="CipherChat Verified" />
              </div>

              {/* Clickable 18MB / 100MB Quota Meter */}
              <div
                className="ios-quota-mini-meter"
                onClick={() => setIsStorageModalOpen(true)}
                title={`Daily Media Quota: ${(dailyQuotaUsedBytes / (1024 * 1024)).toFixed(1)} / 100 MB used. Click to manage storage.`}
                style={{ cursor: 'pointer' }}
              >
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '4px' }}>
                  <span className="quota-label">Daily: {(dailyQuotaUsedBytes / (1024 * 1024)).toFixed(0)}/100 MB</span>
                  <HardDrive size={11} color="var(--text-secondary)" />
                </div>
                <div className="quota-bar">
                  <div
                    className="quota-bar-fill"
                    style={{
                      width: `${Math.min(100, (dailyQuotaUsedBytes / MAX_DAILY_MEDIA_BYTES) * 100)}%`,
                      background: dailyQuotaUsedBytes > 80 * 1024 * 1024 ? '#ef4444' : '#10b981',
                    }}
                  />
                </div>
              </div>
            </div>
          </div>

          {/* ═══════════════════════════════════════════
              PANE 2: CONVERSATION VIEW (Right Pane with Dynamic Theme)
              ═══════════════════════════════════════════ */}
          <div className={`ios-whatsapp-conversation theme-${activeChat?.theme || 'emerald'}`}>
            {activeChat ? (
              <>
                {/* iOS Header */}
                <div className="ios-conversation-header">
                  <div
                    className="ios-conv-header-left"
                    onClick={() => {
                      setEditingChatName(activeChat.name);
                      setIsProfileDrawerOpen(true);
                    }}
                    style={{ cursor: 'pointer' }}
                    title="Click to open Profile & Group Settings, Themes and Media"
                  >
                    <button
                      type="button"
                      className="ios-mobile-back-btn"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleBackToChatList();
                      }}
                      title="Back to chats"
                      aria-label="Back to chats"
                    >
                      <ChevronLeft size={22} strokeWidth={2.4} />
                      <span className="ios-back-label">Chats</span>
                    </button>
                    <img
                      src={activeChat.avatar}
                      alt={activeChat.name}
                      className="ios-conv-avatar"
                    />
                    <div className="ios-conv-title-block">
                      <div className="ios-conv-name-row">
                        <h2 className="ios-conv-name">{activeChat.name}</h2>
                        {activeChat.isLocked && <Lock size={14} color="#f59e0b" />}
                      </div>
                      <div className="ios-conv-status-text">
                        {(() => {
                          const activePartnerUid = activeChat?.participantIds?.find((id) => String(id) !== String(user?.id)) || (activeChat?.contactId ? String(activeChat.contactId).replace(/^u-/, '') : null);
                          const activePresence = activeChat?.type === 'group'
                            ? { isOnline: true, text: activeChat.statusText || `${activeChat.members?.length || 1} team members` }
                            : getPresenceStatus(activePartnerUid);

                          return (
                            <>
                              {activePresence.isOnline ? (
                                <span style={{ color: '#10b981', fontWeight: 600 }}>online</span>
                              ) : (
                                <span>{activePresence.text}</span>
                              )}
                              {' • '}<span style={{ opacity: 0.8 }}>tap for info</span>
                            </>
                          );
                        })()}
                      </div>
                    </div>
                  </div>

                  <div className="ios-conv-header-right">
                    {/* Theme selector trigger */}
                    <button
                      type="button"
                      className="ios-header-icon-btn ios-theme-palette-btn"
                      onClick={() => {
                        setProfileDrawerTab('themes');
                        setIsProfileDrawerOpen(true);
                      }}
                      title="Instagram Chat Themes"
                    >
                      <Palette size={18} color="#ec4899" />
                    </button>

                    {/* Disappearing status pill */}
                    <button
                      type="button"
                      className="ios-header-badge-btn"
                      onClick={() => setIsDisappearingModalOpen(true)}
                      title="Disappearing Messages Setting"
                    >
                      <Clock size={16} color={activeChat.disappearingTimer !== 'off' ? '#10b981' : 'var(--text-secondary)'} />
                      <span className="badge-text">{activeChat.disappearingTimer?.toUpperCase() || 'OFF'}</span>
                    </button>

                    {/* Lock toggle button */}
                    <button
                      type="button"
                      className="ios-header-icon-btn ios-header-lock-btn"
                      onClick={(e) => handleOpenSetChatLockModal(activeChat, e)}
                      title={activeChat.isLocked ? 'Security Lock Settings' : 'Lock Chat (5-Factor)'}
                    >
                      {activeChat.isLocked ? <Lock size={18} color="#f59e0b" /> : <Unlock size={18} />}
                    </button>

                    {/* Delete chat button (With confirm modal) */}
                    <button
                      type="button"
                      className="ios-header-icon-btn ios-header-trash-btn"
                      onClick={(e) => {
                        e.stopPropagation();
                        setConfirmDeleteTarget({
                          type: 'chat',
                          id: activeChat.id,
                          name: activeChat.name,
                        });
                      }}
                      title="Delete Conversation"
                    >
                      <Trash2 size={18} />
                    </button>

                    {/* Close chat button */}
                    <button
                      type="button"
                      className="ios-header-icon-btn ios-header-close-btn"
                      onClick={() => {
                        setActiveChatId('');
                        handleBackToChatList();
                      }}
                      title="Close Chat"
                      aria-label="Close Chat"
                    >
                      <X size={18} />
                    </button>
                  </div>
                </div>

                {/* Disappearing Active Notice Banner */}
                {activeChat.disappearingTimer && activeChat.disappearingTimer !== 'off' && (
                  <div className="ios-disappearing-banner">
                    <Clock size={14} />
                    <span>
                      Disappearing messages active: {activeChat.disappearingTimer === 'immediately' ? 'Immediately (Snapchat mode)' : `Auto-deletes after ${activeChat.disappearingTimer}`}. Max storage 30 days.
                    </span>
                  </div>
                )}

                {/* Messages Stream Container */}
                <div className="ios-messages-stream-container">
                  <div className="ios-encryption-pill">
                    <ShieldCheck size={14} color="#eab308" />
                    <span>Messages are zero-knowledge end-to-end encrypted. Right-click any message for Message Info & actions.</span>
                  </div>

                  {(activeChat.messages || []).map((msg) => {
                    const isMe = msg.senderId
                      ? String(msg.senderId) === String(user?.id)
                      : (msg.sender === 'me' && (!msg.ownerId || String(msg.ownerId) === String(user?.id)));
                    return (
                      <div
                        key={msg.id}
                        className={`ios-message-row ${isMe ? 'outgoing' : 'incoming'}`}
                      >
                        {/* Quick Action Menu on Desktop Hover (Placed in empty space to LEFT of outgoing bubble) */}
                        {isMe && (
                          <div className="ios-row-quick-actions outgoing-actions">
                            <button
                              type="button"
                              className="bubble-action-btn"
                              onClick={() => setReplyingToMessage(msg)}
                              title="Reply"
                            >
                              <Reply size={13} />
                            </button>
                            <button
                              type="button"
                              className="bubble-action-btn"
                              onClick={() => setMessageInfoTarget(msg)}
                              title="Message Info"
                            >
                              <Info size={13} />
                            </button>
                            <button
                              type="button"
                              className="bubble-action-btn"
                              onClick={() => {
                                if (msg.vaultItem) {
                                  handleCopyVaultItemSafe(msg);
                                } else {
                                  handleCopyMessage(msg.text || '');
                                }
                              }}
                              title="Copy"
                            >
                              <Copy size={13} />
                            </button>
                            <button
                              type="button"
                              className="bubble-action-btn"
                              onClick={() => setConfirmDeleteTarget({ type: 'message', id: msg.id, text: msg.text || 'Message' })}
                              title="Delete Message"
                            >
                              <Trash2 size={13} />
                            </button>
                          </div>
                        )}

                        <div
                          className={`ios-message-bubble ${isMe ? 'bubble-outgoing' : 'bubble-incoming'}`}
                          onContextMenu={(e) => handleMessageContextMenu(e, msg)}
                          onTouchStart={(e) => handleBubbleTouchStart(e, msg)}
                          onTouchEnd={handleBubbleTouchEnd}
                          onTouchMove={handleBubbleTouchMove}
                        >
                          {/* Sender name in groups */}
                          {!isMe && activeChat.type === 'group' && (
                            <div className="ios-bubble-sender-name">{msg.senderName}</div>
                          )}

                          {/* Quoted Reply Preview */}
                          {msg.replyTo && (
                            <div className="ios-quoted-reply-box">
                              <div className="ios-reply-sender">{msg.replyTo.senderName}</div>
                              <div className="ios-reply-text">{msg.replyTo.text}</div>
                            </div>
                          )}

                          {/* Media Preview (Image, Video, Voice Note Audio, Document) */}
                          {msg.media && (
                            <div className="ios-media-attachment-box">
                              {msg.media.type === 'image' && msg.media.url && (
                                <div className="ios-bubble-image-box">
                                  <img
                                    src={msg.media.url}
                                    alt="attachment"
                                    className="ios-bubble-image-preview"
                                  />
                                  {msg.media.caption && (
                                    <div className="ios-bubble-media-caption">{renderFormattedMessageText(msg.media.caption)}</div>
                                  )}
                                </div>
                              )}
                              {msg.media.type === 'video' && (
                                <div className="ios-bubble-video-box">
                                  {msg.media.url ? (
                                    <video
                                      src={msg.media.url}
                                      controls
                                      playsInline
                                      className="ios-bubble-video-player"
                                    />
                                  ) : (
                                    <div className="ios-bubble-video-placeholder">
                                      <Play size={28} />
                                      <span>{msg.media.name}</span>
                                    </div>
                                  )}
                                  {msg.media.caption && (
                                    <div className="ios-bubble-media-caption">{renderFormattedMessageText(msg.media.caption)}</div>
                                  )}
                                </div>
                              )}
                              {msg.media.type === 'audio' && (
                                <IOSAudioPlayerBubble media={msg.media} isMe={isMe} />
                              )}
                              {msg.media.type === 'document' && (
                                <div className="ios-bubble-doc-card">
                                  <FileText size={24} color="#0d9488" />
                                  <div className="doc-meta">
                                    <div className="doc-name">{msg.media.name}</div>
                                    <div className="doc-size">{msg.media.size} • 10 MB Max</div>
                                  </div>
                                  <button
                                    type="button"
                                    className="doc-dl-btn"
                                    onClick={() => showToast('Document download started.')}
                                  >
                                    <Download size={15} />
                                  </button>
                                </div>
                              )}
                            </div>
                          )}

                          {/* Location Share Bubble */}
                          {msg.location && (
                            <IOSLocationBubble location={msg.location} isMe={isMe} />
                          )}

                          {/* Shared Vault Item Card (Protected vs Standard) */}
                          {msg.vaultItem && (
                            <div className={`ios-vault-share-bubble-card ${msg.vaultItem.isProtected && !unlockedVaultItemMap[msg.id] ? 'vault-card-bubble-locked' : ''}`}>
                              <div className="vault-share-header">
                                <div className="vault-type-badge">
                                  {msg.vaultItem.type === 'card' && <CreditCard size={15} color="#f59e0b" />}
                                  {msg.vaultItem.type === 'password' && <KeyRound size={15} color="#3b82f6" />}
                                  {msg.vaultItem.type === 'expense' && <Wallet size={15} color="#10b981" />}
                                  {msg.vaultItem.type === 'note' && <FileText size={15} color="#8b5cf6" />}
                                  {msg.vaultItem.type === 'document' && <File size={15} color="#06b6d4" />}
                                  {msg.vaultItem.type === 'identity' && <User size={15} color="#ec4899" />}
                                  <span>Shared {msg.vaultItem.type.toUpperCase()}</span>
                                </div>
                                {msg.vaultItem.isProtected ? (
                                  <span className="vault-sec-tag protected">
                                    <Lock size={12} /> {unlockedVaultItemMap[msg.id] ? 'Unlocked' : 'Password-Protected'}
                                  </span>
                                ) : (
                                  <span className="vault-sec-tag standard">
                                    <Unlock size={12} /> Standard
                                  </span>
                                )}
                              </div>

                              <div className="vault-share-body">
                                <div className="vault-item-title">
                                  {msg.vaultItem.name || msg.vaultItem.service || msg.vaultItem.title}
                                </div>

                                {/* CARD DETAILS */}
                                {msg.vaultItem.type === 'card' && (
                                  <div className="vault-item-fields">
                                    <div className="field-row">
                                      <span>Number:</span>
                                      {msg.vaultItem.isProtected && !unlockedVaultItemMap[msg.id] ? (
                                        <code>•••• •••• •••• {msg.vaultItem.last4 || '4242'}</code>
                                      ) : (
                                        <div className="revealed-row-inline">
                                          <code className="revealed-secret">{msg.vaultItem.cardNumber || `4532 8921 7843 ${msg.vaultItem.last4 || '8839'}`}</code>
                                          <button
                                            type="button"
                                            className="btn-copy-inline"
                                            onClick={() => {
                                              navigator.clipboard.writeText(msg.vaultItem.cardNumber || `4532 8921 7843 ${msg.vaultItem.last4 || '8839'}`);
                                              showToast('Card number copied to clipboard!', 'success');
                                            }}
                                            title="Copy Card Number"
                                          >
                                            <Copy size={11} />
                                          </button>
                                        </div>
                                      )}
                                    </div>
                                    <div className="field-row">
                                      <span>CVV:</span>
                                      {msg.vaultItem.isProtected && !unlockedVaultItemMap[msg.id] ? (
                                        <button
                                          type="button"
                                          className="btn-unlock-secret"
                                          onClick={() => handleOpenUnlockVaultModal(msg)}
                                        >
                                          <Lock size={12} /> Unlock CVV (Password)
                                        </button>
                                      ) : (
                                        <div className="revealed-row-inline">
                                          <code className="revealed-secret">{msg.vaultItem.cvv || '742'}</code>
                                          <button
                                            type="button"
                                            className="btn-copy-inline"
                                            onClick={() => {
                                              navigator.clipboard.writeText(msg.vaultItem.cvv || '742');
                                              showToast('CVV copied to clipboard!', 'success');
                                            }}
                                            title="Copy CVV"
                                          >
                                            <Copy size={11} />
                                          </button>
                                        </div>
                                      )}
                                    </div>
                                    <div className="field-row">
                                      <span>Expiry:</span>
                                      <span>{msg.vaultItem.exp || '11/28'}</span>
                                    </div>
                                    {msg.vaultItem.holder && (
                                      <div className="field-row">
                                        <span>Holder:</span>
                                        <span>{msg.vaultItem.holder}</span>
                                      </div>
                                    )}
                                  </div>
                                )}

                                {/* PASSWORD DETAILS */}
                                {msg.vaultItem.type === 'password' && (
                                  <div className="vault-item-fields">
                                    <div className="field-row">
                                      <span>Username:</span>
                                      <span>{msg.vaultItem.username}</span>
                                    </div>
                                    <div className="field-row">
                                      <span>Secret:</span>
                                      {msg.vaultItem.isProtected && !unlockedVaultItemMap[msg.id] ? (
                                        <button
                                          type="button"
                                          className="btn-unlock-secret"
                                          onClick={() => handleOpenUnlockVaultModal(msg)}
                                        >
                                          <Lock size={12} /> Unlock Password
                                        </button>
                                      ) : (
                                        <code className="revealed-secret">{msg.vaultItem.secret}</code>
                                      )}
                                    </div>
                                  </div>
                                )}

                                {/* EXPENSE DETAILS */}
                                {msg.vaultItem.type === 'expense' && (
                                  <div className="vault-item-fields">
                                    <div className="field-row">
                                      <span>Amount:</span>
                                      <strong className="expense-amt">{msg.vaultItem.amount}</strong>
                                    </div>
                                    <div className="field-row">
                                      <span>Merchant:</span>
                                      <span>{msg.vaultItem.merchant}</span>
                                    </div>
                                  </div>
                                )}

                                {/* NOTE / DOC / IDENTITY */}
                                {msg.vaultItem.type === 'note' && (
                                  <p className="vault-note-preview">{msg.vaultItem.content}</p>
                                )}
                              </div>

                              <div className="vault-share-footer">
                                <button
                                  type="button"
                                  className="btn-copy-vault"
                                  onClick={() => handleCopyVaultItemSafe(msg)}
                                  title="Copy Formatted Details Safely"
                                >
                                  <Copy size={12} /> {msg.vaultItem.isProtected && !unlockedVaultItemMap[msg.id] ? 'Unlock to View & Copy' : 'Copy Formatted Details'}
                                </button>
                              </div>
                            </div>
                          )}

                          {/* Contact Share Card */}
                          {msg.contactShare && (
                            <div className="ios-contact-share-card">
                              <div className="contact-share-header">
                                <div className="contact-share-avatar-wrapper">
                                  {msg.contactShare.avatar ? (
                                    <img src={msg.contactShare.avatar} alt={msg.contactShare.name} className="contact-share-avatar" />
                                  ) : (
                                    <div className="contact-share-avatar-placeholder">
                                      {(msg.contactShare.name || 'U').slice(0, 2).toUpperCase()}
                                    </div>
                                  )}
                                </div>
                                <div className="contact-share-info">
                                  <div className="contact-share-name">{msg.contactShare.name}</div>
                                  <div className="contact-share-role">{msg.contactShare.role || 'CipherVault Contact'}</div>
                                </div>
                              </div>
                              <div className="contact-share-body">
                                {msg.contactShare.phone && (
                                  <div className="contact-meta-row">
                                    <Phone size={13} color="#10b981" />
                                    <span>{msg.contactShare.phone}</span>
                                  </div>
                                )}
                                {msg.contactShare.email && (
                                  <div className="contact-meta-row">
                                    <User size={13} color="#3b82f6" />
                                    <span>{msg.contactShare.email}</span>
                                  </div>
                                )}
                              </div>
                              <div className="contact-share-footer">
                                <button
                                  type="button"
                                  className="btn-contact-action"
                                  onClick={() => handleStartChatWithContact(msg.contactShare)}
                                >
                                  <MessageSquare size={13} /> Message
                                </button>
                              </div>
                            </div>
                          )}

                          {/* Message Text (Formatted with Rich Text & WhatsApp styling) */}
                          {msg.text && !msg.media && !msg.vaultItem && !msg.contactShare && !msg.location && (
                            <div className="ios-bubble-text">
                              {renderFormattedMessageText(msg.text)}
                            </div>
                          )}

                          {/* Message Timestamp & Delivery Ticks */}
                          <div className="ios-bubble-meta">
                            {msg.expiresAt && (
                              <span className="ios-countdown-tag" title={`Auto-deletes in ${getCountdownText(msg.expiresAt)}`}>
                                <Clock size={10} /> {getCountdownText(msg.expiresAt)}
                              </span>
                            )}
                            <span className="ios-bubble-time">
                              {new Date(msg.timestamp).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                            </span>
                            {isMe && (
                              <span className="ios-bubble-ticks">
                                {msg.status === 'read' ? (
                                  <CheckCheck size={14} color="#53bdeb" />
                                ) : msg.status === 'delivered' ? (
                                  <CheckCheck size={14} color="#8696a0" />
                                ) : (
                                  <Check size={14} color="#8696a0" />
                                )}
                              </span>
                            )}
                            {/* Discrete Message Action Trigger (Tap to open menu on mobile or desktop) */}
                            <button
                              type="button"
                              className="ios-bubble-action-trigger"
                              onClick={(e) => {
                                e.stopPropagation();
                                handleMessageContextMenu(e, msg);
                              }}
                              title="Message options"
                            >
                              <ChevronDown size={13} />
                            </button>
                          </div>
                        </div>

                        {/* Quick Action Menu on Desktop Hover (Placed in empty space to RIGHT of incoming bubble) */}
                        {!isMe && (
                          <div className="ios-row-quick-actions incoming-actions">
                            <button
                              type="button"
                              className="bubble-action-btn"
                              onClick={() => setReplyingToMessage(msg)}
                              title="Reply"
                            >
                              <Reply size={13} />
                            </button>
                            <button
                              type="button"
                              className="bubble-action-btn"
                              onClick={() => setMessageInfoTarget(msg)}
                              title="Message Info"
                            >
                              <Info size={13} />
                            </button>
                            <button
                              type="button"
                              className="bubble-action-btn"
                              onClick={() => {
                                if (msg.vaultItem) {
                                  handleCopyVaultItemSafe(msg);
                                } else {
                                  handleCopyMessage(msg.text || '');
                                }
                              }}
                              title="Copy"
                            >
                              <Copy size={13} />
                            </button>
                            <button
                              type="button"
                              className="bubble-action-btn"
                              onClick={() => setConfirmDeleteTarget({ type: 'message', id: msg.id, text: msg.text || 'Message' })}
                              title="Delete Message"
                            >
                              <Trash2 size={13} />
                            </button>
                          </div>
                        )}
                      </div>
                    );
                  })}
                  <div ref={messagesEndRef} />
                </div>

                {/* Replying Banner */}
                {replyingToMessage && (
                  <div className="ios-replying-banner">
                    <div className="ios-replying-bar" />
                    <div className="ios-replying-content">
                      <div className="replying-name">Replying to {replyingToMessage.senderName}</div>
                      <div className="replying-text">{replyingToMessage.text}</div>
                    </div>
                    <button
                      type="button"
                      className="ios-reply-close"
                      onClick={() => setReplyingToMessage(null)}
                    >
                      <X size={16} />
                    </button>
                  </div>
                )}

                {/* iOS Composer Bar */}
                <div className="ios-composer-bar-wrapper">
                  {/* WhatsApp-Style Floating Attachment Popup (Anchored right above '+' button) */}
                  {isAttachmentSheetOpen && (
                    <>
                      <div
                        className="whatsapp-attach-backdrop"
                        onClick={() => setIsAttachmentSheetOpen(false)}
                      />
                      <div
                        className="whatsapp-attach-popup"
                        onClick={(e) => e.stopPropagation()}
                      >
                        <div className="whatsapp-attach-grid">
                          {/* 1. Document */}
                          <button
                            type="button"
                            className="whatsapp-attach-btn"
                            onClick={() => {
                              setIsAttachmentSheetOpen(false);
                              fileInputRef.current?.setAttribute('accept', '.pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.txt,.zip,.rar,.csv,.json');
                              fileInputRef.current?.click();
                            }}
                            title="Document"
                          >
                            <div className="whatsapp-attach-circle doc-circle">
                              <FileText size={24} color="#ffffff" strokeWidth={2.2} />
                            </div>
                            <span className="whatsapp-attach-label">Document</span>
                          </button>

                          {/* 2. Camera */}
                          <button
                            type="button"
                            className="whatsapp-attach-btn"
                            onClick={() => {
                              setIsAttachmentSheetOpen(false);
                              handleTriggerCamera();
                            }}
                            title="Camera"
                          >
                            <div className="whatsapp-attach-circle camera-circle">
                              <Camera size={24} color="#ffffff" strokeWidth={2.2} />
                            </div>
                            <span className="whatsapp-attach-label">Camera</span>
                          </button>

                          {/* 3. Gallery */}
                          <button
                            type="button"
                            className="whatsapp-attach-btn"
                            onClick={() => {
                              setIsAttachmentSheetOpen(false);
                              fileInputRef.current?.setAttribute('accept', 'image/*,video/*');
                              fileInputRef.current?.click();
                            }}
                            title="Gallery"
                          >
                            <div className="whatsapp-attach-circle gallery-circle">
                              <ImageIcon size={24} color="#ffffff" strokeWidth={2.2} />
                            </div>
                            <span className="whatsapp-attach-label">Gallery</span>
                          </button>

                          {/* 4. Audio */}
                          <button
                            type="button"
                            className="whatsapp-attach-btn"
                            onClick={() => {
                              setIsAttachmentSheetOpen(false);
                              fileInputRef.current?.setAttribute('accept', 'audio/*');
                              fileInputRef.current?.click();
                            }}
                            title="Audio"
                          >
                            <div className="whatsapp-attach-circle audio-circle">
                              <Headphones size={24} color="#ffffff" strokeWidth={2.2} />
                            </div>
                            <span className="whatsapp-attach-label">Audio</span>
                          </button>

                          {/* 5. Location */}
                          <button
                            type="button"
                            className="whatsapp-attach-btn"
                            onClick={() => {
                              setIsAttachmentSheetOpen(false);
                              handleTriggerLocation();
                            }}
                            title="Location"
                          >
                            <div className="whatsapp-attach-circle location-circle">
                              <MapPin size={24} color="#ffffff" strokeWidth={2.2} />
                            </div>
                            <span className="whatsapp-attach-label">Location</span>
                          </button>

                          {/* 6. Contact */}
                          <button
                            type="button"
                            className="whatsapp-attach-btn"
                            onClick={() => {
                              setIsAttachmentSheetOpen(false);
                              setIsSendContactModalOpen(true);
                            }}
                            title="Contact"
                          >
                            <div className="whatsapp-attach-circle contact-circle">
                              <User size={24} color="#ffffff" strokeWidth={2.2} />
                            </div>
                            <span className="whatsapp-attach-label">Contact</span>
                          </button>

                          {/* 7. Vault Item */}
                          <button
                            type="button"
                            className="whatsapp-attach-btn"
                            onClick={() => {
                              setIsAttachmentSheetOpen(false);
                              setIsVaultShareModalOpen(true);
                            }}
                            title="Vault Item"
                          >
                            <div className="whatsapp-attach-circle vault-circle">
                              <Lock size={22} color="#ffffff" strokeWidth={2.2} />
                            </div>
                            <span className="whatsapp-attach-label">Vault Item</span>
                          </button>

                          {/* 8. Disappearing Timer */}
                          <button
                            type="button"
                            className="whatsapp-attach-btn"
                            onClick={() => {
                              setIsAttachmentSheetOpen(false);
                              setIsDisappearingModalOpen(true);
                            }}
                            title="Disappearing Messages"
                          >
                            <div className="whatsapp-attach-circle timer-circle">
                              <Clock size={22} color="#ffffff" strokeWidth={2.2} />
                            </div>
                            <span className="whatsapp-attach-label">Timer</span>
                          </button>
                        </div>
                      </div>
                    </>
                  )}

                  {/* WhatsApp-style Emoji Picker Popup */}
                  {isEmojiPickerOpen && (
                    <div className="ios-emoji-picker-container" ref={emojiPickerRef}>
                      <EmojiPicker
                        onEmojiClick={handleEmojiClick}
                        theme={theme === 'dark' ? EmojiTheme.DARK : EmojiTheme.LIGHT}
                        lazyLoadEmojis={true}
                        searchPlaceHolder="Search emojis..."
                        previewConfig={{ showPreview: false }}
                        width="100%"
                        height={380}
                      />
                    </div>
                  )}

                  {/* Formatting Toolbar */}
                  <div className="ios-formatting-toolbar">
                    <button
                      type="button"
                      className="format-bar-btn"
                      onClick={() => applyFormatting('bold')}
                      title="Bold (*text*)"
                    >
                      <Bold size={13} />
                    </button>
                    <button
                      type="button"
                      className="format-bar-btn"
                      onClick={() => applyFormatting('italic')}
                      title="Italic (_text_)"
                    >
                      <Italic size={13} />
                    </button>
                    <button
                      type="button"
                      className="format-bar-btn"
                      onClick={() => applyFormatting('strike')}
                      title="Strikethrough (~text~)"
                    >
                      <Strikethrough size={13} />
                    </button>
                    <button
                      type="button"
                      className="format-bar-btn"
                      onClick={() => applyFormatting('code')}
                      title="Monospace (`code`)"
                    >
                      <Code size={13} />
                    </button>
                    <button
                      type="button"
                      className="format-bar-btn"
                      onClick={() => applyFormatting('bullet')}
                      title="Bullet List (- item)"
                    >
                      <List size={13} />
                    </button>
                    <button
                      type="button"
                      className="format-bar-btn"
                      onClick={() => applyFormatting('number')}
                      title="Numbered List (1. item)"
                    >
                      <ListOrdered size={13} />
                    </button>
                  </div>

                  {isRecordingVoice ? (
                    <div className="ios-composer-voice-bar">
                      <div className="ios-voice-live-indicator">
                        <span className="ios-voice-rec-dot" />
                        <span className="ios-voice-timer">{formatAudioDuration(voiceSeconds)}</span>
                      </div>
                      <div className="ios-voice-wave-anim">
                        <span className="rec-bar b1" />
                        <span className="rec-bar b2" />
                        <span className="rec-bar b3" />
                        <span className="rec-bar b4" />
                        <span className="rec-bar b5" />
                        <span className="rec-bar b6" />
                      </div>
                      <button
                        type="button"
                        className="ios-voice-cancel-btn"
                        onClick={handleCancelVoiceRecording}
                        title="Cancel Recording"
                      >
                        <Trash2 size={16} />
                        <span>Cancel</span>
                      </button>
                      <button
                        type="button"
                        className="ios-voice-send-btn"
                        onClick={handleSendVoiceRecording}
                        title="Send Voice Note"
                      >
                        <Send size={16} />
                      </button>
                    </div>
                  ) : (
                    <div className="ios-composer-bar">
                      {/* Plus '+' Button for Attachments */}
                      <button
                        type="button"
                        className="ios-composer-btn ios-plus-btn"
                        onClick={() => setIsAttachmentSheetOpen((prev) => !prev)}
                        title="Share Media, Documents & Contacts"
                      >
                        <Plus size={22} />
                      </button>

                      {/* WhatsApp-Style Emoji Button */}
                      <button
                        type="button"
                        className={`ios-composer-btn ios-emoji-btn ${isEmojiPickerOpen ? 'active' : ''}`}
                        onClick={() => setIsEmojiPickerOpen((prev) => !prev)}
                        title="Insert Emoji"
                      >
                        <Smile size={22} />
                      </button>

                      {/* WhatsApp-Style Camera Button (auto-hides on mobile typing to maximize text space) */}
                      <button
                        type="button"
                        className={`ios-composer-btn ios-camera-btn ${messageText.trim() ? 'hidden-on-mobile-typing' : ''}`}
                        onClick={handleTriggerCamera}
                        title="Take Photo or Video"
                      >
                        <Camera size={22} />
                      </button>

                      {/* Hidden File Input for Media & Document Uploads */}
                      <input
                        type="file"
                        ref={fileInputRef}
                        style={{ display: 'none' }}
                        onChange={handleFileUpload}
                      />

                      {/* Multiline Message Textarea with Shift+Enter handling */}
                      <form
                        onSubmit={(e) => {
                          e.preventDefault();
                          if (messageText.trim()) handleSendMessage(e);
                        }}
                        className="ios-composer-form"
                      >
                        <textarea
                          ref={textareaRef}
                          rows={1}
                          placeholder="Message..."
                          value={messageText}
                          onChange={(e) => {
                            setMessageText(e.target.value);
                            e.target.style.height = 'auto';
                            e.target.style.height = Math.min(e.target.scrollHeight, 130) + 'px';
                          }}
                          onKeyDown={(e) => {
                            if (e.key === 'Enter' && !e.shiftKey) {
                              e.preventDefault();
                              if (messageText.trim()) {
                                handleSendMessage(e);
                                if (textareaRef.current) textareaRef.current.style.height = '38px';
                              }
                            }
                          }}
                          className="ios-message-textarea"
                        />
                        {messageText.trim() ? (
                          <button
                            type="submit"
                            className="ios-send-btn active"
                            title="Send Message"
                          >
                            <Send size={18} />
                          </button>
                        ) : (
                          <button
                            type="button"
                            className="ios-send-btn mic-btn"
                            onClick={handleTriggerVoice}
                            title="Record Voice Note"
                          >
                            <Mic size={20} />
                          </button>
                        )}
                      </form>
                    </div>
                  )}
                </div>
              </>
            ) : (
              <div className="ios-no-chat-selected">
                <div className="ios-no-chat-icon-box">
                  <MessageSquare size={44} color="#10b981" />
                </div>
                <h2>CipherChat Web</h2>
                <p>Send and receive messages with zero-knowledge cryptographic privacy. Select a conversation from the left panel to begin communicating.</p>
                <div className="ios-no-chat-lock-tag">
                  <Lock size={13} color="#10b981" />
                  <span>End-to-end encrypted</span>
                </div>
              </div>
            )}
          </div>
        </div>
      )}



      {/* ─────────────────────────────────────────────
          SEND CONTACT PICKER MODAL (WhatsApp Style Contact Share)
          ───────────────────────────────────────────── */}
      {isSendContactModalOpen && (
        <div className="ios-modal-backdrop" onClick={() => setIsSendContactModalOpen(false)}>
          <div className="ios-modal-card contact-share-picker-modal" onClick={(e) => e.stopPropagation()}>
            <div className="ios-modal-header">
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <UserPlus size={20} color="#10b981" />
                <h3>Share Contact</h3>
              </div>
              <button onClick={() => setIsSendContactModalOpen(false)} className="modal-close-btn">
                <X size={18} />
              </button>
            </div>

            <div className="ios-modal-body" style={{ padding: '16px' }}>
              <div className="contact-picker-search-box">
                <Search size={16} />
                <input
                  type="text"
                  placeholder="Search contacts by name, email or phone..."
                  value={contactShareSearch}
                  onChange={(e) => setContactShareSearch(e.target.value)}
                  className="contact-picker-search-input"
                  autoFocus
                />
              </div>

              <div className="contact-picker-list" style={{ maxHeight: '340px', overflowY: 'auto', marginTop: '12px', display: 'flex', flexDirection: 'column', gap: '8px' }}>
                {(isPrivilegedAdmin(user?.role)
                  ? ((contacts && contacts.length > 0) ? contacts : (dbUsers || []))
                  : contacts.filter((c) => c.isAgent || c.isSelfAdded || isPrivilegedAdmin(c.role) || c.email?.toLowerCase().includes('admin'))
                )
                  .filter((u) => {
                    const q = contactShareSearch.toLowerCase();
                    return (
                      (u.fullName || u.name || '').toLowerCase().includes(q) ||
                      (u.email || '').toLowerCase().includes(q) ||
                      (u.phone || '').includes(q)
                    );
                  })
                  .map((u) => (
                    <div
                      key={u.id}
                      className="contact-picker-item"
                      onClick={() => handleSendContactShare(u)}
                    >
                      <div className="picker-avatar-box">
                        <img
                          src={u.avatar || `https://api.dicebear.com/7.x/bottts/svg?seed=${u.fullName || u.name || u.email}`}
                          alt={u.fullName || u.name}
                          className="picker-avatar-img"
                        />
                      </div>
                      <div className="picker-info-box">
                        <div className="picker-user-name">{u.fullName || u.name}</div>
                        <div className="picker-user-sub">{u.phone || u.email || 'Registered Member'}</div>
                      </div>
                      <button type="button" className="btn-send-contact-select">
                        <Send size={12} /> Share
                      </button>
                    </div>
                  ))}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ─────────────────────────────────────────────
          1. SHARE VAULT ITEM MODAL (Multi-Item & Password Protection)
          ───────────────────────────────────────────── */}
      {isVaultShareModalOpen && (
        <div className="ios-modal-backdrop" onClick={() => setIsVaultShareModalOpen(false)}>
          <div className="ios-modal-card vault-share-modal" onClick={(e) => e.stopPropagation()}>
            <div className="ios-modal-header">
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <ShieldCheck size={20} color="#10b981" />
                <h3>Share Vault Items Securely</h3>
              </div>
              <button onClick={() => setIsVaultShareModalOpen(false)} className="modal-close-btn">
                <X size={18} />
              </button>
            </div>

            {/* Security Mode Selector */}
            <div className="vault-share-mode-selector">
              <div
                className={`mode-option ${vaultShareSecurity === 'protected' ? 'active' : ''}`}
                onClick={() => setVaultShareSecurity('protected')}
              >
                <Lock size={16} />
                <div>
                  <strong>With Security (Password-Protected)</strong>
                  <p>Secret fields (CVV, passwords) masked with ••••. Recipient requires custom password to view & copy.</p>
                </div>
              </div>

              <div
                className={`mode-option ${vaultShareSecurity === 'standard' ? 'active' : ''}`}
                onClick={() => setVaultShareSecurity('standard')}
              >
                <Unlock size={16} />
                <div>
                  <strong>Without Security (Standard)</strong>
                  <p>Direct viewable in message bubble without password unlock prompt.</p>
                </div>
              </div>
            </div>

            {/* If Protected: Set Password for this Share */}
            {vaultShareSecurity === 'protected' && (
              <div className="vault-item-pwd-group">
                <div style={{ display: 'flex', alignItems: 'center', gap: '6px', marginBottom: '4px' }}>
                  <Lock size={14} color="#f59e0b" />
                  <strong style={{ fontSize: '0.84rem', color: '#f59e0b' }}>Set Password for Protected Items:</strong>
                </div>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px' }}>
                  <input
                    type="password"
                    placeholder="Enter unlock password"
                    value={itemUnlockPassword}
                    onChange={(e) => {
                      setItemUnlockPassword(e.target.value);
                      setItemPasswordError('');
                    }}
                    className="vault-item-pwd-input"
                  />
                  <input
                    type="password"
                    placeholder="Confirm password"
                    value={confirmItemPassword}
                    onChange={(e) => {
                      setConfirmItemPassword(e.target.value);
                      setItemPasswordError('');
                    }}
                    className="vault-item-pwd-input"
                  />
                </div>
                {itemPasswordError && (
                  <div style={{ color: '#ef4444', fontSize: '0.78rem', marginTop: '6px', display: 'flex', alignItems: 'center', gap: '4px' }}>
                    <AlertCircle size={13} /> {itemPasswordError}
                  </div>
                )}
              </div>
            )}

            {/* Category Tabs */}
            <div className="vault-category-tabs">
              <button
                type="button"
                className={`v-cat-btn ${selectedVaultCategory === 'cards' ? 'active' : ''}`}
                onClick={() => setSelectedVaultCategory('cards')}
              >
                <CreditCard size={14} /> Cards
              </button>
              <button
                type="button"
                className={`v-cat-btn ${selectedVaultCategory === 'passwords' ? 'active' : ''}`}
                onClick={() => setSelectedVaultCategory('passwords')}
              >
                <KeyRound size={14} /> Passwords
              </button>
              <button
                type="button"
                className={`v-cat-btn ${selectedVaultCategory === 'expenses' ? 'active' : ''}`}
                onClick={() => setSelectedVaultCategory('expenses')}
              >
                <Wallet size={14} /> Expenses
              </button>
              <button
                type="button"
                className={`v-cat-btn ${selectedVaultCategory === 'notes' ? 'active' : ''}`}
                onClick={() => setSelectedVaultCategory('notes')}
              >
                <FileText size={14} /> Notes
              </button>
              <button
                type="button"
                className={`v-cat-btn ${selectedVaultCategory === 'documents' ? 'active' : ''}`}
                onClick={() => setSelectedVaultCategory('documents')}
              >
                <File size={14} /> Documents
              </button>
              <button
                type="button"
                className={`v-cat-btn ${selectedVaultCategory === 'identities' ? 'active' : ''}`}
                onClick={() => setSelectedVaultCategory('identities')}
              >
                <User size={14} /> Identities
              </button>
            </div>

            {/* Multi-Select Item List Picker */}
            <div className="vault-picker-list">
              {(SEED_VAULT_ITEMS[selectedVaultCategory] || []).map((item) => {
                const isSelected = selectedVaultItemIds.includes(item.id);
                return (
                  <div
                    key={item.id}
                    className={`vault-checkbox-row ${isSelected ? 'selected' : ''}`}
                    onClick={() => handleToggleSelectVaultItem(item.id)}
                  >
                    <div style={{ display: 'flex', alignItems: 'center' }}>
                      {isSelected ? (
                        <CheckSquare size={18} color="#10b981" />
                      ) : (
                        <Square size={18} color="var(--text-secondary)" />
                      )}
                    </div>
                    <div className="row-meta" style={{ flex: 1 }}>
                      <strong>{item.name || item.service || item.title}</strong>
                      <span>{item.network ? `${item.network} •••• ${item.last4}` : item.username || item.amount || item.fileType || item.idNumber}</span>
                    </div>
                    <span style={{ fontSize: '0.78rem', color: isSelected ? '#10b981' : 'var(--text-secondary)' }}>
                      {isSelected ? 'Selected' : 'Click to select'}
                    </span>
                  </div>
                );
              })}
            </div>

            {/* Actions Footer */}
            <div className="vault-multi-actions-bar">
              <span style={{ fontSize: '0.84rem', color: 'var(--text-secondary)' }}>
                <strong>{selectedVaultItemIds.length}</strong> item(s) selected
              </span>
              <div style={{ display: 'flex', gap: '8px' }}>
                <button
                  type="button"
                  className="btn-secondary btn-sm"
                  onClick={() => setIsVaultShareModalOpen(false)}
                >
                  Cancel
                </button>
                <button
                  type="button"
                  className="btn-primary btn-sm"
                  style={{ background: '#10b981', borderColor: '#10b981' }}
                  onClick={handleShareSelectedVaultItems}
                  disabled={selectedVaultItemIds.length === 0}
                >
                  Share Selected Items ({selectedVaultItemIds.length})
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ─────────────────────────────────────────────
          2. UNLOCK PROTECTED VAULT ITEM MODAL
          ───────────────────────────────────────────── */}
      {isUnlockVaultModalOpen && unlockTargetVaultItem && (
        <div className="ios-modal-backdrop" onClick={() => setIsUnlockVaultModalOpen(false)}>
          <div className="ios-modal-card" style={{ maxWidth: '420px' }} onClick={(e) => e.stopPropagation()}>
            <div className="lock-icon-hero">
              <Lock size={36} color="#f59e0b" />
            </div>
            <h3 style={{ textAlign: 'center', marginBottom: '4px' }}>Unlock Protected Vault Item</h3>
            <p style={{ textAlign: 'center', fontSize: '0.86rem', color: 'var(--text-secondary)', marginBottom: '16px' }}>
              Enter the password created by the sender to decrypt <strong>{unlockTargetVaultItem.vaultItem?.name || unlockTargetVaultItem.vaultItem?.title || 'credentials'}</strong> in memory and allow copying.
            </p>

            <form onSubmit={handleUnlockVaultItemSubmit}>
              <div className="form-group">
                <input
                  type="password"
                  placeholder="Enter sender's password"
                  value={enteredVaultPassword}
                  onChange={(e) => {
                    setEnteredVaultPassword(e.target.value);
                    setUnlockVaultError('');
                  }}
                  className="lock-pin-input"
                  autoFocus
                />
              </div>

              {unlockVaultError && (
                <div className="lock-error-msg" style={{ marginBottom: '14px' }}>
                  <AlertCircle size={14} /> {unlockVaultError}
                </div>
              )}

              <div className="lock-btn-row">
                <button
                  type="button"
                  className="btn-secondary"
                  onClick={() => setIsUnlockVaultModalOpen(false)}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="btn-primary"
                  style={{ background: '#10b981', borderColor: '#10b981' }}
                  disabled={!enteredVaultPassword.trim()}
                >
                  Decrypt & Reveal
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* ─────────────────────────────────────────────
          3. MULTI-FACTOR CHAT LOCK SETUP MODAL
          ───────────────────────────────────────────── */}
      {isSetChatLockModalOpen && setLockTargetChat && (
        <div className="ios-modal-backdrop" onClick={() => setIsSetChatLockModalOpen(false)}>
          <div className="ios-modal-card" style={{ maxWidth: '480px' }} onClick={(e) => e.stopPropagation()}>
            <div className="ios-modal-header">
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <ShieldAlert size={20} color="#f59e0b" />
                <h3>Set Security Lock: {setLockTargetChat.name}</h3>
              </div>
              <button onClick={() => setIsSetChatLockModalOpen(false)} className="modal-close-btn">
                <X size={18} />
              </button>
            </div>

            <p style={{ fontSize: '0.84rem', color: 'var(--text-secondary)', margin: '4px 0 14px 0' }}>
              Choose your preferred security factor to encrypt and lock access to this conversation.
            </p>

            {/* 5 Security Type Selector */}
            <div className="lock-type-selector">
              <div
                className={`lock-type-chip ${lockSecurityType === 'pin' ? 'selected' : ''}`}
                onClick={() => setLockSecurityType('pin')}
              >
                <Lock size={18} />
                <span>Numeric PIN</span>
              </div>
              <div
                className={`lock-type-chip ${lockSecurityType === 'password' ? 'selected' : ''}`}
                onClick={() => setLockSecurityType('password')}
              >
                <KeyRound size={18} />
                <span>Password</span>
              </div>
              <div
                className={`lock-type-chip ${lockSecurityType === 'pattern' ? 'selected' : ''}`}
                onClick={() => setLockSecurityType('pattern')}
              >
                <Grid size={18} />
                <span>Pattern (3x3)</span>
              </div>
              <div
                className={`lock-type-chip ${lockSecurityType === 'fingerprint' ? 'selected' : ''}`}
                onClick={() => setLockSecurityType('fingerprint')}
              >
                <Fingerprint size={18} />
                <span>Fingerprint</span>
              </div>
              <div
                className={`lock-type-chip ${lockSecurityType === 'face' ? 'selected' : ''}`}
                onClick={() => setLockSecurityType('face')}
              >
                <Scan size={18} />
                <span>Face Lock</span>
              </div>
            </div>

            <form onSubmit={handleSaveChatLock}>
              {/* PIN Input */}
              {lockSecurityType === 'pin' && (
                <div style={{ display: 'flex', flexDirection: 'column', gap: '10px', marginBottom: '14px' }}>
                  <input
                    type="password"
                    maxLength={8}
                    placeholder="Enter 4 to 8-digit PIN"
                    value={lockSecretInput}
                    onChange={(e) => {
                      setLockSecretInput(e.target.value.replace(/\D/g, ''));
                      setSetLockError('');
                    }}
                    className="lock-pin-input"
                    autoFocus
                  />
                  <input
                    type="password"
                    maxLength={8}
                    placeholder="Confirm PIN code"
                    value={lockSecretConfirm}
                    onChange={(e) => {
                      setLockSecretConfirm(e.target.value.replace(/\D/g, ''));
                      setSetLockError('');
                    }}
                    className="lock-pin-input"
                  />
                </div>
              )}

              {/* Password Input */}
              {lockSecurityType === 'password' && (
                <div style={{ display: 'flex', flexDirection: 'column', gap: '10px', marginBottom: '14px' }}>
                  <input
                    type="password"
                    placeholder="Enter secret chat password (min 6 chars)"
                    value={lockSecretInput}
                    onChange={(e) => {
                      setLockSecretInput(e.target.value);
                      setSetLockError('');
                    }}
                    className="lock-pin-input"
                    autoFocus
                  />
                  <input
                    type="password"
                    placeholder="Confirm chat password"
                    value={lockSecretConfirm}
                    onChange={(e) => {
                      setLockSecretConfirm(e.target.value);
                      setSetLockError('');
                    }}
                    className="lock-pin-input"
                  />
                </div>
              )}

              {/* 3x3 Pattern Grid */}
              {lockSecurityType === 'pattern' && (
                <div className="pattern-lock-container">
                  <span className="pattern-status-feedback">
                    Connect at least 4 dots to draw pattern: {lockPatternNodes.length > 0 ? `(${lockPatternNodes.map((n) => n + 1).join(' → ')})` : 'Tap dots in order'}
                  </span>
                  <div className="pattern-grid-3x3">
                    {[0, 1, 2, 3, 4, 5, 6, 7, 8].map((index) => {
                      const isNodeSelected = lockPatternNodes.includes(index);
                      return (
                        <div
                          key={index}
                          className={`pattern-node-dot ${isNodeSelected ? 'selected' : ''}`}
                          onClick={() => handleTogglePatternNode(index)}
                        >
                          <div className="pattern-inner-pip" />
                        </div>
                      );
                    })}
                  </div>
                  {lockPatternNodes.length > 0 && (
                    <button
                      type="button"
                      className="btn-secondary btn-sm"
                      onClick={() => setLockPatternNodes([])}
                    >
                      Clear Pattern
                    </button>
                  )}
                </div>
              )}

              {/* Biometric Sensor Setup Notice */}
              {(lockSecurityType === 'fingerprint' || lockSecurityType === 'face') && (
                <div className="biometric-sensor-box">
                  {lockSecurityType === 'fingerprint' ? (
                    <Fingerprint size={48} color="#10b981" />
                  ) : (
                    <Scan size={48} color="#007aff" />
                  )}
                  <strong style={{ marginTop: '10px', fontSize: '0.92rem' }}>
                    {lockSecurityType === 'fingerprint' ? 'Touch ID / Fingerprint Lock' : 'Face ID / Facial Recognition'}
                  </strong>
                  <p style={{ fontSize: '0.78rem', color: 'var(--text-secondary)', textAlign: 'center', marginTop: '4px' }}>
                    Protected by device hardware keystore simulation. Every time this chat is opened, your biometric credential will be required.
                  </p>
                </div>
              )}

              {setLockError && (
                <div className="lock-error-msg" style={{ marginBottom: '14px' }}>
                  <AlertCircle size={14} /> {setLockError}
                </div>
              )}

              <div className="lock-btn-row">
                <button
                  type="button"
                  className="btn-secondary"
                  onClick={() => setIsSetChatLockModalOpen(false)}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="btn-primary"
                  style={{ background: '#f59e0b', borderColor: '#f59e0b', color: '#000', fontWeight: 700 }}
                >
                  Lock Conversation
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* ─────────────────────────────────────────────
          4. MULTI-FACTOR CHAT UNLOCK SCREEN MODAL
          ───────────────────────────────────────────── */}
      {isUnlockChatModalOpen && lockedChatAuthTarget && (
        <div className="ios-modal-backdrop" onClick={() => setIsUnlockChatModalOpen(false)}>
          <div className="ios-modal-card lock-auth-modal" style={{ maxWidth: '440px' }} onClick={(e) => e.stopPropagation()}>
            <div className="lock-icon-hero">
              <Lock size={36} color="#f59e0b" />
            </div>
            <h3 style={{ textAlign: 'center' }}>Chat is Locked</h3>
            <p style={{ textAlign: 'center', fontSize: '0.86rem', color: 'var(--text-secondary)' }}>
              Authentication required to access conversation with <strong>{lockedChatAuthTarget.name}</strong>.
            </p>

            <form onSubmit={handleUnlockChatSecurity}>
              {/* PIN or Password Mode */}
              {(!lockedChatAuthTarget.lockType || lockedChatAuthTarget.lockType === 'pin' || lockedChatAuthTarget.lockType === 'password') && (
                <div style={{ margin: '16px 0' }}>
                  <input
                    type="password"
                    maxLength={lockedChatAuthTarget.lockType === 'pin' ? 8 : 32}
                    placeholder={lockedChatAuthTarget.lockType === 'password' ? 'Enter Password (e.g. 1234)' : 'Enter PIN (e.g. 1234)'}
                    value={unlockInputCode}
                    onChange={(e) => {
                      setUnlockInputCode(e.target.value);
                      setUnlockError('');
                    }}
                    className="lock-pin-input"
                    autoFocus
                  />
                </div>
              )}

              {/* 3x3 Pattern Unlock Grid */}
              {lockedChatAuthTarget.lockType === 'pattern' && (
                <div className="pattern-lock-container">
                  <span className="pattern-status-feedback">
                    Draw your unlock pattern: {unlockPatternNodes.length > 0 ? `(${unlockPatternNodes.map((n) => n + 1).join(' → ')})` : 'Connect dots'}
                  </span>
                  <div className="pattern-grid-3x3">
                    {[0, 1, 2, 3, 4, 5, 6, 7, 8].map((idx) => {
                      const isSelected = unlockPatternNodes.includes(idx);
                      return (
                        <div
                          key={idx}
                          className={`pattern-node-dot ${isSelected ? 'selected' : ''}`}
                          onClick={() => handleToggleUnlockPatternNode(idx)}
                        >
                          <div className="pattern-inner-pip" />
                        </div>
                      );
                    })}
                  </div>
                  {unlockPatternNodes.length > 0 && (
                    <button
                      type="button"
                      className="btn-secondary btn-sm"
                      onClick={() => setUnlockPatternNodes([])}
                    >
                      Clear Pattern
                    </button>
                  )}
                </div>
              )}

              {/* Fingerprint Interactive Sensor */}
              {lockedChatAuthTarget.lockType === 'fingerprint' && (
                <div
                  className="biometric-sensor-box"
                  onClick={handleUnlockChatSecurity}
                  title="Click to simulate touch fingerprint sensor"
                >
                  <div className={`biometric-touch-target ${isBiometricScanning ? 'scanning' : ''}`}>
                    <Fingerprint size={42} color="#10b981" />
                  </div>
                  <strong style={{ fontSize: '0.9rem' }}>
                    {isBiometricScanning ? 'Scanning Fingerprint...' : 'Touch Sensor to Authenticate'}
                  </strong>
                  <span style={{ fontSize: '0.74rem', color: 'var(--text-secondary)', marginTop: '4px' }}>
                    Tap anywhere inside this box to verify biometrics
                  </span>
                </div>
              )}

              {/* Face ID Viewfinder */}
              {lockedChatAuthTarget.lockType === 'face' && (
                <div
                  className="biometric-sensor-box"
                  onClick={handleUnlockChatSecurity}
                  title="Click to simulate Face ID scanning"
                >
                  <div className="face-id-viewfinder">
                    <Scan size={48} color="#007aff" />
                    {isBiometricScanning && <div className="face-scan-laser" />}
                  </div>
                  <strong style={{ fontSize: '0.9rem' }}>
                    {isBiometricScanning ? 'Verifying Facial Geometry...' : 'Click to Scan Face ID'}
                  </strong>
                  <span style={{ fontSize: '0.74rem', color: 'var(--text-secondary)', marginTop: '4px' }}>
                    Camera viewfinder simulation active
                  </span>
                </div>
              )}

              {unlockError && (
                <div className="lock-error-msg" style={{ marginBottom: '14px' }}>
                  <AlertCircle size={14} /> {unlockError}
                </div>
              )}

              <div className="lock-btn-row">
                <button
                  type="button"
                  className="btn-secondary"
                  onClick={() => setIsUnlockChatModalOpen(false)}
                >
                  Cancel
                </button>
                {lockedChatAuthTarget.lockType !== 'fingerprint' && lockedChatAuthTarget.lockType !== 'face' && (
                  <button
                    type="submit"
                    className="btn-primary"
                    style={{ background: '#10b981', borderColor: '#10b981' }}
                  >
                    Unlock Chat
                  </button>
                )}
              </div>
            </form>
          </div>
        </div>
      )}

      {/* ─────────────────────────────────────────────
          5. DELETE CONFIRMATION MODAL (Messages & Chats)
          ───────────────────────────────────────────── */}
      {confirmDeleteTarget && (
        <div className="ios-modal-backdrop" onClick={() => setConfirmDeleteTarget(null)}>
          <div className="ios-modal-card" style={{ maxWidth: '400px' }} onClick={(e) => e.stopPropagation()}>
            <div className="lock-icon-hero" style={{ background: 'rgba(239, 68, 68, 0.15)', borderColor: 'rgba(239, 68, 68, 0.3)' }}>
              <Trash2 size={32} color="#ef4444" />
            </div>
            <h3 style={{ textAlign: 'center', marginBottom: '8px' }}>
              {confirmDeleteTarget.type === 'message' ? 'Delete Message?' : 'Delete Conversation?'}
            </h3>
            <p style={{ textAlign: 'center', fontSize: '0.86rem', color: 'var(--text-secondary)', marginBottom: '18px' }}>
              {confirmDeleteTarget.type === 'message'
                ? 'Are you sure you want to delete this message? This action cannot be undone.'
                : `Are you sure you want to delete this conversation with "${confirmDeleteTarget.name}"? All message history will be permanently erased.`}
            </p>

            <div className="lock-btn-row">
              <button
                type="button"
                className="btn-secondary"
                onClick={() => setConfirmDeleteTarget(null)}
              >
                Cancel
              </button>
              <button
                type="button"
                className="btn-primary"
                style={{ background: '#ef4444', borderColor: '#ef4444', color: '#fff' }}
                onClick={handleConfirmDeleteAction}
              >
                {confirmDeleteTarget.type === 'message' ? 'Delete for Everyone' : 'Delete Chat'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ─────────────────────────────────────────────
          6. DAILY STORAGE & QUOTA MANAGER MODAL (18MB / 100MB)
          ───────────────────────────────────────────── */}
      {isStorageModalOpen && (
        <div className="ios-modal-backdrop" onClick={() => setIsStorageModalOpen(false)}>
          <div className="ios-modal-card storage-manager-card" onClick={(e) => e.stopPropagation()}>
            <div className="ios-modal-header">
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <HardDrive size={20} color="#10b981" />
                <h3>Daily Storage & Media Quota</h3>
              </div>
              <button onClick={() => setIsStorageModalOpen(false)} className="modal-close-btn">
                <X size={18} />
              </button>
            </div>

            {/* Storage Progress Banner */}
            <div className="storage-summary-banner">
              <div className="storage-top-stat">
                <span style={{ fontSize: '0.86rem', fontWeight: 600, color: 'var(--text-secondary)' }}>Daily Quota Usage</span>
                <span className="storage-big-num">
                  {(dailyQuotaUsedBytes / (1024 * 1024)).toFixed(1)} <span style={{ fontSize: '0.9rem', fontWeight: 500 }}>/ 100 MB</span>
                </span>
              </div>
              <div className="storage-progress-track">
                <div
                  className="storage-progress-bar"
                  style={{
                    width: `${Math.min(100, (dailyQuotaUsedBytes / MAX_DAILY_MEDIA_BYTES) * 100)}%`,
                    background: dailyQuotaUsedBytes > 80 * 1024 * 1024 ? '#ef4444' : 'linear-gradient(90deg, #10b981 0%, #3b82f6 100%)',
                  }}
                />
              </div>
              <div className="storage-category-pills">
                <span className="storage-cat-pill">
                  📷 Photos: ~{(dailyQuotaUsedBytes > 0 ? (dailyQuotaUsedBytes * 0.58 / (1024 * 1024)).toFixed(1) : 0)} MB
                </span>
                <span className="storage-cat-pill">
                  🎥 Videos: ~{(dailyQuotaUsedBytes > 0 ? (dailyQuotaUsedBytes * 0.28 / (1024 * 1024)).toFixed(1) : 0)} MB
                </span>
                <span className="storage-cat-pill">
                  📄 Docs: ~{(dailyQuotaUsedBytes > 0 ? (dailyQuotaUsedBytes * 0.14 / (1024 * 1024)).toFixed(1) : 0)} MB
                </span>
              </div>
            </div>

            <div style={{ marginBottom: '12px', fontSize: '0.84rem', fontWeight: 600, color: 'var(--text-primary)' }}>
              Recent Cached Media Files
            </div>

            <div className="storage-file-list">
              {activeChat?.messages.filter((m) => m.media).length === 0 ? (
                <div style={{ padding: '14px', textAlign: 'center', fontSize: '0.82rem', color: 'var(--text-secondary)' }}>
                  No uploaded media files cached in this conversation yet.
                </div>
              ) : (
                activeChat.messages
                  .filter((m) => m.media)
                  .map((m) => (
                    <div key={m.id} className="storage-file-row">
                      <div className="storage-file-left">
                        {m.media.type === 'image' && <ImageIcon size={18} color="#ec4899" />}
                        {m.media.type === 'video' && <Video size={18} color="#f59e0b" />}
                        {m.media.type === 'document' && <FileText size={18} color="#0d9488" />}
                        <div className="storage-file-info">
                          <span className="storage-file-name">{m.media.name}</span>
                          <span className="storage-file-meta">{m.media.size}</span>
                        </div>
                      </div>
                      <span style={{ fontSize: '0.74rem', color: '#10b981', fontWeight: 600 }}>Cached</span>
                    </div>
                  ))
              )}
            </div>

            {/* Clear Media Storage Button */}
            <button
              type="button"
              className="clear-storage-btn"
              onClick={handleClearMediaStorage}
            >
              <Trash2 size={16} /> Clear Media Cache & Free Space (Reset to 0 MB)
            </button>
          </div>
        </div>
      )}

      {/* ─────────────────────────────────────────────
          7. PROFILE & GROUP INFO DRAWER (With Instagram Themes)
          ───────────────────────────────────────────── */}
      {isProfileDrawerOpen && activeChat && (
        <div className="profile-drawer-backdrop" onClick={() => setIsProfileDrawerOpen(false)}>
          <div className="profile-drawer-card" onClick={(e) => e.stopPropagation()}>
            <div className="profile-drawer-header">
              <h3 style={{ margin: 0, fontSize: '1.05rem', fontWeight: 700 }}>
                {activeChat.type === 'group' ? 'Group Information' : 'Contact Details'}
              </h3>
              <button
                type="button"
                className="modal-close-btn"
                onClick={() => setIsProfileDrawerOpen(false)}
              >
                <X size={18} />
              </button>
            </div>

            {/* Drawer Tabs */}
            <div className="profile-drawer-tabs">
              <button
                type="button"
                className={`profile-tab-btn ${profileDrawerTab === 'overview' ? 'active' : ''}`}
                onClick={() => setProfileDrawerTab('overview')}
              >
                Overview & Name
              </button>
              <button
                type="button"
                className={`profile-tab-btn ${profileDrawerTab === 'themes' ? 'active' : ''}`}
                onClick={() => setProfileDrawerTab('themes')}
              >
                Chat Themes ({INSTAGRAM_CHAT_THEMES.length})
              </button>
              <button
                type="button"
                className={`profile-tab-btn ${profileDrawerTab === 'media' ? 'active' : ''}`}
                onClick={() => setProfileDrawerTab('media')}
              >
                Media & Docs
              </button>
            </div>

            {/* TAB 1: OVERVIEW & NAME EDIT */}
            {profileDrawerTab === 'overview' && (
              <div style={{ display: 'flex', flexDirection: 'column' }}>
                <div className="profile-avatar-hero-wrap">
                  <div className="profile-avatar-large-container">
                    <img
                      src={activeChat.avatar}
                      alt={activeChat.name}
                      className="profile-avatar-large-img"
                    />
                    <label className="profile-avatar-upload-label" title="Change Avatar">
                      <Camera size={16} />
                      <input
                        type="file"
                        accept="image/*"
                        style={{ display: 'none' }}
                        onChange={(e) => {
                          const f = e.target.files?.[0];
                          if (f) {
                            const r = new FileReader();
                            r.onload = (ev) => {
                              setChats((prev) =>
                                prev.map((c) => (c.id === activeChat.id ? { ...c, avatar: ev.target.result } : c))
                              );
                              showToast('Avatar updated!');
                            };
                            r.readAsDataURL(f);
                          }
                        }}
                      />
                    </label>
                  </div>

                  {/* Name Edit Form */}
                  <form onSubmit={handleSaveProfileInfo} className="profile-name-edit-form">
                    <input
                      type="text"
                      value={editingChatName}
                      onChange={(e) => setEditingChatName(e.target.value)}
                      className="profile-name-input"
                      placeholder="Display Name / Subject"
                    />
                    <button
                      type="submit"
                      className="btn-primary btn-sm"
                      style={{ background: '#10b981', borderColor: '#10b981' }}
                    >
                      Save
                    </button>
                  </form>
                  <span style={{ fontSize: '0.78rem', color: 'var(--text-secondary)', marginTop: '6px' }}>
                    {activeChat.type === 'group' ? 'Group Subject' : 'Contact Full Name'}
                  </span>
                </div>

                {/* Details Section */}
                <div style={{ padding: '16px', display: 'flex', flexDirection: 'column', gap: '14px' }}>
                  {/* Mobile Phone (Admin phone is strictly hidden from regular users) */}
                  {!((activeChat.isAgent || activeChat.name?.includes('Admin') || activeChat.contactId === 'u-1') && !isPrivilegedAdmin(user?.role)) && activeChat.phone && (
                    <div style={{ display: 'flex', justifyContent: 'space-between', borderBottom: '1px solid var(--border-color, rgba(255,255,255,0.06))', paddingBottom: '10px' }}>
                      <span style={{ fontSize: '0.82rem', color: 'var(--text-secondary)' }}>Mobile Phone</span>
                      <strong style={{ fontSize: '0.86rem' }}>{activeChat.phone}</strong>
                    </div>
                  )}
                  {((activeChat.isAgent || activeChat.name?.includes('Admin') || activeChat.contactId === 'u-1') && !isPrivilegedAdmin(user?.role)) && (
                    <div style={{ display: 'flex', justifyContent: 'space-between', borderBottom: '1px solid var(--border-color, rgba(255,255,255,0.06))', paddingBottom: '10px' }}>
                      <span style={{ fontSize: '0.82rem', color: 'var(--text-secondary)' }}>Contact Channel</span>
                      <strong style={{ fontSize: '0.86rem', color: '#10b981' }}>Official In-App Support</strong>
                    </div>
                  )}

                  <div style={{ display: 'flex', justifyContent: 'space-between', borderBottom: '1px solid var(--border-color, rgba(255,255,255,0.06))', paddingBottom: '10px' }}>
                    <span style={{ fontSize: '0.82rem', color: 'var(--text-secondary)' }}>Security Status</span>
                    <span style={{ fontSize: '0.82rem', color: '#10b981', fontWeight: 600, display: 'flex', alignItems: 'center', gap: '4px' }}>
                      <ShieldCheck size={14} /> End-to-End Encrypted
                    </span>
                  </div>

                  <div style={{ display: 'flex', justifyContent: 'space-between', borderBottom: '1px solid var(--border-color, rgba(255,255,255,0.06))', paddingBottom: '10px' }}>
                    <span style={{ fontSize: '0.82rem', color: 'var(--text-secondary)' }}>Disappearing Timer</span>
                    <button
                      type="button"
                      className="ios-header-badge-btn"
                      onClick={() => setIsDisappearingModalOpen(true)}
                    >
                      <Clock size={13} /> {activeChat.disappearingTimer?.toUpperCase() || 'OFF'}
                    </button>
                  </div>

                  <div style={{ display: 'flex', justifyContent: 'space-between', borderBottom: '1px solid var(--border-color, rgba(255,255,255,0.06))', paddingBottom: '10px' }}>
                    <span style={{ fontSize: '0.82rem', color: 'var(--text-secondary)' }}>Lock Protection</span>
                    <button
                      type="button"
                      className="btn-secondary btn-sm"
                      onClick={(e) => handleOpenSetChatLockModal(activeChat, e)}
                    >
                      <Lock size={13} /> {activeChat.isLocked ? `Locked (${activeChat.lockType || 'pin'})` : 'Set Lock'}
                    </button>
                  </div>

                  <button
                    type="button"
                    className="clear-storage-btn"
                    onClick={() => {
                      setIsProfileDrawerOpen(false);
                      setConfirmDeleteTarget({ type: 'chat', id: activeChat.id, name: activeChat.name });
                    }}
                  >
                    <Trash2 size={16} /> Delete Conversation History
                  </button>
                </div>
              </div>
            )}

            {/* TAB 2: INSTAGRAM CHAT THEMES */}
            {profileDrawerTab === 'themes' && (
              <div>
                <p style={{ padding: '16px 16px 0 16px', fontSize: '0.82rem', color: 'var(--text-secondary)', margin: 0 }}>
                  Select an Instagram-style vibrant gradient theme for this conversation. Changes outgoing message bubbles and ambient accents.
                </p>
                <div className="instagram-themes-grid">
                  {INSTAGRAM_CHAT_THEMES.map((th) => {
                    const isCurrent = (activeChat.theme || 'emerald') === th.id;
                    return (
                      <div
                        key={th.id}
                        className={`theme-card-option ${isCurrent ? 'active' : ''}`}
                        onClick={() => handleSelectChatTheme(th.id)}
                      >
                        <div
                          className="theme-circle-preview"
                          style={{ background: th.gradient }}
                        />
                        <span className="theme-card-label">{th.name}</span>
                        {isCurrent && (
                          <span style={{ fontSize: '0.72rem', color: '#10b981', fontWeight: 700 }}>
                            ✓ Active
                          </span>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            {/* TAB 3: MEDIA & VAULT GALLERY */}
            {profileDrawerTab === 'media' && (
              <div style={{ padding: '16px' }}>
                <div style={{ fontSize: '0.84rem', fontWeight: 600, marginBottom: '12px' }}>
                  Shared Media & Attachments ({activeChat.messages.filter((m) => m.media || m.vaultItem).length})
                </div>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '10px' }}>
                  {activeChat.messages
                    .filter((m) => m.media || m.vaultItem)
                    .map((m) => (
                      <div
                        key={m.id}
                        style={{
                          padding: '10px',
                          background: 'rgba(255,255,255,0.04)',
                          borderRadius: '10px',
                          border: '1px solid var(--border-color, rgba(255,255,255,0.08))',
                          display: 'flex',
                          flexDirection: 'column',
                          gap: '6px',
                        }}
                      >
                        {m.media && (
                          <>
                            {m.media.type === 'image' && m.media.url ? (
                              <img src={m.media.url} alt="media" style={{ width: '100%', height: '80px', objectFit: 'cover', borderRadius: '6px' }} />
                            ) : (
                              <FileText size={24} color="#10b981" />
                            )}
                            <span style={{ fontSize: '0.78rem', fontWeight: 600, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                              {m.media.name}
                            </span>
                            <span style={{ fontSize: '0.7rem', color: 'var(--text-secondary)' }}>{m.media.size}</span>
                          </>
                        )}
                        {m.vaultItem && (
                          <>
                            <CreditCard size={24} color="#f59e0b" />
                            <span style={{ fontSize: '0.78rem', fontWeight: 600 }}>{m.vaultItem.name || m.vaultItem.title}</span>
                            <span style={{ fontSize: '0.7rem', color: '#f59e0b' }}>Vault {m.vaultItem.type}</span>
                          </>
                        )}
                      </div>
                    ))}
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* ─────────────────────────────────────────────
          8. CONTEXT MENU (Sidebar Chats & Messages)
          ───────────────────────────────────────────── */}
      {contextMenu && (
        <>
          {/* Backdrop for closing context menu on tap outside */}
          <div
            className="ios-context-menu-backdrop"
            onClick={() => setContextMenu(null)}
          />
          <div
            className={`ios-context-menu ${theme === 'dark' ? 'ios-dark' : 'ios-light'}`}
            style={
              typeof window !== 'undefined' && window.innerWidth > 640
                ? { top: `${contextMenu.y}px`, left: `${contextMenu.x}px` }
                : {}
            }
            onClick={(e) => e.stopPropagation()}
          >
            {/* Chat Row Context Menu */}
            {contextMenu.type === 'chat' && (
              <>
                <button
                  type="button"
                  className="ios-context-menu-item"
                  onClick={() => {
                    togglePinChat(contextMenu.target.id);
                    setContextMenu(null);
                  }}
                >
                  <Pin size={15} /> {contextMenu.target.isPinned ? 'Unpin Chat' : 'Pin to Top'}
                </button>
                <button
                  type="button"
                  className="ios-context-menu-item"
                  onClick={() => {
                    toggleFavouriteChat(contextMenu.target.id);
                    setContextMenu(null);
                  }}
                >
                  <Star size={15} color="#f59e0b" /> {contextMenu.target.isFavourite ? 'Remove from Favourites' : 'Add to Favourites'}
                </button>
                <button
                  type="button"
                  className="ios-context-menu-item"
                  onClick={(e) => {
                    handleOpenSetChatLockModal(contextMenu.target, e);
                    setContextMenu(null);
                  }}
                >
                  <Lock size={15} color="#f59e0b" /> Security Lock (5-Factor)
                </button>
                <button
                  type="button"
                  className="ios-context-menu-item"
                  onClick={() => {
                    toggleArchiveChat(contextMenu.target.id);
                    setContextMenu(null);
                  }}
                >
                  <Archive size={15} /> {contextMenu.target.isArchived ? 'Unarchive Chat' : 'Archive Chat'}
                </button>
                <div className="ios-context-divider" />
                <button
                  type="button"
                  className="ios-context-menu-item danger"
                  onClick={() => {
                    setConfirmDeleteTarget({ type: 'chat', id: contextMenu.target.id, name: contextMenu.target.name });
                    setContextMenu(null);
                  }}
                >
                  <Trash2 size={15} /> Delete Conversation
                </button>
              </>
            )}

            {/* Message Bubble Context Menu */}
            {contextMenu.type === 'message' && (
              <>
                <button
                  type="button"
                  className="ios-context-menu-item"
                  onClick={() => {
                    setReplyingToMessage(contextMenu.target);
                    setContextMenu(null);
                  }}
                >
                  <Reply size={15} /> Reply
                </button>
                <button
                  type="button"
                  className="ios-context-menu-item"
                  onClick={() => {
                    setMessageInfoTarget(contextMenu.target);
                    setContextMenu(null);
                  }}
                >
                  <Info size={15} color="#3b82f6" /> Message Info
                </button>
                <button
                  type="button"
                  className="ios-context-menu-item"
                  onClick={() => {
                    if (contextMenu.target.vaultItem) {
                      handleCopyVaultItemSafe(contextMenu.target);
                    } else {
                      handleCopyMessage(contextMenu.target.text || '');
                    }
                    setContextMenu(null);
                  }}
                >
                  <Copy size={15} /> Copy
                </button>
                <div className="ios-context-divider" />
                <button
                  type="button"
                  className="ios-context-menu-item danger"
                  onClick={() => {
                    setConfirmDeleteTarget({ type: 'message', id: contextMenu.target.id, text: contextMenu.target.text || 'Message' });
                    setContextMenu(null);
                  }}
                >
                  <Trash2 size={15} /> Delete Message
                </button>
              </>
            )}

            {/* Cancel Button (Visible on Mobile Sheet) */}
            <div className="ios-context-divider mobile-only-divider" />
            <button
              type="button"
              className="ios-context-menu-item ios-context-cancel-btn"
              onClick={() => setContextMenu(null)}
            >
              <X size={15} /> Cancel
            </button>
          </div>
        </>
      )}

      {/* ─────────────────────────────────────────────
          9. MESSAGE INFO MODAL (Sent, Delivered, Read Exact Timestamps)
          ───────────────────────────────────────────── */}
      {messageInfoTarget && (
        <div className="ios-modal-backdrop" onClick={() => setMessageInfoTarget(null)}>
          <div className="ios-modal-card message-info-card" onClick={(e) => e.stopPropagation()}>
            <div className="ios-modal-header">
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <Info size={20} color="#3b82f6" />
                <h3>Message Information</h3>
              </div>
              <button onClick={() => setMessageInfoTarget(null)} className="modal-close-btn">
                <X size={18} />
              </button>
            </div>

            {/* Message Preview Box */}
            <div className="msg-info-preview-box">
              <div style={{ fontSize: '0.76rem', color: 'var(--text-secondary)', marginBottom: '4px' }}>
                {messageInfoTarget.senderName || 'Me'} • {new Date(messageInfoTarget.timestamp).toLocaleDateString()}
              </div>
              <div style={{ fontSize: '0.92rem', fontWeight: 500 }}>
                {messageInfoTarget.text || messageInfoTarget.media?.name || (messageInfoTarget.vaultItem ? `🔐 Vault ${messageInfoTarget.vaultItem.type}` : 'Encrypted Message')}
              </div>
            </div>

            {/* Timestamps Timeline */}
            <div className="msg-info-timeline">
              {/* Read Status */}
              <div className="msg-info-timeline-row status-read">
                <div className="msg-info-status-left">
                  <CheckCheck size={18} color="#53bdeb" />
                  <span>Read</span>
                </div>
                <span className="msg-info-time-val">
                  {messageInfoTarget.readAt
                    ? new Date(messageInfoTarget.readAt).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', second: '2-digit' })
                    : (messageInfoTarget.status === 'read' ? 'Today, Just now' : 'Not read yet')}
                </span>
              </div>

              {/* Delivered Status */}
              <div className="msg-info-timeline-row status-delivered">
                <div className="msg-info-status-left">
                  <CheckCheck size={18} color="#94a3b8" />
                  <span>Delivered</span>
                </div>
                <span className="msg-info-time-val">
                  {messageInfoTarget.deliveredAt
                    ? new Date(messageInfoTarget.deliveredAt).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', second: '2-digit' })
                    : (new Date(messageInfoTarget.timestamp + 800).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', second: '2-digit' }))}
                </span>
              </div>

              {/* Sent Status */}
              <div className="msg-info-timeline-row status-sent">
                <div className="msg-info-status-left">
                  <Check size={18} color="#64748b" />
                  <span>Sent</span>
                </div>
                <span className="msg-info-time-val">
                  {new Date(messageInfoTarget.sentAt || messageInfoTarget.timestamp).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', second: '2-digit' })}
                </span>
              </div>
            </div>

            <div style={{ marginTop: '20px', display: 'flex', justifyContent: 'flex-end' }}>
              <button
                type="button"
                className="btn-secondary"
                onClick={() => setMessageInfoTarget(null)}
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ─────────────────────────────────────────────
          10. DISAPPEARING MESSAGES MODAL
          ───────────────────────────────────────────── */}
      {isDisappearingModalOpen && (
        <div className="ios-modal-backdrop" onClick={() => setIsDisappearingModalOpen(false)}>
          <div className="ios-modal-card disappearing-modal" onClick={(e) => e.stopPropagation()}>
            <div className="ios-modal-header">
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <Clock size={20} color="#10b981" />
                <h3>Disappearing Messages</h3>
              </div>
              <button onClick={() => setIsDisappearingModalOpen(false)} className="modal-close-btn">
                <X size={18} />
              </button>
            </div>

            <p className="disappearing-desc">
              For more privacy, all new messages in this chat will disappear after the selected duration. Messages can be stored for a maximum of 30 days.
            </p>

            <div className="disappearing-options-list">
              {[
                { label: 'Immediately (Snapchat mode — 8s after viewing)', value: 'immediately' },
                { label: '24 Hours (1 Day)', value: '1d' },
                { label: '7 Days (Default)', value: '7d' },
                { label: '15 Days', value: '15d' },
                { label: '30 Days (Maximum Store Limit)', value: '30d' },
                { label: 'Off (Do Not Auto-Delete)', value: 'off' },
              ].map((opt) => (
                <div
                  key={opt.value}
                  className={`disappearing-option-row ${activeChat?.disappearingTimer === opt.value ? 'selected' : ''}`}
                  onClick={() => handleSetDisappearingTimer(opt.value)}
                >
                  <span>{opt.label}</span>
                  {activeChat?.disappearingTimer === opt.value && <Check size={18} color="#10b981" />}
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* ─────────────────────────────────────────────
          11. NEW CHAT / CONTACTS DIRECTORY MODAL
          ───────────────────────────────────────────── */}
      {isNewChatModalOpen && (
        <div className="ios-modal-backdrop" onClick={() => setIsNewChatModalOpen(false)}>
          <div className="ios-modal-card new-chat-modal" onClick={(e) => e.stopPropagation()}>
            <div className="ios-modal-header">
              <h3>Contacts & Directory</h3>
              <button onClick={() => setIsNewChatModalOpen(false)} className="modal-close-btn">
                <X size={18} />
              </button>
            </div>

            <div className="new-chat-top-actions" style={{ display: 'flex', gap: '8px', marginBottom: '14px', flexWrap: 'wrap' }}>
              <button
                type="button"
                className="btn-primary btn-sm"
                onClick={() => setIsAddContactModalOpen(true)}
                style={{ flex: '1 1 120px', background: '#10b981', borderColor: '#10b981', whiteSpace: 'nowrap', justifyContent: 'center' }}
              >
                <UserPlus size={14} /> Add Contact
              </button>

              {isPrivilegedAdmin(user?.role) && (
                <button
                  type="button"
                  className="btn-secondary btn-sm"
                  onClick={() => setIsCreateUserModalOpen(true)}
                  style={{ flex: '1 1 120px', whiteSpace: 'nowrap', justifyContent: 'center' }}
                >
                  <Plus size={14} /> Create User
                </button>
              )}
            </div>

            {(() => {
              const visibleContacts = isPrivilegedAdmin(user?.role)
                ? contacts
                : contacts.filter((c) => c.isAgent || c.isSelfAdded || isPrivilegedAdmin(c.role) || c.email?.toLowerCase().includes('admin'));

              if (isLoadingDirectory) {
                return (
                  <div style={{ padding: '24px', textAlign: 'center', color: 'var(--text-secondary)' }}>
                    <RefreshCw size={20} className="spin" style={{ marginBottom: '8px' }} />
                    <p>Loading real users & teams from database...</p>
                  </div>
                );
              }

              if (visibleContacts.length === 0) {
                return (
                  <div style={{ padding: '24px', textAlign: 'center', color: 'var(--text-secondary)' }}>
                    <Users size={32} style={{ opacity: 0.4, marginBottom: '8px' }} />
                    <p>No registered contacts yet. Click "Add Contact" to validate and add users from the CipherVault database.</p>
                  </div>
                );
              }

              return (
                <div className="contacts-list-stream">
                  {visibleContacts.map((cnt) => {
                    const isAgent = cnt.isAgent || isPrivilegedAdmin(cnt.role) || cnt.email?.toLowerCase().includes('admin');
                    const targetUid = String(cnt.userId || cnt.id).replace(/^u-/, '');
                    const existingChat = chats.find((c) =>
                      c.type === 'direct' && (
                        c.participantIds?.map(String).includes(targetUid) ||
                        String(c.contactId) === `u-${targetUid}` ||
                        c.email?.toLowerCase() === cnt.email?.toLowerCase()
                      )
                    );

                    return (
                      <div
                        key={cnt.id}
                        className="contact-item-row"
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'space-between',
                          gap: '8px',
                          padding: '10px 12px',
                          marginBottom: '8px',
                          borderBottom: '1px solid var(--border-color, rgba(255,255,255,0.06))',
                        }}
                      >
                        <div style={{ display: 'flex', alignItems: 'center', gap: '10px', minWidth: 0, flex: 1, overflow: 'hidden' }}>
                          <img
                            src={cnt.avatar}
                            alt={cnt.name}
                            className="contact-avatar"
                            style={{ width: '38px', height: '38px', borderRadius: '50%', flexShrink: 0 }}
                          />
                          <div className="contact-info" style={{ minWidth: 0, flex: 1, overflow: 'hidden' }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '5px', marginBottom: '2px', minWidth: 0, overflow: 'hidden' }}>
                              <strong
                                style={{
                                  fontSize: '0.88rem',
                                  color: 'var(--text-primary)',
                                  overflow: 'hidden',
                                  textOverflow: 'ellipsis',
                                  whiteSpace: 'nowrap',
                                  maxWidth: '125px',
                                  display: 'inline-block',
                                }}
                              >
                                {cnt.name}
                              </strong>
                              {isAgent && (
                                <span style={{ fontSize: '10px', background: 'rgba(16, 185, 129, 0.15)', color: '#10b981', padding: '1px 5px', borderRadius: '4px', fontWeight: 700, flexShrink: 0 }}>
                                  Agent 🛡️
                                </span>
                              )}
                              {existingChat ? (
                                <span style={{ fontSize: '9.5px', background: 'rgba(16, 185, 129, 0.12)', color: '#10b981', border: '1px solid rgba(16, 185, 129, 0.3)', padding: '1px 5px', borderRadius: '4px', fontWeight: 600, flexShrink: 0 }}>
                                  In Chat ✓
                                </span>
                              ) : (
                                <span style={{ fontSize: '9.5px', background: 'rgba(59, 130, 246, 0.12)', color: '#3b82f6', border: '1px solid rgba(59, 130, 246, 0.3)', padding: '1px 5px', borderRadius: '4px', fontWeight: 600, flexShrink: 0 }}>
                                  New
                                </span>
                              )}
                            </div>
                            <span
                              className="contact-phone"
                              style={{
                                fontSize: '0.75rem',
                                color: 'var(--text-secondary)',
                                display: 'block',
                                textOverflow: 'ellipsis',
                                overflow: 'hidden',
                                whiteSpace: 'nowrap',
                                minWidth: 0,
                              }}
                            >
                              {(isAgent || cnt.role === 'ADMIN' || cnt.role === 'SUPER_ADMIN') && !isPrivilegedAdmin(user?.role) ? (
                                <strong style={{ color: '#10b981', fontWeight: 600 }}>Official In-App Support</strong>
                              ) : cnt.phone ? (
                                <strong style={{ color: '#10b981', fontWeight: 600 }}>{cnt.phone}</strong>
                              ) : (
                                <span style={{ color: '#f59e0b', fontStyle: 'italic' }}>No phone</span>
                              )}
                              {' • '}
                              <span>{cnt.email}</span>
                            </span>
                          </div>
                        </div>

                        {existingChat ? (
                          <button
                            type="button"
                            className="contact-status-btn in-chat"
                            style={{
                              background: 'rgba(16, 185, 129, 0.12)',
                              color: '#10b981',
                              border: '1px solid rgba(16, 185, 129, 0.35)',
                              display: 'inline-flex',
                              alignItems: 'center',
                              gap: '4px',
                              fontWeight: 600,
                              cursor: 'pointer',
                              padding: '5px 10px',
                              borderRadius: '8px',
                              fontSize: '0.75rem',
                              flexShrink: 0,
                              whiteSpace: 'nowrap',
                            }}
                            onClick={() => {
                              setActiveChatId(existingChat.id);
                              setIsNewChatModalOpen(false);
                              setMobileShowChat(true);
                              showToast(`Opened chat with ${existingChat.name}`);
                            }}
                            title="Conversation already active. Click to open."
                          >
                            <CheckCircle2 size={13} color="#10b981" /> Chat
                          </button>
                        ) : (
                          <button
                            type="button"
                            className="contact-status-btn on-app"
                            style={{
                              background: '#10b981',
                              color: '#ffffff',
                              border: 'none',
                              display: 'inline-flex',
                              alignItems: 'center',
                              gap: '4px',
                              fontWeight: 600,
                              cursor: 'pointer',
                              padding: '5px 10px',
                              borderRadius: '8px',
                              fontSize: '0.75rem',
                              flexShrink: 0,
                              whiteSpace: 'nowrap',
                            }}
                            onClick={() => {
                              handleStartChatWithContact(cnt);
                              setIsNewChatModalOpen(false);
                            }}
                            title="Start new conversation with this contact."
                          >
                            <Plus size={13} /> Chat
                          </button>
                        )}
                      </div>
                    );
                  })}
                </div>
              );
            })()}
          </div>
        </div>
      )}

      {/* ─────────────────────────────────────────────
          12. ADMIN CREATE USER MODAL (On-the-Fly DB Save)
          ───────────────────────────────────────────── */}
      {isCreateUserModalOpen && (
        <div className="ios-modal-backdrop" onClick={() => setIsCreateUserModalOpen(false)}>
          <div className="ios-modal-card" style={{ maxWidth: '440px' }} onClick={(e) => e.stopPropagation()}>
            <div className="ios-modal-header">
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <UserPlus size={20} color="#10b981" />
                <h3>Create New User in Database</h3>
              </div>
              <button onClick={() => setIsCreateUserModalOpen(false)} className="modal-close-btn">
                <X size={18} />
              </button>
            </div>

            <form onSubmit={handleCreateAdminUserSubmit}>
              <div className="form-group">
                <label>User Email Address *</label>
                <input
                  type="email"
                  className="input-field"
                  placeholder="e.g. devraj@ciphervault.io"
                  value={createEmail}
                  onChange={(e) => setCreateEmail(e.target.value)}
                  required
                  autoFocus
                />
              </div>

              <div className="form-group">
                <label>Phone Number * (Compulsory)</label>
                <input
                  type="tel"
                  className="input-field"
                  placeholder="e.g. +91 9800000000"
                  value={createPhone}
                  onChange={(e) => setCreatePhone(e.target.value)}
                  required
                />
              </div>

              <div className="form-group">
                <label>Temporary Password</label>
                <input
                  type="password"
                  className="input-field"
                  placeholder="Create temporary password"
                  value={createPassword}
                  onChange={(e) => setCreatePassword(e.target.value)}
                  required
                />
              </div>

              <div className="form-group">
                <label>User Role</label>
                <select
                  value={createRole}
                  onChange={(e) => setCreateRole(e.target.value)}
                  className="input-field"
                >
                  <option value="USER">Regular User</option>
                  <option value="ADMIN">Administrator</option>
                </select>
              </div>

              {createUserError && (
                <div className="lock-error-msg" style={{ marginBottom: '14px' }}>
                  <AlertCircle size={14} /> {createUserError}
                </div>
              )}

              <div style={{ display: 'flex', gap: '8px', justifyContent: 'flex-end', marginTop: '16px' }}>
                <button
                  type="button"
                  className="btn-secondary"
                  onClick={() => setIsCreateUserModalOpen(false)}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="btn-primary"
                  style={{ background: '#10b981', borderColor: '#10b981' }}
                  disabled={isCreatingUser}
                >
                  {isCreatingUser ? 'Creating in DB...' : 'Create Real User'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* ─────────────────────────────────────────────
          13. ADD CONTACT MODAL (Validated against DB)
          ───────────────────────────────────────────── */}
      {isAddContactModalOpen && (
        <div className="ios-modal-backdrop" onClick={() => setIsAddContactModalOpen(false)}>
          <div
            className="ios-modal-card add-contact-modal-card"
            style={{ maxWidth: '440px', width: '100%', boxSizing: 'border-box' }}
            onClick={(e) => e.stopPropagation()}
          >
            <div className="ios-modal-header" style={{ marginBottom: '14px' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px', minWidth: 0 }}>
                <UserPlus size={20} color="#10b981" style={{ flexShrink: 0 }} />
                <h3 style={{ fontSize: '1.05rem', fontWeight: 700, margin: 0, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                  Add Contact
                </h3>
              </div>
              <button
                onClick={() => setIsAddContactModalOpen(false)}
                className="modal-close-btn"
                aria-label="Close add contact modal"
                style={{
                  width: '32px',
                  height: '32px',
                  borderRadius: '50%',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  background: 'rgba(255, 255, 255, 0.08)',
                  border: 'none',
                  color: 'var(--text-secondary)',
                  cursor: 'pointer',
                  flexShrink: 0
                }}
              >
                <X size={18} />
              </button>
            </div>

            <form onSubmit={handleSearchContactToValidate} className="add-contact-form">
              <div className="form-group" style={{ marginBottom: '14px' }}>
                <label style={{ display: 'block', fontSize: '0.84rem', fontWeight: 600, color: 'var(--text-secondary)', marginBottom: '6px' }}>
                  Phone, Email, or Username
                </label>
                <div style={{ display: 'flex', gap: '8px', alignItems: 'stretch' }}>
                  <input
                    type="text"
                    className="input-field add-contact-search-input"
                    placeholder="e.g. nikhil2, 9844556677"
                    value={addContactIdentifier}
                    onChange={(e) => {
                      setAddContactIdentifier(e.target.value);
                      setAddContactError('');
                      setAlreadyInChatContact(null);
                      setFoundContactUser(null);
                    }}
                    required
                    autoFocus
                    style={{
                      flex: 1,
                      minWidth: 0,
                      height: '42px',
                      fontSize: '15px',
                      padding: '0 12px',
                      borderRadius: '10px'
                    }}
                  />
                  <button
                    type="submit"
                    className="btn-primary add-contact-search-btn"
                    style={{
                      background: '#10b981',
                      borderColor: '#10b981',
                      whiteSpace: 'nowrap',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      gap: '6px',
                      padding: '0 14px',
                      height: '42px',
                      borderRadius: '10px',
                      flexShrink: 0,
                      fontSize: '0.9rem',
                      fontWeight: 600
                    }}
                    disabled={isValidatingContact || !addContactIdentifier.trim()}
                  >
                    {isValidatingContact ? (
                      <span className="ios-spinner" style={{ width: '14px', height: '14px' }} />
                    ) : (
                      <Search size={15} />
                    )}
                    <span>Search</span>
                  </button>
                </div>
              </div>

              <div className="form-group" style={{ marginBottom: '14px' }}>
                <label style={{ display: 'block', fontSize: '0.84rem', fontWeight: 600, color: 'var(--text-secondary)', marginBottom: '6px' }}>
                  Contact Nickname / Custom Label (Optional)
                </label>
                <input
                  type="text"
                  className="input-field"
                  placeholder="e.g. Alex (Lead)"
                  value={addContactNickname}
                  onChange={(e) => setAddContactNickname(e.target.value)}
                  style={{
                    width: '100%',
                    height: '42px',
                    fontSize: '15px',
                    padding: '0 12px',
                    borderRadius: '10px',
                    boxSizing: 'border-box'
                  }}
                />
              </div>

              {/* Found Contact User Result Card */}
              {foundContactUser && (
                <div
                  className="add-contact-result-card animate-fade-in"
                  style={{
                    padding: '14px',
                    background: 'var(--bg-surface, rgba(255, 255, 255, 0.04))',
                    border: '1px solid var(--border-color, rgba(255, 255, 255, 0.12))',
                    borderRadius: '14px',
                    marginBottom: '14px',
                    boxSizing: 'border-box',
                    overflow: 'hidden',
                    width: '100%'
                  }}
                >
                  <div style={{ display: 'flex', alignItems: 'flex-start', gap: '12px', marginBottom: '12px', minWidth: 0 }}>
                    <img
                      src={foundContactUser.user.avatar}
                      alt={foundContactUser.user.name}
                      style={{ width: '44px', height: '44px', borderRadius: '50%', border: '2px solid #10b981', flexShrink: 0, objectFit: 'cover' }}
                    />
                    <div style={{ minWidth: 0, flex: 1, overflow: 'hidden' }}>
                      <div style={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: '6px', marginBottom: '3px' }}>
                        <strong style={{ fontSize: '0.94rem', color: 'var(--text-primary)', wordBreak: 'break-word', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                          {foundContactUser.user.name}
                        </strong>
                        <span style={{ fontSize: '10px', background: 'rgba(16, 185, 129, 0.15)', color: '#10b981', padding: '2px 7px', borderRadius: '4px', fontWeight: 700, flexShrink: 0, whiteSpace: 'nowrap' }}>
                          Registered Account ✓
                        </span>
                      </div>
                      <div style={{ fontSize: '0.8rem', color: 'var(--text-secondary)', wordBreak: 'break-all', marginBottom: '2px' }}>
                        {foundContactUser.user.email}
                      </div>
                      {foundContactUser.user.phone ? (
                        <div style={{ fontSize: '0.78rem', color: '#10b981', fontWeight: 600 }}>
                          📱 {foundContactUser.user.phone}
                        </div>
                      ) : (
                        <div style={{ fontSize: '0.76rem', color: 'var(--text-muted)', fontStyle: 'italic' }}>
                          {foundContactUser.user.role === 'ADMIN' || foundContactUser.user.role === 'SUPER_ADMIN' ? 'Official In-App Support' : 'No public phone registered'}
                        </div>
                      )}
                    </div>
                  </div>

                  {foundContactUser.isAlreadyInChat ? (
                    <div>
                      <div style={{ fontSize: '0.82rem', color: '#10b981', display: 'flex', alignItems: 'center', gap: '6px', marginBottom: '10px', fontWeight: 600 }}>
                        <CheckCircle2 size={15} style={{ flexShrink: 0 }} />
                        <span>Already in your active chat list!</span>
                      </div>
                      <button
                        type="button"
                        className="btn-primary"
                        style={{
                          width: '100%',
                          height: '42px',
                          background: '#10b981',
                          borderColor: '#10b981',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          gap: '6px',
                          fontWeight: 600,
                          fontSize: '0.88rem',
                          borderRadius: '10px'
                        }}
                        onClick={() => {
                          setActiveChatId(foundContactUser.existingChat?.id);
                          setIsAddContactModalOpen(false);
                          setFoundContactUser(null);
                          setAddContactIdentifier('');
                          setMobileShowChat(true);
                          showToast(`Opened chat with ${foundContactUser.existingChat?.name || foundContactUser.user?.name}`);
                        }}
                      >
                        <MessageSquare size={15} /> Open Existing Chat
                      </button>
                    </div>
                  ) : (
                    <div>
                      <div style={{ fontSize: '0.82rem', color: 'var(--text-secondary)', marginBottom: '10px' }}>
                        User verified in database. Click below to add this contact and start messaging.
                      </div>
                      <button
                        type="button"
                        className="btn-primary"
                        style={{
                          width: '100%',
                          height: '42px',
                          background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
                          borderColor: '#10b981',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          gap: '8px',
                          fontWeight: 700,
                          fontSize: '0.88rem',
                          borderRadius: '10px',
                          cursor: 'pointer'
                        }}
                        onClick={() => handleConfirmAddContactToChat(foundContactUser.user)}
                      >
                        <Plus size={16} /> Add to My Chats
                      </button>
                    </div>
                  )}
                </div>
              )}

              {addContactError && (
                <div
                  className="ios-activation-error"
                  style={{
                    padding: '10px 14px',
                    background: 'rgba(239, 68, 68, 0.12)',
                    border: '1px solid rgba(239, 68, 68, 0.35)',
                    borderRadius: '10px',
                    color: '#ef4444',
                    fontSize: '0.84rem',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px',
                    marginBottom: '14px',
                  }}
                >
                  <AlertCircle size={16} style={{ flexShrink: 0 }} />
                  <span>{addContactError}</span>
                </div>
              )}

              <div className="ios-activation-notice" style={{ marginBottom: '14px' }}>
                <Info size={14} color="#3b82f6" style={{ flexShrink: 0 }} />
                <span>Search by phone, email, or username. Once verified in the database, click "Add to My Chats" to start messaging.</span>
              </div>

              <div style={{ display: 'flex', gap: '8px', justifyContent: 'flex-end' }}>
                <button
                  type="button"
                  className="btn-secondary"
                  style={{ height: '38px', padding: '0 16px', borderRadius: '10px', fontSize: '0.88rem' }}
                  onClick={() => {
                    setIsAddContactModalOpen(false);
                    setFoundContactUser(null);
                    setAddContactError('');
                  }}
                >
                  Close
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* ─────────────────────────────────────────────
          14. NEW GROUP CREATION MODAL
          ───────────────────────────────────────────── */}
      {isNewGroupModalOpen && (
        <div className="ios-modal-backdrop" onClick={() => setIsNewGroupModalOpen(false)}>
          <div className="ios-modal-card new-group-modal" onClick={(e) => e.stopPropagation()}>
            <div className="ios-modal-header">
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <Users size={20} color="#10b981" />
                <h3>Create New Group</h3>
              </div>
              <button onClick={() => setIsNewGroupModalOpen(false)} className="modal-close-btn">
                <X size={18} />
              </button>
            </div>

            <form onSubmit={handleCreateGroup} className="new-group-form">
              <div className="form-group">
                <label>Group Subject / Name</label>
                <input
                  type="text"
                  className="input-field"
                  placeholder="e.g. Executive Vault & Finance"
                  value={newGroupName}
                  onChange={(e) => setNewGroupName(e.target.value)}
                  required
                />
              </div>

              <div className="form-group">
                <label>Select Members ({newGroupMembers.length} selected)</label>
                <div className="group-members-picker">
                  {contacts.length === 0 ? (
                    <div style={{ padding: '14px', fontSize: '0.82rem', color: 'var(--text-secondary)', textAlign: 'center' }}>
                      No registered contacts available. Add contacts to populate your group members.
                    </div>
                  ) : (
                    contacts.map((cnt) => {
                      const isSelected = newGroupMembers.includes(cnt.id);
                      return (
                        <div
                          key={cnt.id}
                          className={`group-member-pick-row ${isSelected ? 'selected' : ''}`}
                          onClick={() => {
                            setNewGroupMembers((prev) =>
                              isSelected
                                ? prev.filter((id) => id !== cnt.id)
                                : [...prev, cnt.id]
                            );
                          }}
                        >
                          <input
                            type="checkbox"
                            checked={isSelected}
                            onChange={() => {}}
                          />
                          <img src={cnt.avatar} alt={cnt.name} className="picker-avatar" />
                          <div className="picker-info">
                            <strong>{cnt.name}</strong>
                            <span>{cnt.phone} • {cnt.email}</span>
                          </div>
                        </div>
                      );
                    })
                  )}
                </div>
              </div>

              <div style={{ display: 'flex', gap: '8px', justifyContent: 'flex-end', marginTop: '16px' }}>
                <button
                  type="button"
                  className="btn-secondary"
                  onClick={() => setIsNewGroupModalOpen(false)}
                >
                  Cancel
                </button>
                <button type="submit" className="btn-primary" style={{ background: '#10b981', borderColor: '#10b981' }}>
                  Create Group
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* ─────────────────────────────────────────────
          15. iOS WHATSAPP CAMERA MODAL (Photo & Video)
          ───────────────────────────────────────────── */}
      <IOSWhatsAppCameraModal
        isOpen={isCameraModalOpen}
        onClose={() => setIsCameraModalOpen(false)}
        onSendMedia={(mediaData) => {
          sendCustomMessage({
            text: mediaData.caption || '',
            media: {
              type: mediaData.type,
              url: mediaData.url,
              name: mediaData.name,
              size: mediaData.size,
              caption: mediaData.caption || '',
            },
          });
          showToast(`${mediaData.type === 'image' ? 'Photo' : 'Video'} sent to chat!`, 'success');
        }}
      />

      {/* ─────────────────────────────────────────────
          16. DYNAMIC SINGLE-PERMISSION RE-PROMPT MODAL
          ───────────────────────────────────────────── */}
      {singlePermModal && (
        <IOSSinglePermissionModal
          permType={singlePermModal.permType}
          onAllow={(granted) => {
            const cb = singlePermModal.onGranted;
            setSinglePermModal(null);
            if (granted && cb) {
              setTimeout(cb, 80);
            } else if (!granted) {
              showToast(`Permission denied for ${singlePermModal.permType}.`, 'warning');
            }
          }}
          onDeny={() => {
            setSinglePermModal(null);
          }}
        />
      )}
    </MainLayout>
  );
}
